import React, { useEffect, useState, useMemo, useRef } from "react";
import useHiddenVerticals, { isHiddenVertical } from "../../hooks/useHiddenVerticals";
import { canonicalSimulatorSector } from "./sectorCanonical";
import { SECTOR_PROFESOR, ETIQUETA_PROFESOR, esLeadProfesor, valorSelectorSector, camposLeadDeSelector } from "./sectorProfesor";
import { entrarModoProfesorSim } from "../../simulator/pages/autoescuela/planProfesorSim";
import { esSectorAcademia, esModoAcademia, entrarModoAcademiaSim } from "../../simulator/pages/autoescuela/modoAcademiaSim";
import { flushSync } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaTimes, FaWhatsapp, FaExclamationCircle } from "react-icons/fa";
import axiosClient from "../../api/axiosClient";
import { useSimulator } from "../../contexts/SimulatorContext";
import activityTracker from "../../lib/activityTracker";
import {
  Calendar,
  AlertCircle,
  Building2,
  Globe,
  Users,
  Clock,
  MapPin,
  ImagePlus,
  Trash2,
  Loader2,
  MonitorPlay,
  LifeBuoy,
  Eye,
  EyeOff,
  CheckCircle,
  X,
  Copy,
  Zap,
  KeyRound,
  Smartphone,
  Undo2,
  CreditCard,
  Swords,
  Sun,
  Moon,
  Ban,
  Mail,
  Megaphone,
} from "lucide-react";
import countries from "../../data/countries.json";
import SectorPickerModal from "./SectorPickerModal";
import ScheduleDemoBlock from "./ScheduleDemoBlock";
import LeadDemoSection from "./LeadDemoSection";
import LeadRecordings from "./LeadRecordings";
import RecipientPickerModal from "./RecipientPickerModal";
import { isAppointmentSector } from "../../simulator/constants/appointmentSectors";
import { jwtDecode } from "jwt-decode";
import { normalizeCityName, fetchCityByCP } from "../../utils/cpCity";
import { getTarjetaUrl, getTelefonoComercial } from "../../utils/tarjetasConfig";
import { computePersonaContactName } from "../../utils/personaContactName";
import { shouldCanonicalizeMainPhone } from "../../utils/mainPhoneCanonical";
import { isSendablePhone } from "../../utils/sendablePhone";
import { validateNationalPhone } from "../../utils/phoneNumberValidation";
import {
  isValidEmail,
  buildAccesosEmailPayload,
  accesosEmailErrorMessage,
} from "../../utils/accesosEmail";
import AccessRequestModal from "./AccessRequestModal";
import WhatsAppPreviewModal from "./WhatsAppPreviewModal";
import FlowPreviewModal from "./FlowPreviewModal";
import SendFlowModal from "./SendFlowModal";
import EmailPreviewModal from "./EmailPreviewModal";
import AccesosEmailPreviewModal from "./AccesosEmailPreviewModal";
import CuentaPruebaPanel from "./CuentaPruebaPanel";
// Icono REAL de WhatsApp (lucide no trae logos de marca). Componente compartido; no
// redefinirlo inline. Ver memoria feedback_icono_whatsapp_siempre_el_real.
import { WhatsAppIcon } from "../../simulator/components/icons/BrandIcons";
import {
  headTemplateNameForSector,
  headUsesBusinessType,
  televentaTemplateNameForSector,
  televentaHeaderForSector,
  televentaEmailTemplateIdForSector,
  hasTeleventaFlow,
  hasMarketingFlow,
  marketingTemplateNameForSector,
  marketingVideoUrlForSector,
  marketingEmailTemplateIdForSector,
  simulatorPayloadForSector,
  miniTemplateNameForSector,
  SECTOR_LABELS,
  businessTypeForSector,
} from "./flowTemplates";
import { COMPETITION_CONFIG, SECTOR_TO_COMPETITION_KEY } from "../../pages/competitionConfig";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Time24Picker from "../ui/Time24Picker";
import es from "date-fns/locale/es";
import { findPlantillaByName } from "../../utils/plantillas";

registerLocale("es", es);

// Franja vertical para columnas colapsadas del modal de edición de lead.
// Solo visible en lg+; en mobile cada sección se renderiza expandida.
function CollapsedColumnRail({ icon: Icon, label, iconClass, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Expandir ${label}`}
      className="hidden lg:flex flex-col items-center gap-4 w-14 py-6 rounded-2xl bg-slate-50/60 hover:bg-slate-100 border border-slate-100 transition-all active:scale-95 shrink-0 self-start"
    >
      <div className={`p-2.5 rounded-2xl ${iconClass}`}>
        <Icon size={18} />
      </div>
      <h3
        className="text-xs font-black text-slate-700 uppercase tracking-[0.15em] whitespace-nowrap"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
      >
        {label}
      </h3>
    </button>
  );
}

// Module-scope constants
// Las constantes del "Flow" (mapeo sector→head, mini genérico, sectores
// soportados y etiquetas) viven en flowTemplates.js para compartirlas con
// FlowPreviewModal. Ver ./flowTemplates.

const LISTA_SECTOR_PREFIXES = {
  barberia: "barberia", barber: "barberia", barbers: "barberia",
  pelu: "peluquerias", peluqueria: "peluquerias", peluquerias: "peluquerias",
  estetica: "estetica", spa: "spa", salud: "salud", lavadero: "lavadero",
  consultoria: "consultoria", abogados: "despacho_abogados", despacho: "despacho_abogados",
  autoescuela: "autoescuela", pizzeria: "pizzeria", pizza: "pizzeria",
  distribucion: "distribucion", distribuidor: "distribucion", distribuidora: "distribucion",
  otro: "otro",
};

const sanitizeListNameIfSector = (name) => {
  const trimmed = (name || "").trim();
  const firstToken = trimmed.toLowerCase().split(/[\s_]+/)[0];
  if (LISTA_SECTOR_PREFIXES[firstToken]) {
    return trimmed.toLowerCase().replace(/\s+/g, "_");
  }
  return trimmed;
};

/**
 * LeadEditModal
 * Componente unificado para editar o crear un lead (contacto de ventas).
 * Incluye campos básicos y los nuevos campos de seguimiento de ventas.
 * Integra Full Premium styling con glassmorphism e indigo/emerald palette.
 */
// Mismo mapeo que v_getSalesContacts (chat_status / chat_status_label).
const CHAT_STATUS_LABEL = {
  received: "respondió",
  read: "leído",
  delivered: "entregado",
  sent: "enviado",
  failed: "error",
};
export const chatStatusDe = (d = {}) =>
  d.chat_status || (d.has_inbound ? "received" : d.snippet_status || null);

export default function LeadEditModal({
  open,
  onClose,
  contactId = null, // null para creación
  initialData = {},
  onSaved,
  onAccessRequested,
  focusSection,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activate, setSector, setReturnLead } = useSimulator();
  const hiddenVerticals = useHiddenVerticals(); // verticales ocultas a este usuario (1/10/2026)

  /**
   * Abre el chat de un contacto llevándose la "vuelta a la ficha": la página que
   * hospeda este modal (`location.pathname`) y el registro del lead. `Chats.jsx`
   * pinta con eso una flecha ← que devuelve a ESTA misma ficha, en vez de obligar
   * a volver a Contactos y buscar el lead otra vez.
   * El destino de vuelta reutiliza el `reopenLeadModal` que ya usa Tabla Competencia.
   */
  const goToChat = (cid) => {
    if (!cid) return;
    navigate(`/chats/${encodeURIComponent(cid)}`, {
      state: {
        leadReturn: {
          pathname: location.pathname,
          // La ficha se reabre sobre el lead titular (contactId), aunque el chat
          // que se abra sea el de su persona de contacto.
          contactData: { ...(initialData || {}), contact_id: contactId },
        },
      },
    });
  };
  const [enteringSimulator, setEnteringSimulator] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [customGroup, setCustomGroup] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [creatingList, setCreatingList] = useState(false);
  const [createListError, setCreateListError] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [dragOverLogo, setDragOverLogo] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorMsg, setPhoneErrorMsg] = useState(""); // mensaje de validación de longitud
  const [movilError, setMovilError] = useState(""); // error del móvil de la persona de contacto
  const [cpSuggestion, setCpSuggestion] = useState({
    status: "idle", // "idle" | "loading" | "detected" | "error" | "dismissed" | "accepted"
    listName: null,
    city: null,
  });

  // Estados para "Enviar Flow" (generación de miniatura + envío de templates)
  const [sendingFlow, setSendingFlow] = useState(false);
  const [flowError, setFlowError] = useState(null);
  // Modal único de envío del Flow: las 3 variantes + historial + vista previa, y el
  // selector de sector si el lead no tiene (antes eran 3 botones y 2 modales apilados).
  const [sendFlowModalOpen, setSendFlowModalOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  // Sector con el que se abre la vista previa del email: el del lead, o el elegido en
  // SendFlowModal si el lead no tiene (ese no se guarda en la ficha).
  const [emailPreviewSector, setEmailPreviewSector] = useState(null);
  const [marketingEmailPreviewOpen, setMarketingEmailPreviewOpen] = useState(false);
  // Sector elegido en el modal para ESTE envío. Va por ref porque entre elegirlo y
  // llegar a handleSendFlow puede meterse el selector de destinatario (interacción
  // asíncrona del usuario), y un state daría una lectura obsoleta.
  const flowSectorOverrideRef = useRef(null);
  // Modal de vista previa del Flow (solo lectura), abierto desde el ojo de cada variante.
  const [flowPreview, setFlowPreview] = useState({
    open: false,
    sector: null,
    withPresentation: false,
    online: false,
    single: false,
  });

  // Estados para "Enviar Tarjeta" (template tarjeta_visita con imagen estática)
  const [sendingTarjeta, setSendingTarjeta] = useState(false);
  const [tarjetaError, setTarjetaError] = useState(null);
  // Estado para "Enviar competencia" (imagen comparativa ConextCRM vs competidor)
  const [sendingComparison, setSendingComparison] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [comparisonSectorPicker, setComparisonSectorPicker] = useState({ open: false, phone: null });
  const [comparisonCompetitorPicker, setComparisonCompetitorPicker] = useState({ open: false, phone: null, sectorKey: null });
  const [accesosModalOpen, setAccesosModalOpen] = useState(false);
  const [recipientPicker, setRecipientPicker] = useState({ open: false, pendingAction: null });
  const [ownedMainPhone, setOwnedMainPhone] = useState(null);
  const [accesosPhone, setAccesosPhone] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [accessDeniedData, setAccessDeniedData] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, type: null });
  const [accesosSentInSession, setAccesosSentInSession] = useState(false);
  const [accesosCreatedInSession, setAccesosCreatedInSession] = useState(false);
  const [accesosClearedInSession, setAccesosClearedInSession] = useState(false);
  // Estado de las credenciales del admin creado para este lead (modal "ver accesos").
  // - `sessionCredentials`: credenciales devueltas al crear accesos en esta sesión
  //   (incluyen password en claro). Sobrescriben temporalmente lo que devuelva
  //   el backend hasta que se recargue el lead.
  // - `accesosStatus`: { exists, username, temp_password, must_change_password, last_login_at }
  //   leído desde /ventas/accesos-status. `temp_password` refleja SIEMPRE la
  //   contraseña vigente del cliente (también tras el primer cambio).
  const [sessionCredentials, setSessionCredentials] = useState(null);
  const [accesosStatus, setAccesosStatus] = useState(null);
  const [accesosStatusLoading, setAccesosStatusLoading] = useState(false);
  const [accesosClearing, setAccesosClearing] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  // Reenvío de las credenciales por WhatsApp desde el modal "Accesos del
  // cliente" (cuando los accesos ya existen). Reutiliza la plantilla
  // `acceso_plataforma`, la misma que el envío al crearlos en SectorPickerModal.
  const [sendingAccesosWA, setSendingAccesosWA] = useState(false);
  // Envío de las credenciales por EMAIL desde el modal "Accesos del cliente".
  const [sendingAccesosEmail, setSendingAccesosEmail] = useState(false);
  // Guardamos el ISO real del envío (no un booleano): calcularlo con
  // `new Date().toISOString()` en cada render hacía que la hora mostrada
  // avanzara sola mientras el modal seguía abierto.
  const [accesosEmailSentAt, setAccesosEmailSentAt] = useState(null);
  // Vista previa del email de accesos desde la propia ficha (hermana del ojo de
  // WhatsApp que hay junto a "Crear Accesos").
  const [accesosEmailPreviewOpen, setAccesosEmailPreviewOpen] = useState(false);
  // Historial de envíos hechos EN ESTA SESIÓN (aún no reflejados en initialData,
  // que solo se refresca al reabrir el modal). Cada entrada: { at, by_user_id, by_name }.
  const [tarjetaSessionSends, setTarjetaSessionSends] = useState([]);
  const [flowSessionSends, setFlowSessionSends] = useState([]);
  const [emailSessionSends, setEmailSessionSends] = useState([]);
  const [marketingEmailSessionSends, setMarketingEmailSessionSends] = useState([]);
  const [marketingFlowSessionSends, setMarketingFlowSessionSends] = useState([]);
  const [tarjetaClearedInSession, setTarjetaClearedInSession] = useState(false);
  const [flowClearedInSession, setFlowClearedInSession] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showMoreLinks, setShowMoreLinks] = useState(false);
  // Feedback visible tras guardar correctamente (antes no salía nada y parecía
  // que no guardaba). Se autolimpia a los pocos segundos.
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Devolución de tablet: al pulsar "Ha devuelto la tablet" se abre el campo de
  // motivo (obligatorio); al guardar, el lead deja de constar como entregada y
  // pasa a "Tablet devuelta". Estado solo de UI (no se persiste tal cual).
  const [returningTablet, setReturningTablet] = useState(false);
  const [tabletReturnReason, setTabletReturnReason] = useState("");
  // Venta de tablet: al pulsar "Marcar como vendida" se abre el mini-formulario
  // (importe + forma de pago). Al guardar, el lead deja de constar como entregada
  // y pasa al estado terminal "Tablet vendida". Estado solo de UI (no se persiste
  // tal cual; los datos persistidos viven en formData.tablet_venta_*).
  const [sellingTablet, setSellingTablet] = useState(false);
  // Snapshot de formData tras un guardado correcto. El padre no refresca
  // `initialData` al guardar (solo recarga la lista), así que sin esto
  // `hasUnsavedChanges()` seguiría comparando contra los datos viejos y avisaría
  // de "cambios sin guardar" aunque ya se hubiera guardado.
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  // Baseline del SEGUIMIENTO (interés/competencia/notas/fecha de recordatorio)
  // usado para decidir si crear una tarea de seguimiento al guardar. Igual que
  // `savedSnapshot`, el padre no refresca `initialData` tras guardar, así que sin
  // esto cada guardado en la MISMA sesión (p. ej. tras enviar Flow/Tarjeta) volvería
  // a comparar contra los datos viejos y crearía una tarea de seguimiento DUPLICADA.
  // `null` = aún no se ha guardado en esta sesión → se usa `initialData` como baseline.
  const [followUpBaseline, setFollowUpBaseline] = useState(null);

  // Asignación del lead (admin reasigna el chat a un trabajador)
  const [workers, setWorkers] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const myJwt = (() => {
    try {
      const tok = localStorage.getItem("token");
      return tok ? jwtDecode(tok) : null;
    } catch (_) {
      return null;
    }
  })();
  const myLevel =
    myJwt?.level !== undefined ? Number(myJwt.level) : 99;
  const isAdmin = myLevel === 0;
  // Solo el usuario David (admin level 0) ve el company_id de la empresa trial
  // creada junto al nombre del lead.
  const isDavid = myLevel === 0 && myJwt?.user_id === "david";
  const [companyIdCopied, setCompanyIdCopied] = useState(false);

  // Columnas expandidas (siempre 2 simultáneas en lg+). El orden del array es
  // la recencia: al expandir una nueva, se colapsa la más antigua (índice 0).
  const [expandedColumns, setExpandedColumns] = useState(
    focusSection === "demo" ? ["seguimiento", "demo"] : ["info", "seguimiento"],
  );
  // Deep-link: al abrir la ficha enfocando una sección (p.ej. "demo") desde el
  // calendario / Mis Agendas / aviso, expandir esa columna. El modal permanece
  // montado entre aperturas, así que el initializer de useState no basta.
  useEffect(() => {
    if (open && focusSection === "demo") {
      setExpandedColumns(["seguimiento", "demo"]);
    }
  }, [open, focusSection]);

  const isColumnExpanded = (id) => expandedColumns.includes(id);
  const expandColumn = (id) => {
    setExpandedColumns((prev) => {
      if (prev.includes(id)) return prev;
      return [prev[1], id];
    });
  };

  const [formData, setFormData] = useState({
    nombre: "",
    persona_contacto: "",
    email: "",
    phone_number: "",
    prefix: "+34",
    grupos: ["general"],
    interest: "",
    competitor: "",
    customCompetitor: "",
    competitor_price: "",
    callReminderDate: null,
    categoria: "",
    direccion: "",
    telefono_fijo: "",
    telefono_movil: "",
    prefix_movil: "+34",
    sitio_web: "",
    instagram: "",
    calificacion: "",
    num_resenas: "",
    notes: "",
    chat_status: null,
    chat_status_label: null,
    last_message_ts: null,
    chat_snippet: null,
    v_assets_ready: false,
    wa_undeliverable_at: null,
    closing_days: [],
    tiene_facebook: false,
    sector: "",
    // Plan Profesor (26/10/2026): "profesor" con sector autoescuela = «Profesor de autoescuela» (sectorProfesor.js).
    autoescuela_plan: "",
    tiene_whatsapp_business: false,
    logo_url: "",
    logo_s3_key: "",
    contact_type: "",
    created_by: "",
    codigo_postal: "",
    persona_contacto_id: "",
    tablet_entregada: false,
    tablet_devuelta: false,
    tablet_devuelta_at: null,
    tablet_devuelta_motivo: "",
    tablet_devuelta_by: null,
    tablet_vendida: false,
    tablet_vendida_at: null,
    tablet_vendida_by: null,
    tablet_venta_precio: 100,
    tablet_venta_metodo: "efectivo",
    televenta: false,
    televenta_disposition: "",
    recordings: [],
  });
  // Modo televenta: oculta lo presencial (tablet/visita) y muestra el bloque cold call + agenda.
  const isTeleventa = !!formData.televenta;

  const weekdays = [
    { key: "monday", label: t("calendar.schedule.monday", "Lunes") },
    { key: "tuesday", label: t("calendar.schedule.tuesday", "Martes") },
    { key: "wednesday", label: t("calendar.schedule.wednesday", "Miércoles") },
    { key: "thursday", label: t("calendar.schedule.thursday", "Jueves") },
    { key: "friday", label: t("calendar.schedule.friday", "Viernes") },
    { key: "saturday", label: t("calendar.schedule.saturday", "Sábado") },
    { key: "sunday", label: t("calendar.schedule.sunday", "Domingo") },
  ];

  const handleCPLookup = async (cp, sector, currentGroups) => {
    if (!cp || !/^\d{5}$/.test(cp) || !sector) return;
    setCpSuggestion({ status: "loading", listName: null, city: null });
    try {
      const city = await fetchCityByCP(cp);
      const normalized = normalizeCityName(city);
      const listName = `${sector}_${normalized}`;
      const alreadyAssigned = (currentGroups || []).includes(listName);
      setCpSuggestion({
        status: alreadyAssigned ? "accepted" : "detected",
        listName,
        city,
      });
    } catch (err) {
      console.warn("[LeadEditModal] CP lookup failed:", err?.message || err);
      setCpSuggestion({ status: "error", listName: null, city: null });
    }
  };

  useEffect(() => {
    if (!open) {
      setShowAccessDenied(false);
      setAccessDeniedData(null);
      setAccesosSentInSession(false);
      setAccesosEmailSentAt(null);
      setAccesosEmailPreviewOpen(false);
      setAccesosCreatedInSession(false);
      setAccesosClearedInSession(false);
      setTarjetaSessionSends([]);
      setFlowSessionSends([]);
      setEmailSessionSends([]);
      setMarketingEmailSessionSends([]);
      setMarketingFlowSessionSends([]);
      setTarjetaClearedInSession(false);
      setFlowClearedInSession(false);
      setSessionCredentials(null);
      setAccesosStatus(null);
      setAccesosStatusLoading(false);
      setCredentialsModalOpen(false);
    }
  }, [open]);

  // Tracking de apertura/cierre del modal con duración.
  useEffect(() => {
    if (!open) return;
    const timerLabel = `lead_modal:${contactId || "new"}:${Date.now()}`;
    activityTracker.startTimer(timerLabel);
    activityTracker.track("LEAD_MODAL_OPEN", {
      contact_id: contactId || null,
    });
    return () => {
      const duration_ms = activityTracker.endTimer(timerLabel);
      activityTracker.track("LEAD_MODAL_CLOSE", {
        contact_id: contactId || null,
        duration_ms,
      });
    };
  }, [open, contactId]);

  useEffect(() => {
    if (!open) return;
    setCpSuggestion({ status: "idle", listName: null, city: null });
    setGroupSearch("");
    // Nueva apertura / nuevo lead → baseline vuelve a ser `initialData`.
    setSavedSnapshot(null);
    setFollowUpBaseline(null);
    setSaveSuccess(false);

    const safeData = initialData || {};

    // Si hay initialData, la cargamos
    const rawGroups = safeData.grupo || safeData.grupos;
    const groups = Array.isArray(rawGroups)
      ? rawGroups
      : rawGroups
        ? [rawGroups]
        : ["general"];

    // Lógica para detectar si es una competencia "estándar" o personalizada
    // Incluye todas las de la tabla de competencia + legacy
    const standardCompetitors = [
      "Bookly",
      "Booksy",
      "Treatwell",
      "Calendly",
      "Fresha",
      "Square Appointments",
      "GoBarber",
      "Neverlate",
      "Peluvis",
      "AgendaPro",
      "",
    ];
    const isCustomCompetitor =
      safeData.competitor && !standardCompetitors.includes(safeData.competitor);

    // Parse phone: if stored with prefix (e.g. +34661515334), split it out
    let rawPhone = safeData.phone_number || "";
    let detectedPrefix = "+34";
    let displayPhone = rawPhone;
    if (rawPhone.startsWith("+")) {
      const matched = countries
        .slice()
        .sort((a, b) => b.dial_code.length - a.dial_code.length)
        .find((c) => rawPhone.startsWith(c.dial_code));
      if (matched) {
        detectedPrefix = matched.dial_code;
        displayPhone = rawPhone.slice(matched.dial_code.length);
      }
    }

    setFormData({
      nombre: safeData.nombre || "",
      persona_contacto: safeData.persona_contacto || "",
      email: safeData.email || "",
      phone_number: displayPhone,
      prefix: detectedPrefix,
      grupos: groups,
      interest: safeData.interest || "",
      competitor: isCustomCompetitor ? "Otros" : safeData.competitor || "",
      customCompetitor: isCustomCompetitor ? safeData.competitor : "",
      competitor_price: safeData.competitor_price ? String(safeData.competitor_price) : "",
      notes: safeData.notes || safeData.notas || "",
      callReminderDate: safeData.call_reminder_date
        ? new Date(safeData.call_reminder_date)
        : null,
      categoria: safeData.categoria || "",
      direccion: safeData.direccion || "",
      telefono_fijo: safeData.telefono_fijo || "",
      telefono_movil: safeData.telefono_movil || "",
      prefix_movil: safeData.prefix_movil || "+34",
      sitio_web: safeData.sitio_web || "",
      instagram: safeData.instagram || "",
      tiene_facebook: safeData.tiene_facebook || false,
      calificacion: safeData.calificacion || "",
      num_resenas: safeData.num_resenas || "",
      // Abierta desde el propio chat, `initialData` es la fila del chat (has_inbound,
      // snippet_status) y no trae chat_status: salía «Sin chat» con el chat delante.
      chat_status: chatStatusDe(safeData),
      chat_status_label: safeData.chat_status_label || CHAT_STATUS_LABEL[chatStatusDe(safeData)] || null,
      last_message_ts: safeData.last_message_ts || null,
      chat_snippet: safeData.chat_snippet || null,
      v_assets_ready: safeData.v_assets_ready || false,
      wa_undeliverable_at: safeData.wa_undeliverable_at || null,
      closing_days: Array.isArray(safeData.closing_days)
        ? safeData.closing_days
        : [],
      sector: safeData.sector || "",
      autoescuela_plan: safeData.autoescuela_plan === "profesor" ? "profesor" : "",
      tiene_whatsapp_business: safeData.tiene_whatsapp_business || false,
      logo_url: safeData.logo_url || "",
      logo_s3_key: safeData.logo_s3_key || "",
      contact_type: "",
      created_by: safeData.created_by || "",
      codigo_postal: safeData.codigo_postal || "",
      persona_contacto_id: safeData.persona_contacto_id || "",
      tablet_entregada: !!safeData.tablet_entregada,
      tablet_devuelta: !!safeData.tablet_devuelta,
      tablet_devuelta_at: safeData.tablet_devuelta_at || null,
      tablet_devuelta_motivo: safeData.tablet_devuelta_motivo || "",
      tablet_devuelta_by: safeData.tablet_devuelta_by || null,
      tablet_vendida: !!safeData.tablet_vendida,
      tablet_vendida_at: safeData.tablet_vendida_at || null,
      tablet_vendida_by: safeData.tablet_vendida_by || null,
      tablet_venta_precio:
        safeData.tablet_venta_precio != null ? Number(safeData.tablet_venta_precio) : 100,
      tablet_venta_metodo: safeData.tablet_venta_metodo || "efectivo",
      televenta: !!safeData.televenta,
      televenta_disposition: safeData.televenta_disposition || "",
      recordings: Array.isArray(safeData.recordings) ? safeData.recordings : [],
    });
    // Reset del flujo de devolución / venta al (re)cargar el lead.
    setReturningTablet(false);
    setTabletReturnReason("");
    setSellingTablet(false);

    // Cargar grupos disponibles (filtrados por list access del vendedor)
    const fetchGroups = async () => {
      try {
        const [groupsRes, accessRes] = await Promise.allSettled([
          axiosClient.get("/grupos?type=all"),
          axiosClient.get("/ventas/settings/access"),
        ]);
        const data = groupsRes.status === "fulfilled" ? (groupsRes.value?.data || groupsRes.value) : null;
        if (!Array.isArray(data?.grupos)) return;
        let allGroups = data.grupos.map((g) => g.nombre || g.name || g);

        // Filtrar por list access si el usuario es vendedor (level > 0)
        try {
          const token = localStorage.getItem("token");
          const me = token ? jwtDecode(token) : null;
          const myLevel = me?.level !== undefined ? Number(me.level) : 99;
          if (myLevel > 0 && accessRes.status === "fulfilled") {
            const listAccess = accessRes.value?.list_access || {};
            if (Object.keys(listAccess).length > 0) {
              // Fail-closed: solo mostrar listas donde el user está explícitamente incluido
              allGroups = allGroups.filter((g) => {
                const allowedUsers = listAccess[g];
                return Array.isArray(allowedUsers) && allowedUsers.includes(me.user_id);
              });

              // Auto-recuperar listas del contacto que no están en list_access (sin configurar).
              // Esto ocurre cuando el vendedor creó la lista antes de que existiera el auto-registro.
              const contactGroups = Array.isArray(initialData?.grupos) ? initialData.grupos : [];
              const missingUnconfigured = contactGroups.filter(
                (g) => !(g in listAccess) && !allGroups.includes(g)
              );
              for (const g of missingUnconfigured) {
                addSelfToListAccess(g); // fire-and-forget: persiste para la próxima vez
                allGroups = [...allGroups, g];
              }
            }
          }
        } catch (_) {}

        setAvailableGroups(allGroups);
      } catch (err) {
        console.warn("Error fetching groups:", err);
      }
    };
    fetchGroups();

    // Estado inicial de asignación: primer usuario de individual_access
    // (fuente de verdad mostrada en la tarjeta de ContactosListas).
    // Fallback: owner_user_id / assigned_user_id si se inyectara desde el chat.
    const iaRaw = safeData.individual_access;
    const iaList = iaRaw instanceof Set
      ? Array.from(iaRaw)
      : Array.isArray(iaRaw)
        ? iaRaw
        : [];
    setAssignedTo(
      iaList[0] || safeData.owner_user_id || safeData.assigned_user_id || "",
    );

    // Cargar workers — admin lo usa para reasignar; no-admin para resolver el nombre del chip
    (async () => {
      try {
        const res = await axiosClient.get("/usuarios");
        const list = Array.isArray(res) ? res : res?.data || [];
        setWorkers(list);
      } catch (err) {
        console.warn("Error fetching workers for assignment:", err?.message || err);
      }
    })();

    // Auto-lookup si el modal abre con CP + sector ya rellenos (modo edición)
    if (safeData.codigo_postal && /^\d{5}$/.test(safeData.codigo_postal) && safeData.sector) {
      const rawGroups = safeData.grupo || safeData.grupos;
      const existingGroups = Array.isArray(rawGroups)
        ? rawGroups
        : rawGroups
        ? [rawGroups]
        : ["general"];
      handleCPLookup(safeData.codigo_postal, safeData.sector, existingGroups);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]);

  // Cargar estado de las credenciales del admin trial cuando el lead tiene
  // `trial_company_id` y consta que se crearon accesos. Permite al equipo
  // volver a visualizar usuario/contraseña mientras el cliente no haya hecho
  // el primer cambio de contraseña.
  useEffect(() => {
    if (!open) return;
    const trialCompanyId =
      sessionCredentials?.company_id || initialData?.trial_company_id;
    const hasAccesos =
      !accesosClearedInSession &&
      (accesosCreatedInSession ||
        !!initialData?.accesos_created_at ||
        !!initialData?.accesos_sent_at);
    // Sin empresa enlazada en la ficha, se busca por su móvil: las altas que el cliente se
    // hace solo en la web no escriben `trial_company_id` en c_contactos (13/10/2026).
    const phoneLookup =
      !accesosClearedInSession &&
      (initialData?.phone_number || String(contactId || "").split("#").pop() || "");
    const params =
      trialCompanyId && (hasAccesos || !accesosClearedInSession)
        ? { company_id: trialCompanyId }
        : /\d{6,}/.test(phoneLookup || "")
          ? { phone: phoneLookup }
          : null;
    if (!params) {
      setAccesosStatus(null);
      return;
    }
    let cancelled = false;
    setAccesosStatusLoading(true);
    axiosClient
      .get("/ventas/accesos-status", { params })
      .then((data) => {
        if (cancelled) return;
        // axiosClient interceptor devuelve response.data directamente.
        setAccesosStatus(data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("accesos-status fetch failed:", err?.message || err);
        setAccesosStatus(null);
      })
      .finally(() => {
        if (!cancelled) setAccesosStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    initialData?.trial_company_id,
    initialData?.accesos_created_at,
    initialData?.accesos_sent_at,
    initialData?.phone_number,
    contactId,
    accesosCreatedInSession,
    accesosClearedInSession,
    sessionCredentials?.company_id,
  ]);

  if (!open) return null;

  // Valores derivados para la vista previa (calculados en render, sin hooks)
  const _previewUser = (() => { try { return jwtDecode(localStorage.getItem("token")); } catch { return null; } })();
  const tarjetaPreviewUrl = (() => {
    const url = getTarjetaUrl(_previewUser?.user_id);
    // Si cae al default (otro usuario), no mostrar imagen falsa
    return url !== "https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/shared-assets/tarjeta_visita.jpeg" ? url : null;
  })();
  const _rawSenderPreview = ((_previewUser?.full_name || _previewUser?.user_id || "ConextCRM").split("@")[0] || "ConextCRM");
  const senderPreviewName = _rawSenderPreview.charAt(0).toUpperCase() + _rawSenderPreview.slice(1);

  // Detectar si a este lead ya se le han creado/enviado accesos previamente.
  // - "ENVIADOS": se enviaron por WhatsApp (campo `accesos_sent_at`).
  // - "CREADOS": solo se creó la cuenta en plataforma sin envío (campo
  //   `accesos_created_at`).
  // El registro se puede limpiar manualmente (botón × en el chip).
  const accesosWasSentByWA =
    !accesosClearedInSession &&
    (accesosSentInSession || !!initialData?.accesos_sent_at);
  // Cuenta encontrada por su móvil (alta sola en la web): ya tiene cuenta, así que
  // «Crear Accesos» no debe crearle otra; pero «Limpiar accesos» no aplica (no es nuestra).
  const cuentaPorTelefono =
    !accesosClearedInSession &&
    accesosStatus?.resolved_by === "phone" &&
    !!accesosStatus?.exists;
  const accesosWasCreated =
    !accesosClearedInSession &&
    (accesosCreatedInSession ||
      !!initialData?.accesos_created_at ||
      accesosWasSentByWA ||
      cuentaPorTelefono ||
      initialData?.last_contact?.type === "accesos");
  const accesosSent = accesosWasCreated;
  const accesosChipLabel = accesosWasSentByWA ? "Enviados" : "Creados";

  // Credenciales visibles:
  // - `sessionCredentials` (creadas en esta sesión) tiene prioridad: incluye
  //   el password en claro recién devuelto por v_create_client.
  // - Si no, `accesosStatus.temp_password` viene del backend, que ahora expone
  //   SIEMPRE la contraseña vigente del cliente (también tras el primer cambio).
  const credentialsUsername =
    sessionCredentials?.username || accesosStatus?.username || null;
  const credentialsPassword =
    sessionCredentials?.password || accesosStatus?.temp_password || null;
  // Código de login rápido (ej. "l7"): de la sesión al crear o del backend al
  // consultar accesos más tarde.
  const credentialsLoginCode =
    sessionCredentials?.login_code || accesosStatus?.login_code || null;
  const canViewCredentials = accesosSent && !!credentialsPassword;

  // Limpiar accesos (solo admin): borra los flags del lead, el tracking 'accesos'
  // y la empresa trial creada en el CRM. Permite volver a "Crear Accesos" para
  // pruebas sin acumular cuentas trial huérfanas.
  const handleClearAccesos = async () => {
    if (!contactId || accesosClearing) return;
    const trialCompanyId =
      sessionCredentials?.company_id || initialData?.trial_company_id || null;
    const confirmMsg = trialCompanyId
      ? `¿Limpiar accesos de este lead y ELIMINAR por completo la empresa trial "${trialCompanyId}" del CRM? Esta acción es irreversible.`
      : "¿Limpiar los accesos de este lead? (No hay empresa trial registrada que borrar.)";
    if (!window.confirm(confirmMsg)) return;
    setAccesosClearing(true);
    try {
      const data = await axiosClient.post("/ventas/clear-accesos", {
        contact_id: contactId,
        delete_company: true,
      });
      // Reset de estado en sesión → reactiva el botón "Crear Accesos".
      setAccesosClearedInSession(true);
      setAccesosCreatedInSession(false);
      setAccesosSentInSession(false);
      setSessionCredentials(null);
      setAccesosStatus(null);
      const deleted = data?.company_deleted;
      window.alert(
        deleted
          ? `Accesos limpiados y empresa trial ${data?.company_id} eliminada del CRM.`
          : "Accesos limpiados. Ya puedes crear accesos de nuevo.",
      );
    } catch (err) {
      console.error("clear-accesos failed:", err);
      window.alert(
        err?.response?.data?.error ||
          "No se pudieron limpiar los accesos. Inténtalo de nuevo.",
      );
    } finally {
      setAccesosClearing(false);
    }
  };

  // Historial de envíos (tarjeta y Flow). Cada canal expone el mismo modelo:
  // un array de { at, by_user_id, by_name } combinando lo persistido en el lead
  // (initialData[`${ch}_sends`], con fallback a los campos legacy de un solo
  // envío) con lo enviado en esta sesión. Si el canal se "limpió" en sesión
  // (cambio de número), se ignora el histórico de initialData.
  const buildSends = (channel, clearedInSession, sessionSends) => {
    if (clearedInSession) return sessionSends;
    const arr = initialData?.[`${channel}_sends`];
    let base = [];
    if (Array.isArray(arr) && arr.length) {
      base = arr;
    } else if (initialData?.[`${channel}_sent_at`]) {
      // Lead antiguo sin array: sintetizar una entrada de los campos legacy.
      base = [
        {
          at: initialData[`${channel}_sent_at`],
          by_user_id: initialData[`${channel}_sent_by_user_id`] || null,
          by_name: initialData[`${channel}_sent_by_name`] || null,
        },
      ];
    } else if (initialData?.[`${channel}_sent_by_user_id`]) {
      base = [
        {
          at: null,
          by_user_id: initialData[`${channel}_sent_by_user_id`],
          by_name: initialData[`${channel}_sent_by_name`] || null,
        },
      ];
    }
    return [...base, ...sessionSends];
  };

  // Formatea la fecha de un envío para el chip ("5 jun") y el tooltip ("5 jun 2026").
  const formatSendDate = (iso, withYear = false) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      ...(withYear ? { year: "numeric" } : {}),
    });
  };

  // Datos derivados que consume el chip/tooltip de un canal.
  const sendInfo = (sends) => {
    if (!sends.length) return { sent: false };
    const last = sends[sends.length - 1];
    const lastName = last?.by_name || last?.by_user_id || null;
    return {
      sent: true,
      count: sends.length,
      lastName,
      lastDate: formatSendDate(last?.at),
      // Texto corto del chip: "Nombre · 5 jun" (o solo nombre si no hay fecha).
      chipLabel: [lastName || "Enviada", formatSendDate(last?.at)]
        .filter(Boolean)
        .join(" · "),
      entries: sends,
    };
  };

  const tarjetaSendsList = buildSends(
    "tarjeta",
    tarjetaClearedInSession,
    tarjetaSessionSends,
  );
  const flowSendsList = buildSends("flow", flowClearedInSession, flowSessionSends);
  const emailSendsList = buildSends("email", false, emailSessionSends);
  const marketingEmailSendsList = buildSends("marketing_email", false, marketingEmailSessionSends);
  const marketingFlowSendsList = buildSends("marketing_flow", false, marketingFlowSessionSends);
  const tarjetaInfo = sendInfo(tarjetaSendsList);
  const flowInfo = sendInfo(flowSendsList);
  const emailInfo = sendInfo(emailSendsList);
  const marketingEmailInfo = sendInfo(marketingEmailSendsList);
  const marketingFlowInfo = sendInfo(marketingFlowSendsList);
  const tarjetaSent = tarjetaInfo.sent;
  const flowSent = flowInfo.sent;
  const emailSent = emailInfo.sent;
  const tarjetaSentBy = tarjetaInfo.lastName || null;

  // Tooltip de historial al pasar el ratón sobre el botón (tarjeta o Flow).
  // Lista cada envío con quién y cuándo. `label` = "la tarjeta" | "el Flow";
  // `pronoun` = clítico para concordancia ("la" tarjeta / "lo" Flow).
  const renderSendHistoryTooltip = (info, label, pronoun = "lo") => (
    <div
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity z-50"
    >
      <div className="bg-slate-900 text-white text-xs font-medium rounded-xl px-3 py-2 shadow-2xl leading-snug">
        {info.count > 1 ? (
          <>
            <div className="mb-1 font-bold">Enviado {info.count} veces:</div>
            <ul className="space-y-0.5">
              {info.entries
                .slice()
                .reverse()
                .map((s, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>{s.by_name || s.by_user_id || "—"}</span>
                    <span className="text-slate-300">
                      {formatSendDate(s.at, true) || "fecha desconocida"}
                    </span>
                  </li>
                ))}
            </ul>
          </>
        ) : (
          <>
            Ya se envió {label}
            {info.lastName ? ` (${info.lastName})` : ""}
            {info.entries[0]?.at
              ? ` el ${formatSendDate(info.entries[0].at, true)}`
              : ""}
            . Puedes reenviar{pronoun} si hace falta.
          </>
        )}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-slate-900" />
      </div>
    </div>
  );

  /**
   * Crea/actualiza el contacto de la "Persona de Contacto" con nombre
   * "Persona (Negocio)" y lo enlaza al lead padre. Robusto ante el contacto
   * "nombre = teléfono" que pudo crear el envío de tarjeta/flow: ante un 409 de
   * teléfono ya existente, lo adopta+renombra+enlaza (re-POST con contact_id).
   * Devuelve el contact_id resuelto de la persona (o el existente si no se pudo).
   */
  const ensurePersonaContacto = async ({ personaPhone, leadPhone, parentLeadId }) => {
    const existingId = formData.persona_contacto_id || null;
    if (!personaPhone || !parentLeadId) return existingId;
    // Guard: si comparte teléfono con el lead, no crear hijo (pisaría el nombre del padre).
    if (personaPhone === leadPhone) return existingId;

    const nombre = computePersonaContactName(
      formData.persona_contacto,
      formData.nombre,
    );

    const childGroupsRaw = Array.isArray(formData.grupos) ? formData.grupos : [];
    const childGroups =
      childGroupsRaw.length > 1
        ? childGroupsRaw.filter((g) => g !== "general")
        : childGroupsRaw.length > 0
          ? childGroupsRaw
          : ["general"];

    const basePayload = {
      phone_number: personaPhone,
      sector: formData.sector || "",
      grupo: childGroups,
      parent_lead_id: parentLeadId,
    };
    if (nombre) basePayload.nombre = nombre;
    if (existingId) basePayload.contact_id = existingId;

    let resolved = existingId;
    try {
      const resp = await axiosClient.post("/ventas/bulk-contacts", basePayload);
      resolved = resp?.contact_id || existingId;
    } catch (err) {
      const data = err?.response?.data || {};
      if (err?.response?.status === 409 && data.contact_id) {
        // Teléfono ya existe (p. ej. contacto "nombre = teléfono" creado por el
        // envío): adoptarlo por contact_id (ruta UPDATE) para renombrar+enlazar.
        try {
          const adoptResp = await axiosClient.post("/ventas/bulk-contacts", {
            ...basePayload,
            contact_id: data.contact_id,
          });
          resolved = adoptResp?.contact_id || data.contact_id;
        } catch (adoptErr) {
          console.warn(
            "[LeadEditModal] No se pudo adoptar el contacto existente:",
            adoptErr?.message || adoptErr,
          );
          return existingId;
        }
      } else {
        console.warn(
          "[LeadEditModal] Error gestionando contacto hijo:",
          err?.message || err,
        );
        return existingId;
      }
    }

    // Defensa: nunca auto-referenciar al lead padre.
    if (resolved && resolved === parentLeadId) {
      console.error(
        "[LeadEditModal] bulk-contacts devolvió el contact_id del lead padre — abortando vinculación",
      );
      return existingId;
    }

    // Enlazar el padre si cambió.
    if (resolved && resolved !== existingId) {
      try {
        await axiosClient.post("/ventas/bulk-contacts", {
          contact_id: parentLeadId,
          persona_contacto_id: resolved,
        });
      } catch (linkErr) {
        console.warn(
          "[LeadEditModal] Error enlazando persona_contacto_id:",
          linkErr?.message || linkErr,
        );
      }
    }
    return resolved;
  };

  /**
   * Antes de enviar tarjeta/flow al número PRINCIPAL: garantizar que el lead es
   * "dueño" de ese phone_number en el índice, para que la conversación caiga en
   * el lead y NO se cree un contacto duplicado nombrado con el teléfono.
   * Devuelve true si se puede continuar con el envío, false si hay que abortar.
   */
  const ensureLeadOwnsMainPhone = async ({ mainPhone }) => {
    if (!mainPhone || !contactId) return true;
    let companyId = null;
    try {
      companyId = jwtDecode(localStorage.getItem("token"))?.company_id || null;
    } catch {
      companyId = null;
    }
    const storedPhone = initialData?.phone_number || null;
    if (
      !shouldCanonicalizeMainPhone({
        contactId,
        storedPhone,
        ownedPhone: ownedMainPhone,
        companyId,
        mainPhone,
      })
    ) {
      return true; // el lead ya posee el número
    }

    try {
      await axiosClient.post("/ventas/bulk-contacts", {
        contact_id: contactId,
        phone_number: mainPhone,
      });
      setOwnedMainPhone(mainPhone);
      onSaved?.();
      return true;
    } catch (err) {
      const data = err?.response?.data || {};
      if (
        err?.response?.status === 409 &&
        data.error === "PHONE_ALREADY_IN_USE" &&
        data.contact_id
      ) {
        // Ese número ya tiene una conversación/contacto aparte → fusionarlo en el
        // lead reutilizando migrate-id (absorbe el duplicado ligero; conserva su chat).
        try {
          await axiosClient.post("/contactos/migrate-id", {
            old_contact_id: contactId,
            new_phone: mainPhone,
          });
          onSaved?.();
          alert(
            "He vinculado la conversación existente de ese número con este lead. Vuelve a pulsar para enviar.",
          );
          return false; // abortar este envío; el lead ya quedó canónico para el siguiente
        } catch (migErr) {
          if (migErr?.response?.status === 409) {
            alert(
              "Ese número ya pertenece a otro lead. Cámbialo o fusiónalos antes de enviar.",
            );
            return false;
          }
          console.warn(
            "[LeadEditModal] migrate-id falló antes de enviar:",
            migErr?.message || migErr,
          );
          return true; // degradar al comportamiento actual, no bloquear
        }
      }
      console.warn(
        "[LeadEditModal] No se pudo fijar el teléfono del lead antes de enviar:",
        err?.message || err,
      );
      return true; // degradar, no bloquear
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // El teléfono solo es obligatorio al CREAR un lead (se usa como identificador
    // sellado en el contact_id). Al EDITAR un lead existente —p.ej. uno scrapeado
    // de Google Maps que se guardó sin teléfono— permitimos guardar cambios de
    // seguimiento sin teléfono. Antes esta validación cortaba el guardado en
    // SILENCIO (solo un borde rojo, a menudo en una columna colapsada), por lo
    // que el usuario veía "no guarda nada".
    if (!contactId && !formData.phone_number.trim()) {
      setPhoneError(true);
      expandColumn("info"); // asegurar que el campo y su aviso sean visibles
      alert("⚠️ El teléfono es obligatorio para crear el lead.");
      return false;
    }
    setPhoneError(false);

    // Validar longitud del número (cuando hay número). Un móvil español de 8
    // dígitos en vez de 9 produce un destinatario que Meta rechaza al entregar
    // (error 131026), generando la "!" en el chat. Validamos antes de guardar y
    // marcamos el campo en rojo con el mensaje inline (sin alert).
    if (formData.phone_number.trim() && !formData.phone_number.trim().startsWith("+")) {
      const mainCheck = validateNationalPhone(formData.prefix, formData.phone_number);
      if (!mainCheck.valid) {
        setPhoneError(true);
        setPhoneErrorMsg(mainCheck.message);
        expandColumn("info");
        return false;
      }
    }
    setPhoneErrorMsg("");
    const rawMovilTrim = (formData.telefono_movil || "").trim();
    if (rawMovilTrim && !rawMovilTrim.startsWith("+")) {
      const movilCheck = validateNationalPhone(formData.prefix_movil, rawMovilTrim);
      if (!movilCheck.valid) {
        setMovilError(movilCheck.message);
        expandColumn("info");
        return false;
      }
    }
    setMovilError("");
    // Si está en curso una devolución de tablet, el motivo es obligatorio.
    if (returningTablet && !tabletReturnReason.trim()) {
      alert("Indica el motivo por el que el cliente ha devuelto la tablet.");
      return false;
    }
    // Si está en curso una venta de tablet, el importe (>0) y la forma de pago
    // son obligatorios.
    if (sellingTablet) {
      const precio = Number(formData.tablet_venta_precio);
      if (!Number.isFinite(precio) || precio <= 0) {
        alert("Indica un importe de venta válido (mayor que 0 €).");
        return false;
      }
      if (!formData.tablet_venta_metodo) {
        alert("Indica la forma de pago de la tablet (efectivo o cargo a su cuenta).");
        return false;
      }
    }
    setLoading(true);
    try {
      const {
        prefix,
        phone_number,
        prefix_movil,
        competitor,
        customCompetitor,
        callReminderDate,
        notes,
        grupos,
        v_assets_ready, // Excluir del payload (solo para UI)
        contact_type, // Excluir del payload (solo para mark-contacted)
        persona_contacto_id,
        ...rest
      } = formData;

      // Determinar competencia final
      const finalCompetitor =
        competitor === "Otros" ? customCompetitor : competitor;

      // Determinar teléfono
      let finalPhone = phone_number
        ? phone_number.startsWith("+")
          ? phone_number
          : `${prefix}${phone_number}`
        : undefined;

      // Número completo de la persona de contacto
      const rawMovil = (formData.telefono_movil || "").trim();
      const fullPhoneMovil = rawMovil
        ? rawMovil.startsWith("+")
          ? rawMovil
          : `${prefix_movil}${rawMovil}`
        : null;

      // Devolución en curso: el usuario pulsó "Ha devuelto la tablet" y escribió
      // el motivo. Fuerza tablet_entregada:false y registra la devolución.
      const tabletReturned = returningTablet && !!formData.tablet_entregada;
      // Venta en curso: el usuario pulsó "Marcar como vendida" y rellenó importe +
      // forma de pago. Fuerza tablet_entregada:false y registra la venta. Igual que
      // la devolución, cancela las tareas de seguimiento de la tablet.
      const tabletSold = sellingTablet && !!formData.tablet_entregada;
      // "Recién entregada" = se acaba de activar la entrega (crea las 2 tareas). NO
      // debe dispararse al DEVOLVER ni al VENDER en la misma sesión: initialData no
      // se refresca tras guardar, así que sin excluirlos re-crearía las tareas.
      const tabletJustDelivered =
        !!formData.tablet_entregada &&
        !initialData?.tablet_entregada &&
        !tabletReturned &&
        !tabletSold;

      // Solo enviar `grupo` si el usuario realmente cambió las listas.
      // Si lo enviamos siempre, el backend reescribe el SS en cada save y
      // puede añadir/perder etiquetas sin que la usuaria haya tocado nada.
      const initialRawGroups = initialData?.grupo || initialData?.grupos;
      const initialGrps = Array.isArray(initialRawGroups)
        ? initialRawGroups
        : initialRawGroups
          ? [initialRawGroups]
          : [];
      const currentGrps = Array.isArray(grupos) ? grupos : [];
      const sameGroups =
        initialGrps.length === currentGrps.length &&
        currentGrps.every((g) => initialGrps.includes(g));
      // Regla: si hay otra lista, "general" no debe coexistir.
      const sanitizedGrps =
        currentGrps.length > 1
          ? currentGrps.filter((g) => g !== "general")
          : currentGrps;

      const payload = {
        ...rest,
        competitor: finalCompetitor,
        phone_number: finalPhone,
        call_reminder_date: callReminderDate
          ? callReminderDate.toISOString()
          : null, // Guardar fecha en vez de días
        notes: notes, // Guardar como campo persistente en el contacto
        tablet_entregada:
          tabletReturned || tabletSold ? false : !!formData.tablet_entregada,
        tablet_entregada_at: tabletJustDelivered
          ? new Date().toISOString()
          : initialData?.tablet_entregada_at || null,
        // Vendedor que entregó la tablet (para el chip en Supervisión de tablets)
        tablet_entregada_by: tabletJustDelivered
          ? myJwt?.user_id || null
          : initialData?.tablet_entregada_by || null,
        // Estado de devolución. Devolución en curso → registrar; resto → preservar
        // lo que tenga formData (fuente de verdad; el padre no refresca initialData
        // tras guardar). La re-entrega limpia formData en el toggle, así que aquí
        // el `null` se traduce en REMOVE en el backend.
        ...(tabletReturned
          ? {
              tablet_devuelta: true,
              tablet_devuelta_at: new Date().toISOString(),
              tablet_devuelta_by: myJwt?.user_id || null,
              tablet_devuelta_motivo: tabletReturnReason.trim(),
            }
          : {
              tablet_devuelta: !!formData.tablet_devuelta,
              tablet_devuelta_at: formData.tablet_devuelta_at || null,
              tablet_devuelta_motivo: formData.tablet_devuelta_motivo || null,
              tablet_devuelta_by: formData.tablet_devuelta_by || null,
            }),
        // Estado de venta. Venta en curso → registrar; resto → preservar lo que
        // tenga formData (la re-entrega limpia estos campos en el toggle, así que
        // el `null` se traduce en REMOVE en el backend).
        // tablet_venta_precio se envía como STRING (igual que competitor_price): el
        // backend lo guarda con cleanValue(), que hace .trim() y rompería con number.
        ...(tabletSold
          ? {
              tablet_vendida: true,
              tablet_vendida_at: new Date().toISOString(),
              tablet_vendida_by: myJwt?.user_id || null,
              tablet_venta_precio: String(Number(formData.tablet_venta_precio)),
              tablet_venta_metodo: formData.tablet_venta_metodo,
            }
          : {
              tablet_vendida: !!formData.tablet_vendida,
              tablet_vendida_at: formData.tablet_vendida_at || null,
              tablet_vendida_by: formData.tablet_vendida_by || null,
              tablet_venta_precio:
                formData.tablet_vendida && formData.tablet_venta_precio != null
                  ? String(Number(formData.tablet_venta_precio))
                  : null,
              tablet_venta_metodo: formData.tablet_vendida
                ? formData.tablet_venta_metodo || null
                : null,
            }),
      };
      if (!sameGroups || sanitizedGrps.length !== currentGrps.length) {
        payload.grupo = sanitizedGrps;
      }

      // No enviar logo_s3_key si no cambió: "" en cada guardado borraría el logo en el backend.
      const initialLogoKey = initialData?.logo_s3_key || "";
      if ((formData.logo_s3_key || "") === initialLogoKey) {
        delete payload.logo_s3_key;
      }

      let currentContactId = contactId;

      if (contactId) {
        // Si cambió el teléfono principal, migrar primero la clave del contacto.
        // El contact_id va "sellado" con el teléfono (company#+telefono): un
        // simple update del atributo dejaría la clave desincronizada. migrate-id
        // reubica el contacto (con su chat y mensajes) a la clave nueva.
        const phoneDigits = (s) => `${s || ""}`.replace(/\D+/g, "");
        // El teléfono "canónico" del lead es el sellado en el propio contactId
        // (company#+telefono) — la MISMA fuente que migrate-id usa para decidir
        // si hay cambio real. Comparar contra initialData.phone_number daba
        // FALSOS POSITIVOS cuando éste venía en otro formato (p.ej. sin prefijo):
        // phoneChanged se activaba sin cambio real, migrate-id respondía "IDs
        // equal" y aun así se ejecutaba la limpieza de accesos, borrando el sello.
        const idPhone = (contactId || "").includes("#")
          ? contactId.split("#").pop()
          : "";
        const canonicalOldPhone = idPhone.startsWith("+")
          ? idPhone
          : initialData?.phone_number || "";
        const initialDigits = phoneDigits(canonicalOldPhone);
        const phoneChanged =
          !!finalPhone &&
          !!initialDigits &&
          phoneDigits(finalPhone) !== initialDigits;
        // Se confirma solo tras migrate-id (la clave se movió de verdad).
        let actuallyMigrated = false;

        // Al cambiar el número, la tarjeta y los accesos se enviaron al número
        // anterior (equivocado): hay que limpiarlos para reenviarlos al nuevo.
        const trialToDelete =
          sessionCredentials?.company_id || initialData?.trial_company_id || null;
        const hadAccesos =
          !!trialToDelete ||
          !!initialData?.accesos_sent_at ||
          !!initialData?.accesos_created_at ||
          accesosCreatedInSession;

        if (phoneChanged) {
          // Confirmar solo si se va a eliminar una empresa trial (irreversible).
          if (trialToDelete) {
            const ok = window.confirm(
              `Vas a cambiar el número principal del lead.\n\n` +
                `Se limpiarán la tarjeta y los accesos enviados al número anterior y se ELIMINARÁ la empresa trial "${trialToDelete}" creada con ese número (acción irreversible), para que puedas volver a enviarlos al número nuevo.\n\n¿Continuar?`,
            );
            if (!ok) {
              setLoading(false);
              return false;
            }
          }

          // 1) Migrar la clave del contacto al número nuevo.
          try {
            const mig = await axiosClient.post("/contactos/migrate-id", {
              old_contact_id: contactId,
              new_phone: finalPhone,
            });
            currentContactId = mig?.new_contact_id || currentContactId;
          } catch (migErr) {
            if (migErr?.response?.status === 409) {
              alert(
                "⚠️ Ya existe un contacto con ese número de teléfono. No se ha cambiado el número principal.",
              );
              setLoading(false);
              return false;
            }
            throw migErr;
          }

          // migrate-id devuelve la MISMA clave cuando el número no cambió de
          // verdad ("No migration needed, IDs are equal"). Solo si la clave se
          // movió realmente procede limpiar accesos/tarjeta y borrar el sello;
          // de lo contrario un falso positivo de phoneChanged borraría los
          // accesos de un lead que NO ha cambiado de número (incidente DP Diego).
          actuallyMigrated =
            !!currentContactId && currentContactId !== contactId;

          if (actuallyMigrated) {
            // 2) Limpiar accesos + eliminar la empresa trial vieja (sobre la clave
            //    nueva). Debe ir ANTES del update que borra trial_company_id.
            if (hadAccesos) {
              try {
                await axiosClient.post("/ventas/clear-accesos", {
                  contact_id: currentContactId,
                  delete_company: true,
                });
              } catch (clrErr) {
                console.warn(
                  "[LeadEditModal] No se pudieron limpiar los accesos tras el cambio de número:",
                  clrErr?.message || clrErr,
                );
              }
            }

            // 3) Marcar el borrado de tarjeta + Flow + accesos en el propio guardado.
            payload.tarjeta_sent_at = null;
            payload.tarjeta_sent_by_user_id = null;
            payload.tarjeta_sent_by_name = null;
            payload.tarjeta_sends = [];
            payload.flow_sent_at = null;
            payload.flow_sent_by_user_id = null;
            payload.flow_sent_by_name = null;
            payload.flow_sends = [];
            payload.accesos_sent_at = null;
            payload.accesos_created_at = null;
            payload.trial_admin_username = null;
            payload.trial_company_id = null;
          }
        }

        // ACTUALIZAR (sobre la clave nueva si hubo migración)
        payload.contact_id = currentContactId;
        await axiosClient.post("/ventas/bulk-contacts", payload);

        // 4) Reactivar "Enviar Tarjeta", "Enviar Flow" y "Crear Accesos" en la
        //    sesión. Solo si la clave migró de verdad (no en falsos positivos).
        if (actuallyMigrated) {
          setTarjetaSessionSends([]);
          setFlowSessionSends([]);
          setTarjetaClearedInSession(true);
          setFlowClearedInSession(true);
          setAccesosClearedInSession(true);
          setAccesosCreatedInSession(false);
          setAccesosSentInSession(false);
          setSessionCredentials(null);
          setAccesosStatus(null);
        }
      } else {
        // CREAR
        try {
          const resp = await axiosClient.post("/ventas/bulk-contacts", payload);
          currentContactId = resp.contact_id;
        } catch (createErr) {
          // Un admin (level 0) tiene acceso a todos los leads: el 409 no es un
          // problema de permisos sino un duplicado (p.ej. contact_id sellado con
          // un teléfono antiguo). En vez de mostrar "Solicitar acceso", ofrecemos
          // actualizar el lead existente directamente.
          if (createErr?.response?.status === 409 && isAdmin) {
            const dupData = createErr.response.data || {};
            const existingId = dupData.contact_id;
            const existingName = dupData.contact_name || "lead existente";
            if (!existingId) throw createErr;
            const ok = window.confirm(
              `Ya existe un lead con ese número: «${existingName}». ` +
                `¿Quieres actualizarlo con estos datos?`,
            );
            if (!ok) {
              setLoading(false);
              return false;
            }
            payload.contact_id = existingId;
            await axiosClient.post("/ventas/bulk-contacts", payload);
            currentContactId = existingId;
          } else {
            throw createErr;
          }
        }
      }

      // Gestión del contacto hijo (persona de contacto)
      let resolvedPersonaContactoId = persona_contacto_id || null;

      if (formData.persona_contacto && fullPhoneMovil) {
        // Guard: si el móvil de la persona coincide con el teléfono del lead,
        // bulk-contacts dedupea por phone y reescribiría el nombre del lead.
        if (fullPhoneMovil === finalPhone) {
          console.warn(
            "[LeadEditModal] Persona de contacto comparte teléfono con el lead — no se crea contacto hijo para evitar pisar el nombre del lead",
          );
          alert(
            "⚠️ El teléfono móvil de la persona de contacto coincide con el del lead. No se ha creado un contacto separado para evitar pisar el nombre del lead. Usa un número distinto si quieres un contacto independiente.",
          );
        } else {
          resolvedPersonaContactoId = await ensurePersonaContacto({
            personaPhone: fullPhoneMovil,
            leadPhone: finalPhone,
            parentLeadId: currentContactId,
          });
        }
      } else if (resolvedPersonaContactoId && currentContactId) {
        // Desvincular si se vaciaron los campos y había un contacto hijo
        try {
          await axiosClient.post("/ventas/bulk-contacts", {
            contact_id: currentContactId,
            persona_contacto_id: null,
          });
          resolvedPersonaContactoId = null;
        } catch (unlinkErr) {
          console.warn("[LeadEditModal] Error desvinculando contacto hijo:", unlinkErr?.message || unlinkErr);
        }
      }

      // Actualizar estado local para que el icono WhatsApp aparezca inmediatamente tras guardar
      if (resolvedPersonaContactoId !== (persona_contacto_id || null)) {
        setFormData((prev) => ({ ...prev, persona_contacto_id: resolvedPersonaContactoId || "" }));
      }

      // ¿Hubo cambio en el seguimiento? (evita tareas duplicadas al guardar sin cambios)
      // Baseline = lo último GUARDADO en esta sesión (followUpBaseline) o, si aún no
      // se ha guardado, `initialData`. Sin esto, re-guardar tras enviar Flow/Tarjeta
      // (el modal no se cierra) compararía contra los datos viejos y duplicaría la tarea.
      const followUpRef = followUpBaseline || initialData || {};
      const initNotes = followUpRef.notes || followUpRef.notas || "";
      const initCallDate = followUpRef.call_reminder_date
        ? new Date(followUpRef.call_reminder_date).toISOString()
        : null;
      const newCallDate = callReminderDate
        ? callReminderDate.toISOString()
        : null;
      const followUpChanged =
        formData.interest !== (followUpRef.interest || "") ||
        finalCompetitor !== (followUpRef.competitor || "") ||
        (notes || "") !== (initNotes || "") ||
        newCallDate !== initCallDate;

      // Crear tarea cuando: interés ≠ "No interesado"/"No existe"/"No corresponde" Y hubo cambio real
      // Fecha: la seleccionada (recordatorio) o dentro de 2 días
      const NO_FOLLOWUP_INTERESTS = ["No interesado", "No existe", "Autolavado", "No corresponde"];
      const isInterestFollowUp =
        formData.interest && !NO_FOLLOWUP_INTERESTS.includes(formData.interest);
      const shouldCreateTask = followUpChanged && (isInterestFollowUp || callReminderDate);

      let taskScheduledAt = null;
      let createdTaskId = null;
      if (shouldCreateTask) {
        let scheduledDate = null;
        if (callReminderDate) {
          const now = new Date();
          const todayStart = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0,
            0,
            0,
            0,
          );
          if (callReminderDate >= todayStart) {
            const h = callReminderDate.getHours();
            const m = callReminderDate.getMinutes();
            const sinHoraIndicada = h === 0 && m === 0;
            scheduledDate = new Date(callReminderDate);
            if (sinHoraIndicada) scheduledDate.setHours(8, 0, 0, 0);
          }
        }
        taskScheduledAt = scheduledDate ? scheduledDate.toISOString() : null;
        const taskResp = await axiosClient.post("/ventas/tasks", {
          title: `Seguimiento: ${formData.nombre || "Lead"}`,
          description:
            notes || `Recordatorio automático. Interés: ${formData.interest}`,
          scheduled_at: taskScheduledAt,
          priority: callReminderDate ? "ALTA" : "MEDIA",
          contact_ids: currentContactId ? [currentContactId] : [],
          created_by: "Sistema",
          task_type: "follow_up",
          interest: formData.interest,
        });
        createdTaskId = taskResp?.task_id || null;
      }

      // Tablet entregada → crear 2 tareas (10 días seguimiento, 28 días verificación)
      let tabletTasksCreated = false;
      if (tabletJustDelivered && currentContactId) {
        const leadName = formData.nombre || "Lead";
        const makeDate = (daysAhead) => {
          const d = new Date();
          d.setDate(d.getDate() + daysAhead);
          d.setHours(9, 0, 0, 0);
          return d;
        };
        const day10 = makeDate(10);
        const day28 = makeDate(28);
        try {
          await axiosClient.post("/ventas/tasks", {
            title: `Tablet — Seguimiento (10d): ${leadName}`,
            description:
              "Tablet entregada hace 10 días. Llamar al cliente y comprobar cómo lleva el uso (configuración, dudas, fricciones).",
            scheduled_at: day10.toISOString(),
            priority: "MEDIA",
            contact_ids: [currentContactId],
            created_by: "Sistema",
            task_type: "tablet_checkin",
          });
          await axiosClient.post("/ventas/tasks", {
            title: `Tablet — Verificación seguridad (28d): ${leadName}`,
            description:
              "Tablet entregada hace 28 días. Asegurar que sigue en buen uso o coordinar recuperación si el cliente no la está usando.",
            scheduled_at: day28.toISOString(),
            priority: "ALTA",
            contact_ids: [currentContactId],
            created_by: "Sistema",
            task_type: "tablet_security",
          });
          tabletTasksCreated = true;
        } catch (tabletErr) {
          console.warn("[LeadEditModal] Error creando tareas de tablet:", tabletErr?.message || tabletErr);
        }
      }

      // Tablet devuelta O vendida → cancelar las 2 tareas de seguimiento pendientes
      // (uso / verificación): ya no hay tablet en préstamo que seguir. Best-effort
      // (no aborta el guardado).
      if ((tabletReturned || tabletSold) && currentContactId) {
        try {
          await axiosClient.post("/ventas/tasks", {
            action: "cancel_tablet_tasks",
            contact_id: currentContactId,
          });
        } catch (cancelErr) {
          console.warn(
            "[LeadEditModal] Error cancelando tareas de tablet:",
            cancelErr?.message || cancelErr,
          );
        }
      }

      // Marcar como contactado si se modificó cualquier campo de seguimiento
      if (contactId && currentContactId && followUpChanged) {
        const activityNotes = [
          notes && notes.trim() ? notes.trim() : null,
          formData.interest ? `Interés: ${formData.interest}` : null,
          finalCompetitor ? `Competencia: ${finalCompetitor}` : null,
        ]
          .filter(Boolean)
          .join(". ");
        await axiosClient.post("/sales/mark-contacted", {
          contact_id: currentContactId,
          notes: activityNotes || "Actualizado seguimiento",
          type: formData.contact_type || "call",
          interest: formData.interest || undefined,
          task_id: createdTaskId || undefined,
        });
      }

      if (tabletTasksCreated) {
        alert(
          "📱 Se han creado 2 tareas de seguimiento de la tablet: una a los 10 días (uso) y otra a los 28 días (verificación de seguridad).",
        );
      }

      // Informar si se creó tarea de seguimiento y cuándo
      if (taskScheduledAt) {
        const d = new Date(taskScheduledAt);
        const fechaStr = d.toLocaleString("es-ES", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
        alert(`✅ Se ha creado una tarea de seguimiento para el ${fechaStr}`);
      } else if (followUpChanged && NO_FOLLOWUP_INTERESTS.includes(formData.interest)) {
        alert(
          `ℹ️ No se ha creado tarea de seguimiento (nivel «${formData.interest}» no genera tareas automáticas)`,
        );
      }

      if (onSaved) onSaved(payload);
      // Tras registrar la devolución o la venta, reflejarla en formData (el modal no
      // se cierra) para que el bloque pase a "Tablet devuelta"/"Tablet vendida" y el
      // baseline quede consistente.
      const tabletStateAfterSave = tabletReturned
        ? {
            tablet_entregada: false,
            tablet_devuelta: true,
            tablet_devuelta_at: payload.tablet_devuelta_at,
            tablet_devuelta_motivo: payload.tablet_devuelta_motivo,
            tablet_devuelta_by: payload.tablet_devuelta_by,
          }
        : tabletSold
          ? {
              tablet_entregada: false,
              tablet_vendida: true,
              tablet_vendida_at: payload.tablet_vendida_at,
              tablet_vendida_by: payload.tablet_vendida_by,
              tablet_venta_precio: payload.tablet_venta_precio,
              tablet_venta_metodo: payload.tablet_venta_metodo,
            }
          : {};
      if (tabletReturned) {
        setFormData((prev) => ({ ...prev, ...tabletStateAfterSave }));
        setReturningTablet(false);
        setTabletReturnReason("");
      } else if (tabletSold) {
        setFormData((prev) => ({ ...prev, ...tabletStateAfterSave }));
        setSellingTablet(false);
      }
      // No cerramos el modal tras guardar: el usuario quiere seguir trabajando
      // en el lead (enviar Flow/Tarjeta, crear accesos, etc.) sin reabrirlo.
      // Pero SÍ marcamos el estado como "guardado": baseline limpio (para que la
      // X no avise de cambios sin guardar) + feedback visible en el botón.
      setSavedSnapshot(
        serializeFormForCompare({
          ...formData,
          ...tabletStateAfterSave,
          persona_contacto_id: resolvedPersonaContactoId || "",
        }),
      );
      // Refrescar el baseline de seguimiento a lo que se acaba de guardar, para que
      // un segundo guardado en la misma sesión (sin cambiar el seguimiento) NO cree
      // otra tarea de seguimiento duplicada.
      setFollowUpBaseline({
        interest: formData.interest || "",
        competitor: finalCompetitor || "",
        notes: notes || "",
        call_reminder_date: callReminderDate ? callReminderDate.toISOString() : null,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      return true;
    } catch (err) {
      console.error("Error saving lead:", err);
      // Solo el 409 de "acceso denegado" (lead de OTRO vendedor) abre el modal de
      // solicitud de acceso. Otros 409 (CONTACT_NOT_FOUND, PHONE_ALREADY_IN_USE)
      // solo muestran su mensaje. Los leads del admin ya no llegan aquí: el backend
      // concede el acceso automáticamente y guarda.
      if (
        err?.response?.status === 409 &&
        !isAdmin &&
        err?.response?.data?.code === "ACCESS_DENIED"
      ) {
        const data = err.response.data || {};
        setAccessDeniedData({
          contactId: data.contact_id || null,
          contactName: data.contact_name || null,
          contactPhone: data.contact_phone || formData.phone_number || null,
          contactLists: Array.isArray(data.contact_lists) ? data.contact_lists : [],
        });
        setShowAccessDenied(true);
        return false;
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      alert(msg ? `Error al guardar el lead: ${msg}` : "Error al guardar el lead");
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * sendTeleventaEmailIfAny() - Email de televenta por Resend (mismo copy/vídeo que el
   * Flow de WhatsApp, un template por sector en v_email_templates). Se manda SIEMPRE que
   * el lead tenga email, en el mismo click que el Flow (o solo, si no tiene teléfono
   * válido) — no es un botón aparte. No lanza: si no hay email, no hay template para el
   * sector, o Resend falla, devuelve { sent: false, reason } sin romper el envío de WhatsApp.
   */
  const sendTeleventaEmailIfAny = async (resolvedSector, product = "crm") => {
    const email = (formData.email || "").trim();
    if (!email) return { sent: false, reason: "no_email" };
    const templateId = product === "marketing"
      ? marketingEmailTemplateIdForSector(resolvedSector)
      : televentaEmailTemplateIdForSector(resolvedSector);
    if (!templateId) return { sent: false, reason: "no_template_for_sector" };

    try {
      console.log(`📧 Enviando email de ${product} (${templateId}) a`, email);
      await axiosClient.post("/sales/send-email", {
        contact_email: email,
        template_id: templateId,
        variables: { nombre: formData.nombre || "" },
        contact_id: contactId,
        notes: `Email ${product} enviado (${templateId}, sector=${resolvedSector})`,
      });

      const emailMe = (() => { try { return jwtDecode(localStorage.getItem("token")); } catch { return null; } })();
      const emailSenderUserId = emailMe?.user_id || emailMe?.username || "";
      const _emailRawSender = (emailMe?.full_name || emailMe?.user_id || "ConextCRM").split("@")[0] || "ConextCRM";
      const emailSenderName = _emailRawSender.charAt(0).toUpperCase() + _emailRawSender.slice(1);
      const emailNowIso = new Date().toISOString();
      const emailNewSend = { at: emailNowIso, by_user_id: emailSenderUserId, by_name: emailSenderName };

      const fieldPrefix = product === "marketing" ? "marketing_email" : "email";
      const currentSendsList = product === "marketing" ? marketingEmailSendsList : emailSendsList;
      const emailUpdatedSends = [...currentSendsList, emailNewSend];
      axiosClient
        .post("/ventas/bulk-contacts", {
          contact_id: contactId,
          [`${fieldPrefix}_sent_at`]: emailNowIso,
          [`${fieldPrefix}_sent_by_user_id`]: emailSenderUserId,
          [`${fieldPrefix}_sent_by_name`]: emailSenderName,
          [`${fieldPrefix}_sends`]: emailUpdatedSends,
        })
        .catch(() => {});
      if (product === "marketing") setMarketingEmailSessionSends((prev) => [...prev, emailNewSend]);
      else setEmailSessionSends((prev) => [...prev, emailNewSend]);
      console.log(`✅ Email de ${product} enviado`);
      return { sent: true };
    } catch (err) {
      console.error(`❌ Error enviando email de ${product}:`, err.response?.data?.message || err.message);
      return { sent: false, reason: err.response?.data?.message || err.message };
    }
  };

  /**
   * finalizeFlowSend() - Cola común a los dos caminos de envío del Flow (los 2 mensajes
   * de siempre y la plantilla única de televenta): marca el lead como contactado,
   * persiste quién lo envió y cuándo (chip "Reenviar Flow" + historial), manda TAMBIÉN
   * el email de televenta si el lead tiene email (mismo click, "pack" WhatsApp+email),
   * y avisa con el resumen de ambos canales.
   */
  const finalizeFlowSend = async ({ resolvedSector, notes, summary }) => {
    console.log("📝 Marcando como contactado...");
    await axiosClient.post("/sales/mark-contacted", {
      contact_id: contactId,
      type: "whatsapp",
      notes,
    });

    // Mismo modelo que la tarjeta: campos legacy (último envío) + array `flow_sends`.
    const flowMe = (() => {
      try {
        return jwtDecode(localStorage.getItem("token"));
      } catch {
        return null;
      }
    })();
    const flowSenderUserId = flowMe?.user_id || flowMe?.username || "";
    const _flowRawSender =
      (flowMe?.full_name || flowMe?.user_id || "ConextCRM").split("@")[0] || "ConextCRM";
    const flowSenderName = _flowRawSender.charAt(0).toUpperCase() + _flowRawSender.slice(1);
    const flowNowIso = new Date().toISOString();
    const flowNewSend = {
      at: flowNowIso,
      by_user_id: flowSenderUserId,
      by_name: flowSenderName,
    };
    const flowUpdatedSends = [...flowSendsList, flowNewSend];
    axiosClient
      .post("/ventas/bulk-contacts", {
        contact_id: contactId,
        flow_sent_at: flowNowIso,
        flow_sent_by_user_id: flowSenderUserId,
        flow_sent_by_name: flowSenderName,
        flow_sends: flowUpdatedSends,
      })
      .catch(() => {});
    setFlowSessionSends((prev) => [...prev, flowNewSend]);

    // "Pack" WhatsApp + email: si el lead tiene email, se manda en el MISMO click.
    const emailResult = await sendTeleventaEmailIfAny(resolvedSector);
    let finalSummary = summary;
    if (emailResult.sent) {
      finalSummary += "\n\n📧 Email de televenta enviado también (mismo copy + vídeo).";
    } else if (emailResult.reason && emailResult.reason !== "no_email" && emailResult.reason !== "no_template_for_sector") {
      finalSummary += `\n\n⚠️ El WhatsApp se envió, pero el email falló: ${emailResult.reason}`;
    }

    console.log("✨ Flow completado exitosamente");
    alert(finalSummary);
    onSaved?.();
  };

  /**
   * handleSendFlow() - Envía flow personalizado
   * 1. Genera miniatura personalizada con nombre del lead
   * 2. Envía Template #1 (barber_head3) con nombre
   * 3. Espera 1.5s
   * 4. Envía Template #2 (barber_mini2) con miniatura personalizada
   *
   * `televenta` (Flow televenta) → NO son dos mensajes: se envía UNA sola plantilla
   * (vídeo + todo el texto + los dos botones), sin miniatura. Solo en sectores con ad
   * producido (hoy peluquería); ver handleSendTeleventaFlow.
   */
  const handleSendFlow = async (
    overridePhone = null,
    sectorOverride = null,
    withPresentation = false,
    online = isTeleventa,
    televenta = false,
    marketing = false,
    emailOnly = false,
  ) => {
    // Validaciones prev
    if (!contactId) {
      alert("❌ El lead debe estar guardado antes de enviar el Flow");
      return;
    }

    const fullPhone = overridePhone || (
      formData.phone_number.startsWith("+")
        ? formData.phone_number
        : `${formData.prefix}${formData.phone_number}`
    );
    const hasValidPhone = isSendablePhone(fullPhone);
    const hasEmail = !!(formData.email || "").trim();

    if (emailOnly && !hasEmail) {
      alert("❌ Este lead no tiene email. Rellena el campo Email de la ficha, guarda y vuelve a enviarlo.");
      return;
    }

    if (!hasValidPhone && !hasEmail) {
      alert("❌ Este lead no tiene teléfono válido ni email. Rellena al menos uno antes de enviar.");
      return;
    }

    // Con teléfono pero SIN email: el correo no se manda. Antes se saltaba en silencio
    // y el vendedor no se enteraba de que le faltaba el dato → ahora se avisa y se le
    // ofrece cancelar para rellenarlo. Solo si el sector tiene email (si no, no hay nada
    // que reclamar).
    if (hasValidPhone && !hasEmail && !emailOnly) {
      const sectorForEmail = sectorOverride || formData.sector || null;
      const emailTplId = marketing
        ? marketingEmailTemplateIdForSector(sectorForEmail)
        : televentaEmailTemplateIdForSector(sectorForEmail);
      if (emailTplId) {
        const continuar = window.confirm(
          "⚠️ Este lead no tiene email.\n\n" +
            "Se enviará SOLO el WhatsApp. Si quieres que además le llegue el correo " +
            "(mismo vídeo + botón para reservar la demo), pulsa Cancelar, rellena el campo " +
            "Email de la ficha, guarda y vuelve a enviarlo.\n\n" +
            "¿Enviar solo el WhatsApp?",
        );
        if (!continuar) return;
      }
    }

    // Camino "solo email", sin tocar nada del Flow de WhatsApp (plantillas/miniatura/
    // mark-contacted de WhatsApp no aplican). Dos entradas: el lead no tiene teléfono
    // válido, o el vendedor eligió a propósito la variante "Solo email".
    if (!hasValidPhone || emailOnly) {
      const resolvedSectorEmailOnly = sectorOverride || formData.sector || null;
      if (!resolvedSectorEmailOnly) {
        alert("❌ Elige un sector antes de enviar el email.");
        return;
      }
      setSendingFlow(true);
      setFlowError(null);
      try {
        const emailResult = await sendTeleventaEmailIfAny(resolvedSectorEmailOnly);
        if (emailResult.sent) {
          alert(
            `✅ Email de televenta enviado correctamente a ${formData.email}` +
              (hasValidPhone
                ? "\n\n(Solo el email: el WhatsApp no se ha enviado.)"
                : "\n\n(Este lead no tiene WhatsApp válido, así que solo se manda el email.)"),
          );
          onSaved?.();
        } else if (emailResult.reason === "no_template_for_sector") {
          alert(`❌ El sector "${resolvedSectorEmailOnly}" todavía no tiene email de televenta (falta su vídeo/plantilla).`);
        } else {
          alert(`❌ No se pudo enviar el email: ${emailResult.reason}`);
        }
      } finally {
        setSendingFlow(false);
      }
      return;
    }

    // Resolver sector → plantilla "head".
    //  - El sector lo garantiza SendFlowModal (el del lead o el elegido allí); este guard
    //    es defensa en profundidad, no un camino normal.
    //  - Si el sector existe pero no tiene plantilla específica (spa, salud,
    //    autoescuela, etc.) → fallback silencioso a barber_head3.
    const resolvedSector = sectorOverride || formData.sector || null;
    if (!resolvedSector) {
      alert("❌ Elige un sector antes de enviar el Flow.");
      return;
    }
    // Flow televenta: UNA sola plantilla (vídeo + todo el texto + los dos botones), sin
    // miniatura. Solo en sectores con ad; si no lo tienen, cae a los 2 mensajes normales.
    const isSingle = televenta && hasTeleventaFlow(resolvedSector);
    const singleTemplateName = isSingle ? televentaTemplateNameForSector(resolvedSector) : null;

    // En televenta (online) la head usa el cierre online ("te llamaremos / sin
    // visita") y la mini con presentación abre el flow de demo ONLINE (videollamada).
    const headTemplateName = headTemplateNameForSector(resolvedSector, online);

    // Mini a enviar: la normal (botón "Simulador de cita"/"...pedido") o la variante
    // con el botón FLOW "Solicitar presentación", que abre el flow de reserva de demo
    // comercial. Se resuelve per-sector (comida usa comida_mini2 / comida_presentacion_mini).
    const miniTemplateName = miniTemplateNameForSector(resolvedSector, withPresentation, online);

    setSendingFlow(true);
    setFlowError(null);

    try {
      console.log(`🎬 Iniciando envío de Flow para: ${formData.nombre} (sector=${resolvedSector})`);

      // 1. Cargar plantillas para obtener templateData completo
      console.log("📋 Cargando plantillas...");
      const plantillasRes = await axiosClient.get("/plantillas");
      const allTemplates =
        plantillasRes?.data || plantillasRes?.plantillas || plantillasRes || [];
      const findTpl = (n) =>
        (Array.isArray(allTemplates) ? allTemplates.find((t) => t.name === n) : null) || null;

      // Flow de MARKETING (servicio de captación/reenganche, no el CRM): plantilla única
      // con vídeo de ejemplo, MISMO flow_id de reserva de demo pero con el flow_token
      // etiquetado #marketing para que el closer sepa de qué va la llamada.
      if (marketing) {
        if (!hasMarketingFlow(resolvedSector)) {
          throw new Error(`El sector "${resolvedSector}" todavía no tiene vídeo de marketing.`);
        }
        const marketingTemplateName = marketingTemplateNameForSector(resolvedSector);
        const tplMarketing = findTpl(marketingTemplateName);
        if (!tplMarketing) {
          throw new Error(`Plantilla "${marketingTemplateName}" no encontrada o no aprobada todavía. Inténtalo más tarde.`);
        }
        console.log(`📨 Enviando plantilla de marketing (${marketingTemplateName}) a`, fullPhone);
        await axiosClient.post("/send-template", {
          phone: fullPhone,
          template_id: tplMarketing.template_id || tplMarketing.id || marketingTemplateName,
          templateData: tplMarketing,
          params: { flow_token: `pres#${contactId}#marketing` },
          templateExtras: { header_url: marketingVideoUrlForSector(resolvedSector) },
          isPrivate: false,
        });
        console.log("✅ Plantilla de marketing enviada");

        await axiosClient.post("/sales/mark-contacted", {
          contact_id: contactId,
          type: "whatsapp",
          notes: `Flow marketing enviado (${marketingTemplateName}, sector=${resolvedSector})`,
        });
        const mfMe = (() => { try { return jwtDecode(localStorage.getItem("token")); } catch { return null; } })();
        const mfSenderUserId = mfMe?.user_id || mfMe?.username || "";
        const _mfRawSender = (mfMe?.full_name || mfMe?.user_id || "ConextCRM").split("@")[0] || "ConextCRM";
        const mfSenderName = _mfRawSender.charAt(0).toUpperCase() + _mfRawSender.slice(1);
        const mfNowIso = new Date().toISOString();
        const mfNewSend = { at: mfNowIso, by_user_id: mfSenderUserId, by_name: mfSenderName };
        const mfUpdatedSends = [...marketingFlowSendsList, mfNewSend];
        axiosClient
          .post("/ventas/bulk-contacts", {
            contact_id: contactId,
            marketing_flow_sent_at: mfNowIso,
            marketing_flow_sent_by_user_id: mfSenderUserId,
            marketing_flow_sent_by_name: mfSenderName,
            marketing_flow_sends: mfUpdatedSends,
          })
          .catch(() => {});
        setMarketingFlowSessionSends((prev) => [...prev, mfNewSend]);

        const emailResult = await sendTeleventaEmailIfAny(resolvedSector, "marketing");
        let mSummary = `✅ Flow de marketing enviado correctamente\n\n• Sector: ${SECTOR_LABELS[resolvedSector] || resolvedSector}\n• Vídeo de ejemplo + presentación del servicio de captación/reenganche`;
        if (emailResult.sent) mSummary += "\n\n📧 Email de marketing enviado también.";
        else if (emailResult.reason && emailResult.reason !== "no_email" && emailResult.reason !== "no_template_for_sector") {
          mSummary += `\n\n⚠️ El WhatsApp se envió, pero el email falló: ${emailResult.reason}`;
        }
        alert(mSummary);
        onSaved?.();
        return;
      }

      // ── Flow televenta: un único envío y salimos del camino de 2 mensajes ──────
      if (isSingle) {
        const tplSingle = findTpl(singleTemplateName);
        if (!tplSingle) {
          throw new Error(
            `Plantilla "${singleTemplateName}" no encontrada o no está aprobada todavía. Inténtalo más tarde.`,
          );
        }
        console.log(`📨 Enviando plantilla única (${singleTemplateName}) a`, fullPhone);
        await axiosClient.post("/send-template", {
          phone: fullPhone,
          template_id: tplSingle.template_id || tplSingle.id || singleTemplateName,
          templateData: tplSingle,
          // El botón FLOW "Solicitar presentación" necesita el flow_token para resolver
          // al vendedor del lead (c_demoPresentationFlow lo parsea).
          params: { 1: formData.nombre, flow_token: `pres#${contactId}` },
          // VIDEO para los sectores con ad, IMAGE (póster) para los que no lo tienen.
          templateExtras: { header_url: televentaHeaderForSector(resolvedSector).url },
          // Taller no tiene simulador: su plantilla lleva un único botón (el FLOW).
          ...(simulatorPayloadForSector(resolvedSector)
            ? { quick_reply_payload: simulatorPayloadForSector(resolvedSector) }
            : {}),
          isPrivate: false,
        });
        console.log("✅ Plantilla única enviada");
        // El resumen tiene que ser fiel a lo que se envía en cada sector: el header es
        // vídeo o póster según televentaHeaderForSector, y el botón de simulador solo
        // existe si el sector lo tiene (taller no: un único botón "Solicitar presentación").
        const singleHeaderType = televentaHeaderForSector(resolvedSector)?.type;
        const singleMediaLabel = singleHeaderType === "IMAGE" ? "póster" : "vídeo";
        const singleButtonsLabel = simulatorPayloadForSector(resolvedSector)
          ? `botones "Solicitar presentación" y "Simulador de cita"`
          : `botón "Solicitar presentación"`;
        await finalizeFlowSend({
          resolvedSector,
          notes: `Flow televenta enviado (${singleTemplateName}, plantilla única sector=${resolvedSector})`,
          summary: `✅ Flow televenta enviado correctamente\n\n• Sector: ${SECTOR_LABELS[resolvedSector] || resolvedSector}\n• Un solo mensaje (${singleTemplateName}): ${singleMediaLabel} + presentación + ${singleButtonsLabel}`,
        });
        return;
      }

      const tplHead = Array.isArray(allTemplates)
        ? allTemplates.find((t) => t.name === headTemplateName)
        : null;
      const tplMini2 = Array.isArray(allTemplates)
        ? allTemplates.find((t) => t.name === miniTemplateName)
        : null;
      console.log(
        `✅ Plantillas cargadas. ${headTemplateName}:`,
        !!tplHead,
        `${miniTemplateName}:`,
        !!tplMini2,
      );
      if (withPresentation && !tplMini2) {
        throw new Error(
          `Plantilla "${miniTemplateName}" no encontrada o no aprobada todavía. Inténtalo más tarde.`,
        );
      }
      if (!tplHead) {
        throw new Error(
          `Plantilla "${headTemplateName}" no encontrada o no está aprobada todavía. Inténtalo más tarde.`,
        );
      }

      // 2. Generar miniatura personalizada (incluye sector → cambia el logo
      //    del header por un círculo+emoji para lavadero/estética)
      console.log("📱 Generando miniatura personalizada...");
      const thumbRes = await axiosClient.post("/ventas/generate-thumbnail", {
        nombre: formData.nombre,
        sector: resolvedSector,
      });

      if (!thumbRes?.success) {
        throw new Error("No se generó la miniatura correctamente");
      }

      const miniatureUrl = thumbRes.url;
      console.log("✅ Miniatura generada:", miniatureUrl);

      // 3. Enviar Template #1 (head con nombre del lead)
      // La head compartida (conext_head6) lleva un 2º parámetro: el tipo de
      // negocio ({{2}}). Las heads per-sector (lavadero) y el fallback solo usan {{1}}.
      const headParams = headUsesBusinessType(headTemplateName)
        ? { 1: formData.nombre, 2: businessTypeForSector(resolvedSector) }
        : { 1: formData.nombre };
      console.log(`📨 Enviando Template #1 (${headTemplateName}) a`, fullPhone);
      const payload1 = {
        phone: fullPhone,
        template_id: tplHead?.template_id || tplHead?.id || headTemplateName,
        templateData: tplHead || undefined,
        params: headParams,
        isPrivate: false,
      };
      await axiosClient.post("/send-template", payload1);
      console.log("✅ Template #1 enviado");

      // 4. Esperar 1.5s
      await new Promise((r) => setTimeout(r, 1500));

      // 5. Enviar Template #2 (mini — con miniatura sector-aware). Si es la variante
      //    con presentación, adjuntamos el flow_token "pres#<contact_id>" para que el
      //    botón FLOW abra el flow y resuelva el vendedor asignado a ESTE lead.
      console.log(`📨 Enviando Template #2 (${miniTemplateName})...`);
      const miniParams = { 1: formData.nombre };
      if (withPresentation) miniParams.flow_token = `pres#${contactId}`;
      const payload2 = {
        phone: fullPhone,
        template_id: tplMini2?.template_id || tplMini2?.id || miniTemplateName,
        templateData: tplMini2 || undefined,
        params: miniParams,
        templateExtras: { header_url: miniatureUrl },
        // El botón "Simulador de cita"/"de pedido" lleva el mismo texto para todos los
        // sectores: sin payload, c_processOnboarding abría siempre el de barbería.
        quick_reply_payload: simulatorPayloadForSector(resolvedSector),
        isPrivate: false,
      };

      await axiosClient.post("/send-template", payload2);
      console.log("✅ Template #2 enviado");

      await finalizeFlowSend({
        resolvedSector,
        notes: `Flow enviado (${headTemplateName} + ${miniTemplateName}${withPresentation ? " +presentación" : ""} sector=${resolvedSector})`,
        summary: `✅ Flow enviado correctamente\n\n• Sector: ${SECTOR_LABELS[resolvedSector] || resolvedSector}\n• Template #1 (${headTemplateName}): Nombre del lead\n• Template #2 (${miniTemplateName}): Con miniatura personalizada${withPresentation ? "\n• Incluye botón 'Solicitar presentación'" : ""}`,
      });
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message;
      console.error("❌ Error en handleSendFlow:", errorMsg);
      setFlowError(errorMsg);
      alert(`❌ Error: ${errorMsg}`);
    } finally {
      setSendingFlow(false);
    }
  };

  const handleSendTarjeta = async (overridePhone = null) => {
    if (!contactId) {
      alert("❌ El lead debe estar guardado antes de enviar la Tarjeta");
      return;
    }

    const fullPhone = overridePhone || (
      formData.phone_number.startsWith("+")
        ? formData.phone_number
        : `${formData.prefix}${formData.phone_number}`
    );

    if (!isSendablePhone(fullPhone)) {
      alert("❌ Este lead no tiene un número de teléfono válido. Rellena el teléfono principal en la ficha antes de enviar.");
      return;
    }
    setSendingTarjeta(true);
    setTarjetaError(null);
    try {
      // 1. Cargar plantillas y buscar tarjeta_visita
      const plantillasRes = await axiosClient.get("/plantillas");
      const allTemplates =
        plantillasRes?.data || plantillasRes?.plantillas || plantillasRes || [];
      const tplTarjeta = Array.isArray(allTemplates)
        ? allTemplates.find((t) => t.name === "tarjeta_visita_v3") ||
          allTemplates.find((t) => t.name === "tarjeta_visita_v2") ||
          allTemplates.find((t) => t.name === "tarjeta_visita")
        : null;

      // 2. Enviar template con imagen y variables personalizadas
      const token = localStorage.getItem("token");
      const me = token ? jwtDecode(token) : null;
      const TARJETA_URL = getTarjetaUrl(me?.user_id);
      const _rawSender = ((me?.full_name || me?.user_id || "ConextCRM").split("@")[0] || "ConextCRM");
      const senderName = _rawSender.charAt(0).toUpperCase() + _rawSender.slice(1);
      const recipientName = formData.nombre || formData.persona_contacto || "";

      // {{3}} = WhatsApp directo del comercial (el de su tarjeta/QR). Si su
      // usuario no tiene número mapeado, NO enviamos: la plantilla quedaría sin
      // el dato clave (a qué número escribir) o con uno equivocado.
      const telefonoComercial = getTelefonoComercial(me?.user_id);
      if (!telefonoComercial) {
        setSendingTarjeta(false);
        alert(
          "⚠️ Tu usuario no tiene un número de WhatsApp configurado, así que no se ha enviado la tarjeta. Pídele al administrador que añada tu número en la configuración de tarjetas.",
        );
        return;
      }

      await axiosClient.post("/send-template", {
        phone: fullPhone,
        template_id:
          tplTarjeta?.template_id || tplTarjeta?.id || "tarjeta_visita_v3",
        templateData: tplTarjeta || undefined,
        params: { 1: recipientName, 2: senderName, 3: telefonoComercial },
        templateExtras: { header_url: TARJETA_URL },
        isPrivate: false,
      });

      // 3. Marcar como contactado
      await axiosClient.post("/sales/mark-contacted", {
        contact_id: contactId,
        type: "tarjeta",
        notes: "Tarjeta de visita enviada",
      });

      // 4. Persistir en el lead quién envió la tarjeta y cuándo (chip + historial).
      //    Mantenemos los campos legacy (último envío, usados al limpiar por cambio
      //    de número) y añadimos la entrada al array `tarjeta_sends`.
      const senderUserId = me?.user_id || me?.username || "";
      const nowIso = new Date().toISOString();
      const newSend = { at: nowIso, by_user_id: senderUserId, by_name: senderName };
      const updatedSends = [...tarjetaSendsList, newSend];
      axiosClient.post("/ventas/bulk-contacts", {
        contact_id: contactId,
        tarjeta_sent_at: nowIso,
        tarjeta_sent_by_user_id: senderUserId,
        tarjeta_sent_by_name: senderName,
        tarjeta_sends: updatedSends,
      }).catch(() => {});
      setTarjetaSessionSends((prev) => [...prev, newSend]);

      alert("✅ Tarjeta enviada correctamente");
      onSaved?.();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message;
      setTarjetaError(errorMsg);
      alert(`❌ Error: ${errorMsg}`);
    } finally {
      setSendingTarjeta(false);
    }
  };

  /**
   * handleSendAccesosWhatsApp() - Reenvía por WhatsApp las credenciales de un
   * lead cuyos accesos YA están creados (desde el modal "Accesos del cliente").
   * Usa la misma plantilla `acceso_plataforma` y los mismos parámetros que el
   * envío que se hace al crearlos en SectorPickerModal. Tras enviar, persiste
   * `accesos_sent_at` y marca el contacto como "Accesos enviados".
   */
  const handleSendAccesosWhatsApp = async () => {
    if (!credentialsUsername || !credentialsPassword) {
      alert("❌ No hay credenciales disponibles para enviar.");
      return;
    }

    const fullPhone = formData.phone_number.startsWith("+")
      ? formData.phone_number
      : `${formData.prefix}${formData.phone_number}`;

    if (!isSendablePhone(fullPhone)) {
      alert("❌ Este lead no tiene un número de teléfono válido. Rellena el teléfono principal en la ficha antes de enviar.");
      return;
    }

    setSendingAccesosWA(true);
    try {
      // Cargar plantillas y localizar acceso_plataforma (igual que SectorPickerModal).
      const plantillasRes = await axiosClient.get("/plantillas");
      const tpl = findPlantillaByName(plantillasRes, "acceso_plataforma");

      // {{1}} = "usuario  Contraseña: password", {{2}} = URL de la app.
      await axiosClient.post("/send-template", {
        phone: fullPhone,
        template_id: tpl?.template_id || tpl?.id || "acceso_plataforma",
        templateData: tpl || undefined,
        params: {
          "1": `${credentialsUsername}  Contraseña: ${credentialsPassword}`,
          "2": "app.conextcrm.es",
        },
      });

      // Reflejar el envío: chip "Enviados" + persistencia en el lead.
      const nowIso = new Date().toISOString();
      setAccesosSentInSession(true);
      if (contactId) {
        axiosClient
          .post("/sales/mark-contacted", {
            contact_id: contactId,
            type: "accesos",
            notes: "Accesos enviados",
          })
          .catch((err) => console.warn("mark-contacted accesos failed:", err));
        axiosClient
          .post("/ventas/bulk-contacts", {
            contact_id: contactId,
            accesos_sent_at: nowIso,
          })
          .catch((err) =>
            console.warn("bulk-contacts persist accesos_sent failed:", err),
          );
      }

      alert(`✅ Accesos enviados por WhatsApp a ${fullPhone}`);
      onSaved?.();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message;
      alert(`❌ Error al enviar los accesos: ${errorMsg}`);
    } finally {
      setSendingAccesosWA(false);
    }
  };

  /**
   * handleSendAccesosEmail() - Envía por email las credenciales de un lead cuyos
   * accesos YA están creados. Las credenciales las resuelve el backend
   * (v_sendAccesosEmail) a partir del `trial_company_id`, así que aquí solo
   * mandamos a quién y de parte de qué lead.
   *
   * `emailOverride` llega del input inline del modal cuando la ficha no tiene email;
   * en ese caso también se persiste en el lead para no volver a pedirlo.
   */
  const handleSendAccesosEmail = async (emailOverride = null) => {
    const trialCompanyId =
      sessionCredentials?.company_id || initialData?.trial_company_id || null;
    if (!trialCompanyId) {
      alert("❌ Este lead todavía no tiene una cuenta de prueba creada.");
      return false;
    }

    const targetEmail = (emailOverride || formData.email || "").trim();
    if (!isValidEmail(targetEmail)) {
      alert("❌ Escribe un email válido para enviarle las credenciales.");
      return false;
    }

    setSendingAccesosEmail(true);
    try {
      await axiosClient.post(
        "/ventas/accesos-email",
        buildAccesosEmailPayload({
          companyId: trialCompanyId,
          email: targetEmail,
          contactId,
          nombre: formData.nombre,
        }),
      );

      // Si el email es nuevo, que quede en la ficha (y en el formulario abierto).
      if (targetEmail !== (formData.email || "").trim()) {
        setFormData((prev) => ({ ...prev, email: targetEmail }));
        if (contactId) {
          axiosClient
            .post("/ventas/bulk-contacts", { contact_id: contactId, email: targetEmail })
            .catch((err) => console.warn("bulk-contacts persist email failed:", err));
        }
      }

      setAccesosEmailSentAt(new Date().toISOString());
      alert(`✅ Accesos enviados por email a ${targetEmail}`);
      onSaved?.();
      return true;
    } catch (err) {
      alert(`❌ Error al enviar los accesos por email: ${accesosEmailErrorMessage(err)}`);
      return false;
    } finally {
      setSendingAccesosEmail(false);
    }
  };

  /**
   * handleSendComparison() - Envía una imagen comparativa ConextCRM vs un competidor
   * del sector del lead, vía la plantilla "comparativa_competencia_v2".
   *  1. Resuelve sector → si no es mapeable abre el selector de sector.
   *  2. Sin competidor → abre el selector de competidor.
   *  3. Construye las filas (features) desde competitionConfig y pide la imagen al backend.
   *  4. Envía la plantilla con la imagen como header y el nombre del competidor como {{1}}.
   */
  const handleSendComparison = async (
    overridePhone = null,
    sectorOverride = null,
    competitorId = null,
  ) => {
    if (!contactId) {
      alert("❌ El lead debe estar guardado antes de enviar la comparativa");
      return;
    }

    const fullPhone = overridePhone || (
      formData.phone_number.startsWith("+")
        ? formData.phone_number
        : `${formData.prefix}${formData.phone_number}`
    );

    if (!isSendablePhone(fullPhone)) {
      alert("❌ Este lead no tiene un número de teléfono válido. Rellena el teléfono principal en la ficha antes de enviar.");
      return;
    }

    // Resolver clave del sector → COMPETITION_CONFIG.
    // `rawSector` puede ser el sector del lead (mapear vía SECTOR_TO_COMPETITION_KEY)
    // o ya una clave de COMPETITION_CONFIG (cuando viene del selector de sector/competidor).
    const rawSector = sectorOverride || formData.sector;
    const key = COMPETITION_CONFIG[rawSector]
      ? rawSector
      : SECTOR_TO_COMPETITION_KEY[rawSector];
    if (!key || !COMPETITION_CONFIG[key]) {
      setComparisonSectorPicker({ open: true, phone: fullPhone });
      return;
    }

    // Sin competidor → abrir selector de competidor.
    if (!competitorId) {
      setComparisonCompetitorPicker({ open: true, phone: fullPhone, sectorKey: key });
      return;
    }

    const config = COMPETITION_CONFIG[key];
    const comp = config.competitors.find((c) => c.id === competitorId);
    if (!comp) {
      alert("❌ Competidor no válido");
      return;
    }

    const features = config.features.map((f) => ({
      label: f.label,
      conext: config.data?.conext?.[f.id] || "—",
      competitor: config.data?.[competitorId]?.[f.id] || "—",
    }));

    setSendingComparison(true);
    setComparisonError(null);

    try {
      // 1. Generar la imagen comparativa en backend
      const genRes = await axiosClient.post("/ventas/generate-comparison", {
        sector_label: config.label,
        competitor_name: comp.name,
        features,
      });

      const url = genRes?.url;
      if (!url) {
        throw new Error("No se generó la imagen comparativa correctamente");
      }

      // 2. Cargar plantillas y buscar "comparativa_competencia_v2"
      const plantillasRes = await axiosClient.get("/plantillas");
      const allTemplates =
        plantillasRes?.data || plantillasRes?.plantillas || plantillasRes || [];
      const tpl = Array.isArray(allTemplates)
        ? allTemplates.find((t) => t.name === "comparativa_competencia_v2")
        : null;

      // 3. Enviar template con la imagen como header y el competidor como {{1}}
      await axiosClient.post("/send-template", {
        phone: fullPhone,
        template_id: tpl?.template_id || tpl?.id || "comparativa_competencia_v2",
        templateData: tpl || undefined,
        params: { 1: comp.name },
        templateExtras: { header_url: url },
        isPrivate: false,
      });

      // 4. Marcar como contactado
      await axiosClient.post("/sales/mark-contacted", {
        contact_id: contactId,
        type: "whatsapp",
        notes: `Comparativa enviada (${config.label} vs ${comp.name})`,
      });

      alert(`✅ Comparativa enviada correctamente\n\n• Sector: ${config.label}\n• Competidor: ${comp.name}`);
      onSaved?.();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message;
      setComparisonError(errorMsg);
      alert(`❌ Error: ${errorMsg}`);
    } finally {
      setSendingComparison(false);
    }
  };

  // Añade al usuario actual a list_access para una lista recién creada (solo admins)
  const addSelfToListAccess = async (listName) => {
    try {
      const token = localStorage.getItem("token");
      const me = token ? jwtDecode(token) : null;
      if (!me?.user_id) return;
      // Solo el admin (level 0) puede escribir en /ventas/settings/access
      const myLevel = me?.level !== undefined ? Number(me.level) : 99;
      if (myLevel !== 0) return;
      const config = await axiosClient.get("/ventas/settings/access");
      const listAccess = { ...(config?.list_access || {}) };
      const existing = Array.isArray(listAccess[listName]) ? listAccess[listName] : [];
      if (!existing.includes(me.user_id)) {
        listAccess[listName] = [...existing, me.user_id];
        await axiosClient.post("/ventas/settings/access", { list_access: listAccess });
      }
    } catch (_) {}
  };

  const toggleGroup = (group) => {
    setFormData((prev) => {
      const isAdding = !prev.grupos.includes(group);
      let nextGroups;
      if (isAdding) {
        const base = group === "general"
          ? prev.grupos
          : prev.grupos.filter((g) => g !== "general");
        nextGroups = [...base, group];
      } else {
        nextGroups = prev.grupos.filter((g) => g !== group);
        if (nextGroups.length === 0) nextGroups = ["general"];
      }
      return { ...prev, grupos: nextGroups };
    });
  };

  const handleAcceptSuggestion = async () => {
    const { listName, status } = cpSuggestion;
    if (!listName || status === "accepted") return;
    try {
      await axiosClient.post("/sales/create-list", { list_name: listName });
      addSelfToListAccess(listName);
      setFormData((prev) => ({
        ...prev,
        grupos: [...new Set([...prev.grupos.filter((g) => g !== "general"), listName])],
      }));
      setAvailableGroups((prev) => {
        const exists = prev.some(
          (g) => g.toLowerCase() === listName.toLowerCase()
        );
        return exists ? prev : [...prev, listName];
      });
      setCpSuggestion((prev) => ({ ...prev, status: "accepted" }));
    } catch (err) {
      setCreateListError(
        err?.response?.data?.error ||
          err?.message ||
          "Error al crear la lista"
      );
      setTimeout(() => setCreateListError(""), 3000);
    }
  };

  /**
   * Serializa los campos editables de un formData a una cadena canónica para
   * comparar igualdad (arrays ordenados, fechas a epoch, numéricos a string).
   * Se usa como baseline tras un guardado correcto.
   */
  const serializeFormForCompare = (fd) =>
    JSON.stringify({
      nombre: fd.nombre || "",
      persona_contacto: fd.persona_contacto || "",
      email: fd.email || "",
      phone_number: fd.phone_number || "",
      prefix: fd.prefix || "+34",
      grupos: [...(fd.grupos || [])].sort(),
      interest: fd.interest || "",
      competitor: fd.competitor || "",
      customCompetitor: fd.customCompetitor || "",
      competitor_price:
        fd.competitor_price != null ? String(fd.competitor_price) : "",
      notes: fd.notes || "",
      callReminderDate: fd.callReminderDate ? fd.callReminderDate.getTime() : null,
      categoria: fd.categoria || "",
      direccion: fd.direccion || "",
      telefono_fijo: fd.telefono_fijo || "",
      telefono_movil: fd.telefono_movil || "",
      prefix_movil: fd.prefix_movil || "+34",
      sitio_web: fd.sitio_web || "",
      instagram: fd.instagram || "",
      calificacion: fd.calificacion != null ? String(fd.calificacion) : "",
      num_resenas: fd.num_resenas != null ? String(fd.num_resenas) : "",
      closing_days: [...(fd.closing_days || [])].sort(),
      tiene_facebook: !!fd.tiene_facebook,
      sector: fd.sector || "",
      autoescuela_plan: fd.autoescuela_plan || "",
      tiene_whatsapp_business: !!fd.tiene_whatsapp_business,
      logo_url: fd.logo_url || "",
      logo_s3_key: fd.logo_s3_key || "",
      codigo_postal: fd.codigo_postal || "",
      persona_contacto_id: fd.persona_contacto_id || "",
    });

  /** Detecta si hay cambios sin guardar comparando formData con initialData */
  const hasUnsavedChanges = () => {
    // Tras un guardado correcto comparamos contra el snapshot guardado (el padre
    // no refresca `initialData`), no contra los datos originales.
    if (savedSnapshot !== null) {
      return serializeFormForCompare(formData) !== savedSnapshot;
    }
    const safe = initialData || {};
    const rawGroups = safe.grupo || safe.grupos;
    const initGroups = Array.isArray(rawGroups)
      ? rawGroups
      : rawGroups
        ? [rawGroups]
        : ["general"];
    const std = [
      "Bookly",
      "Booksy",
      "Treatwell",
      "Calendly",
      "Fresha",
      "Square Appointments",
      "GoBarber",
      "Neverlate",
      "Peluvis",
      "AgendaPro",
      "",
    ];

    const isCustom = safe.competitor && !std.includes(safe.competitor);

    // Lógica para detectar prefijo y teléfono base desde el safeData (initialData)
    // Debe coincidir con la lógica del useEffect inicial
    let initDisplayPhone = safe.phone_number || "";
    let initPrefix = "+34";
    if (initDisplayPhone.startsWith("+")) {
      const matched = countries
        .slice()
        .sort((a, b) => b.dial_code.length - a.dial_code.length)
        .find((c) => initDisplayPhone.startsWith(c.dial_code));
      if (matched) {
        initPrefix = matched.dial_code;
        initDisplayPhone = initDisplayPhone.slice(matched.dial_code.length);
      }
    }

    const initCompetitor = isCustom ? "Otros" : safe.competitor || "";
    const initCustom = isCustom ? safe.competitor : "";
    const finalComp =
      formData.competitor === "Otros"
        ? formData.customCompetitor
        : formData.competitor;
    const initCall = safe.call_reminder_date
      ? new Date(safe.call_reminder_date).getTime()
      : null;
    const currCall = formData.callReminderDate
      ? formData.callReminderDate.getTime()
      : null;

    const res =
      formData.nombre !== (safe.nombre || "") ||
      formData.persona_contacto !== (safe.persona_contacto || "") ||
      formData.email !== (safe.email || "") ||
      formData.phone_number !== (initDisplayPhone || "") ||
      formData.prefix !== (initPrefix || "+34") ||
      JSON.stringify([...formData.grupos].sort()) !==
        JSON.stringify([...initGroups].sort()) ||
      formData.interest !== (safe.interest || "") ||
      formData.competitor !== initCompetitor ||
      formData.customCompetitor !== (initCustom || "") ||
      (formData.notes || "") !== (safe.notes || safe.notas || "") ||
      Math.abs((initCall || 0) - (currCall || 0)) > 1000 || // Tolerancia 1s para fechas
      formData.categoria !== (safe.categoria || "") ||
      formData.direccion !== (safe.direccion || "") ||
      formData.telefono_fijo !== (safe.telefono_fijo || "") ||
      formData.telefono_movil !== (safe.telefono_movil || "") ||
      formData.prefix_movil !== (safe.prefix_movil || "+34") ||
      formData.sitio_web !== (safe.sitio_web || "") ||
      formData.instagram !== (safe.instagram || "") ||
      formData.calificacion !==
        (safe.calificacion ? String(safe.calificacion) : "") ||
      formData.num_resenas !==
        (safe.num_resenas ? String(safe.num_resenas) : "") ||
      JSON.stringify([...formData.closing_days].sort()) !==
        JSON.stringify([...(safe.closing_days || [])].sort()) ||
      formData.tiene_facebook !== !!safe.tiene_facebook ||
      formData.sector !== (safe.sector || "") ||
      (formData.autoescuela_plan || "") !== (safe.autoescuela_plan === "profesor" ? "profesor" : "") ||
      formData.tiene_whatsapp_business !== !!safe.tiene_whatsapp_business ||
      formData.logo_url !== (safe.logo_url || "") ||
      formData.logo_s3_key !== (safe.logo_s3_key || "") ||
      formData.codigo_postal !== (safe.codigo_postal || "") ||
      formData.persona_contacto_id !== (safe.persona_contacto_id || "") ||
      formData.competitor_price !== (safe.competitor_price ? String(safe.competitor_price) : "");

    if (res) {
      console.group("🔍 LeadEditModal: Cambios Detectados");
      if (formData.nombre !== (safe.nombre || ""))
        console.log(
          "nombre:",
          `'${formData.nombre}'`,
          "vs",
          `'${safe.nombre}'`,
        );
      if (formData.phone_number !== (initDisplayPhone || ""))
        console.log(
          "phone_number:",
          `'${formData.phone_number}'`,
          "vs",
          `'${initDisplayPhone}'`,
        );
      if (formData.prefix !== (initPrefix || "+34"))
        console.log("prefix:", `'${formData.prefix}'`, "vs", `'${initPrefix}'`);
      if (
        formData.calificacion !==
        (safe.calificacion ? String(safe.calificacion) : "")
      )
        console.log(
          "calificacion:",
          `'${formData.calificacion}'`,
          "vs",
          `'${safe.calificacion}'`,
        );
      if (
        formData.num_resenas !==
        (safe.num_resenas ? String(safe.num_resenas) : "")
      )
        console.log(
          "num_resenas:",
          `'${formData.num_resenas}'`,
          "vs",
          `'${safe.num_resenas}'`,
        );
      console.groupEnd();
    }

    return res;
  };

  const formatSectorLabel = (s) => {
    if (!s) return s;
    return s
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError("");

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setLogoError("Solo se permiten imágenes JPG, PNG o WEBP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError("El logo no puede superar 2MB.");
      return;
    }

    setUploadingLogo(true);
    try {
      const uploadResult = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (ev) => {
          try {
            // Strip "data:image/jpeg;base64," prefix to get raw base64
            const base64 = ev.target.result.split(",")[1];
            const res = await axiosClient.post("/upload-file", {
              fileName: file.name,
              fileData: base64,
              contentType: file.type,
            });
            if (!res?.fileUrl || !res?.s3Key) {
              throw new Error("Respuesta de subida incompleta (fileUrl/s3Key)");
            }
            resolve({ fileUrl: res.fileUrl, s3Key: res.s3Key });
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error("Error leyendo el archivo"));
        reader.readAsDataURL(file);
      });

      setFormData((prev) => ({
        ...prev,
        logo_url: uploadResult.fileUrl,
        logo_s3_key: uploadResult.s3Key,
      }));
    } catch (err) {
      console.error("Error subiendo logo:", err);
      setLogoError("Error al subir el logo. Inténtalo de nuevo.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCloseClick = () => {
    // Si ya se recibió un 409, el lead no se puede guardar — cerrar directamente
    if (accessDeniedData !== null) {
      onClose();
      return;
    }
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  const handleEnterSimulator = async () => {
    // Los leads de la landing guardan la etiqueta del formulario ("Estética /
    // Spa"); el simulador indexa por clave ("estetica") — sin normalizar se
    // pierden features (p.ej. el botón "Simular cita").
    // Academia (3/11/2026): el simulador de autoescuela sin vehículos y con «clases» (modoAcademiaSim).
    const leadAcademia = esSectorAcademia(formData.sector);
    const sector = leadAcademia ? "autoescuela" : canonicalSimulatorSector(formData.sector);
    if (!sector) return;

    // flushSync fuerza el re-render del spinner antes de las llamadas async
    flushSync(() => setEnteringSimulator(true));
    try {
      // Auto-save unsaved changes before entering simulator
      if (hasUnsavedChanges()) {
        try {
          const {
            prefix, phone_number, competitor, customCompetitor,
            callReminderDate, notes, grupos, v_assets_ready, contact_type,
            ...rest
          } = formData;
          const finalCompetitor = competitor === "Otros" ? customCompetitor : competitor;
          const finalPhone = phone_number
            ? phone_number.startsWith("+") ? phone_number : `${prefix}${phone_number}`
            : undefined;
          const initialRawGroupsAS = initialData?.grupo || initialData?.grupos;
          const initialGrpsAS = Array.isArray(initialRawGroupsAS)
            ? initialRawGroupsAS
            : initialRawGroupsAS
              ? [initialRawGroupsAS]
              : [];
          const currentGrpsAS = Array.isArray(grupos) ? grupos : [];
          const sameGroupsAS =
            initialGrpsAS.length === currentGrpsAS.length &&
            currentGrpsAS.every((g) => initialGrpsAS.includes(g));
          const sanitizedGrpsAS =
            currentGrpsAS.length > 1
              ? currentGrpsAS.filter((g) => g !== "general")
              : currentGrpsAS;
          const savePayload = {
            ...rest,
            competitor: finalCompetitor,
            phone_number: finalPhone,
            call_reminder_date: callReminderDate ? callReminderDate.toISOString() : null,
            notes,
          };
          if (!sameGroupsAS || sanitizedGrpsAS.length !== currentGrpsAS.length) {
            savePayload.grupo = sanitizedGrpsAS;
          }
          const initialLogoKey = initialData?.logo_s3_key || "";
          if ((formData.logo_s3_key || "") === initialLogoKey) delete savePayload.logo_s3_key;
          if (contactId) savePayload.contact_id = contactId;
          await axiosClient.post("/ventas/bulk-contacts", savePayload);
          // No llamar onSaved aquí: cerraría el modal antes de que el simulador esté activo
        } catch (err) {
          console.warn("[LeadEditModal] Auto-save before simulator failed:", err?.message);
        }
      }
      let services = [];

      // En sectores de reserva de citas con un lead identificado, los servicios
      // viven en la tabla por-lead (`c_lead_demo_carta` con `kind=service`).
      // Empiezan vacíos la primera vez; el comercial los añade desde la página
      // de Configuración dentro del simulador y se persisten por contact_id.
      const isLeadAppointment = isAppointmentSector(sector) && !!contactId;

      if (isLeadAppointment) {
        try {
          const res = await axiosClient.get("/ventas/lead-carta", {
            params: { contact_id: contactId, kind: "service" },
          });
          const items = Array.isArray(res?.items) ? res.items : [];
          services = items.map((s) => ({
            service_id: s.item_id || s.service_id,
            name: s.name || "",
            duration: s.duration || 30,
            total_duration: s.duration || 30,
            gender: s.gender || "unisex",
            price: Number(s.price) || 0,
            color: s.color || "#3B82F6",
            description: s.description || "",
          }));
        } catch (err) {
          console.warn("[LeadEditModal] Error cargando servicios del lead:", err);
          services = [];
        }
      } else {
        try {
          const res = await axiosClient.get(`/admin/sector-config/${sector}`, {
            params: { language: "es" },
          });
          let raw = res?.default_services;
          if (raw?.es) raw = raw.es;
          else if (raw?.ES) raw = raw.ES;

          if (Array.isArray(raw)) {
            services = raw.map((s) => ({
              service_id: s.service_id || `sc-${s.nombre || s.name || Math.random()}`,
              name: s.nombre || s.name || "",
              duration: s.duracion_minutos || s.duration || 30,
              total_duration: s.duracion_minutos || s.duration || 30,
              gender: s.gender || "unisex",
              price: s.price || 0,
              color: s.color || "#3B82F6",
            }));
          } else if (raw && typeof raw === "object") {
            services = [
              ...(raw.hombre || []).map((s) => ({ ...s, _bucket: "hombre" })),
              ...(raw.mujer || []).map((s) => ({ ...s, _bucket: "mujer" })),
              ...(raw.unisex || []).map((s) => ({ ...s, _bucket: "unisex" })),
            ].map((s) => ({
              service_id: s.service_id || `sc-${s.nombre || s.name}`,
              name: s.nombre || s.name || "",
              duration: s.duracion_minutos || s.duration || 30,
              total_duration: s.duracion_minutos || s.duration || 30,
              gender: s.gender || s._bucket || "unisex",
            }));
          }
        } catch {
          services = [];
        }
      }

      let workers = [];
      try {
        const res = await axiosClient.get("/peluqueria/workers");
        if (Array.isArray(res) && res.length > 0) {
          workers = res.map((w) => {
            const rawName = w.name || w.username || w.displayName || "";
            const cleanName = rawName.includes("@")
              ? rawName.split("@")[0]
              : rawName;
            const formattedName =
              cleanName.charAt(0).toUpperCase() + cleanName.slice(1).toLowerCase();
            return {
              ...w,
              name: formattedName,
              displayName: formattedName,
              worker_id: w.worker_id || w.user_id,
            };
          });
        }
      } catch {
        workers = [];
      }

      // «Profesor de autoescuela»: el simulador de autoescuela en modo Plan Profesor (la misma tarjeta
      // «Profesor (Plan Profesor)» del selector del simulador: un profesor y las secciones que oculta el alta).
      const leadProfesor = esLeadProfesor(formData);
      if (sector === "autoescuela") entrarModoProfesorSim(leadProfesor);
      // Después del modo profesor, que deja las secciones a su gusto: el de academia oculta las suyas.
      if (leadAcademia || esModoAcademia()) entrarModoAcademiaSim(leadAcademia);
      const sectorLabel = leadAcademia ? "Academia" : leadProfesor ? ETIQUETA_PROFESOR : formatSectorLabel(sector);
      const fullPhone = formData.phone_number
        ? formData.phone_number.startsWith("+")
          ? formData.phone_number
          : `${formData.prefix}${formData.phone_number}`
        : null;

      // Store return context so Layout can reopen this modal after simulator exits
      setReturnLead({ contactId: contactId || null, initialData: { ...(initialData || {}), ...formData } });
      activate();
      const personaPhone = (() => {
        const raw = (formData.telefono_movil || "").trim();
        if (!raw) return null;
        return raw.startsWith("+") ? raw : `${formData.prefix_movil}${raw}`;
      })();

      setSector(
        sector,
        sectorLabel,
        services,
        workers,
        formData.logo_url || null,
        contactId || null,
        formData.nombre || null,
        fullPhone,
        formData.persona_contacto || null,
        personaPhone,
      );
      onClose();
    } finally {
      setEnteringSimulator(false);
    }
  };

  const fullPhoneMovilDisplay = (() => {
    const raw = (formData.telefono_movil || "").trim();
    if (!raw) return null;
    return raw.startsWith("+") ? raw : `${formData.prefix_movil}${raw}`;
  })();

  const mainPhoneDisplay = formData.phone_number
    ? formData.phone_number.startsWith("+")
      ? formData.phone_number
      : `${formData.prefix}${formData.phone_number}`
    : null;

  const hasPersonaWithPhone = !!(formData.persona_contacto && fullPhoneMovilDisplay);

  const openSendAction = (actionName) => {
    if (hasPersonaWithPhone) {
      setRecipientPicker({ open: true, pendingAction: actionName });
    } else {
      executeSendAction(actionName, mainPhoneDisplay);
    }
  };

  const executeSendAction = async (actionName, phone) => {
    // Si el destinatario es la persona de contacto, garantizar su contacto
    // nombrado "Persona (Negocio)" y enlazado, aunque no se vuelva a guardar.
    if (
      contactId &&
      formData.persona_contacto &&
      fullPhoneMovilDisplay &&
      phone === fullPhoneMovilDisplay
    ) {
      const resolved = await ensurePersonaContacto({
        personaPhone: fullPhoneMovilDisplay,
        leadPhone: mainPhoneDisplay,
        parentLeadId: contactId,
      });
      if (resolved && resolved !== (formData.persona_contacto_id || null)) {
        setFormData((prev) => ({ ...prev, persona_contacto_id: resolved }));
      }
    }

    // Si el destinatario es el número PRINCIPAL y la acción envía WhatsApp,
    // asegurar que el lead posee ese número (evita el contacto duplicado).
    if (
      contactId &&
      mainPhoneDisplay &&
      phone === mainPhoneDisplay &&
      (actionName === "tarjeta" ||
        actionName === "flow" ||
        actionName === "flow_presentacion" ||
        actionName === "flow_televenta" ||
        actionName === "flow_marketing" ||
        actionName === "comparison")
    ) {
      const canContinue = await ensureLeadOwnsMainPhone({
        mainPhone: mainPhoneDisplay,
      });
      if (!canContinue) return;
    }

    // El sector lo fija SendFlowModal (el del lead, o el elegido allí si no tenía).
    const flowSector = flowSectorOverrideRef.current;
    if (actionName === "tarjeta") handleSendTarjeta(phone);
    else if (actionName === "flow") handleSendFlow(phone, flowSector);
    else if (actionName === "flow_presentacion") handleSendFlow(phone, flowSector, true);
    // Flow televenta: siempre con presentación + online + plantilla única con vídeo, sin
    // depender del checkbox "Lead de televenta" de la ficha.
    else if (actionName === "flow_televenta")
      handleSendFlow(phone, flowSector, true, true, true);
    else if (actionName === "flow_marketing")
      handleSendFlow(phone, flowSector, false, true, false, true);
    else if (actionName === "comparison") handleSendComparison(phone);
    else if (actionName === "accesos") {
      setAccesosPhone(phone);
      setAccesosModalOpen(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-2 md:p-4">
      <div className="bg-white/95 rounded-[1.5rem] md:rounded-[2.5rem] shadow-2xl w-full max-w-[95vw] sm:max-w-[1450px] max-h-[95vh] md:max-h-[92vh] overflow-hidden flex flex-col border border-red-50/50">
        {/* Header Premium */}
        <div className="px-4 md:px-6 py-2.5 border-b border-red-100/30 bg-gradient-to-br from-red-50/40 via-white to-emerald-50/20">
          {/* Fila 1: título + acciones (siempre en una línea) */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
            <h2 className="text-lg md:text-xl font-black text-slate-900 tracking-tight truncate min-w-0 shrink">
              {contactId ? (formData.nombre || "Lead") : "Nuevo Lead"}
            </h2>
            {isDavid &&
              (sessionCredentials?.company_id ||
                initialData?.trial_company_id) && (
                <button
                  type="button"
                  onClick={() => {
                    const cid =
                      sessionCredentials?.company_id ||
                      initialData?.trial_company_id;
                    if (!cid) return;
                    navigator.clipboard?.writeText(cid);
                    setCompanyIdCopied(true);
                    setTimeout(() => setCompanyIdCopied(false), 1500);
                  }}
                  title="Copiar company_id de la empresa trial"
                  className="inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 text-[11px] font-mono font-semibold text-slate-600 transition-colors max-w-[240px]"
                >
                  <span className="truncate">
                    {sessionCredentials?.company_id ||
                      initialData?.trial_company_id}
                  </span>
                  {companyIdCopied ? (
                    <span className="text-emerald-600 font-sans font-bold">
                      ✓
                    </span>
                  ) : (
                    <Copy size={12} className="shrink-0" />
                  )}
                </button>
              )}
            </div>
            {/* Acciones derechas */}
            <div className="flex items-center gap-1.5 shrink-0">
              {contactId && !location.pathname.startsWith("/chat") && (
                <button
                  type="button"
                  onClick={() => goToChat(contactId)}
                  className="text-xs font-bold text-emerald-600 hover:text-emerald-800 flex items-center gap-1 whitespace-nowrap px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  <FaWhatsapp className="text-emerald-500" size={13} />
                  <span className="hidden sm:inline">IR AL CHAT</span>
                </button>
              )}
              {/* Menú desplegable de accesos rápidos */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowMoreLinks((v) => !v)}
                  className="px-2.5 py-1 rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-100 text-sm font-bold transition-all leading-none"
                  title="Más opciones"
                >
                  ···
                </button>
                {showMoreLinks && (
                  <div
                    className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-2xl shadow-xl py-1.5 min-w-[180px] flex flex-col"
                    onMouseLeave={() => setShowMoreLinks(false)}
                  >
                    {contactId && !location.pathname.startsWith("/chat") && (
                      <button
                        type="button"
                        onClick={() => { setShowMoreLinks(false); goToChat(contactId); }}
                        className="px-4 py-2 text-xs font-bold text-emerald-600 hover:bg-emerald-50 transition-colors text-left flex items-center gap-1.5 whitespace-nowrap sm:hidden"
                      >
                        <FaWhatsapp size={12} /> IR AL CHAT
                      </button>
                    )}
                    <a
                      href="https://conextcrm.es/#plan_section"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                      onClick={() => setShowMoreLinks(false)}
                    >
                      Precios
                    </a>
                    {contactId && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowMoreLinks(false);
                          onClose();
                          navigate("/soporte", {
                            state: {
                              openTicketForContact: { id: contactId, name: formData.nombre || "" },
                              fromLeadEdit: true,
                              returnTo: location.pathname,
                              contactToReopen: initialData || {},
                            },
                          });
                        }}
                        className="px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 transition-colors text-left flex items-center gap-1.5 whitespace-nowrap"
                      >
                        <LifeBuoy size={12} /> Abrir Ticket
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowMoreLinks(false);
                        navigate(`/documentos?contact_id=${encodeURIComponent(contactId)}`, {
                          state: { fromLeadEdit: true, returnTo: location.pathname, contactToReopen: initialData || {} },
                        });
                      }}
                      className="px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors text-left whitespace-nowrap"
                    >
                      Documentos
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMoreLinks(false);
                        onClose();
                        navigate("/tabla-competencia", {
                          state: { fromLeadEdit: true, sector: formData.sector || "", returnTo: location.pathname, contactToReopen: initialData || {} },
                        });
                      }}
                      className="px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors text-left whitespace-nowrap"
                    >
                      Tabla Competencia
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMoreLinks(false);
                        navigate("/plantillas", {
                          state: { fromLeadEdit: true, returnTo: location.pathname, contactToReopen: initialData || {} },
                        });
                      }}
                      className="px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors text-left whitespace-nowrap"
                    >
                      Biblioteca Flows
                    </button>
                    <a
                      href="https://docs.google.com/document/d/1W6TS6_FrDdwgpEwCMWFpW1nX4oBOUmU7/edit?usp=sharing&ouid=104428999502896894469&rtpof=true&sd=true"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                      onClick={() => setShowMoreLinks(false)}
                    >
                      Teatro de ventas
                    </a>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleCloseClick}
                className="p-1.5 hover:bg-slate-100 rounded-xl transition-all active:scale-90"
              >
                <FaTimes className="text-slate-400" size={17} />
              </button>
            </div>
          </div>

          {/* Fila 2: chips de sector / creado por / asignado — todos en una sola línea flex-wrap */}
          {contactId && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              {formData.sector && SECTOR_LABELS[formData.sector] && (
                <span className="bg-red-50 text-red-700 border border-red-200 rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap">
                  {esLeadProfesor(formData) ? ETIQUETA_PROFESOR : SECTOR_LABELS[formData.sector]}
                </span>
              )}
              {formData.created_by && (
                <span className="bg-violet-50 text-violet-600 border border-violet-100 rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap flex items-center gap-1">
                  <Users size={10} />
                  {(() => {
                    const raw = formData.created_by;
                    const base = raw?.includes("@") ? raw.split("@")[0] : raw;
                    return base ? base.charAt(0).toUpperCase() + base.slice(1) : null;
                  })()}
                </span>
              )}
              {(() => {
                const formatWorker = (uid) => {
                  if (!uid) return null;
                  const w = workers.find((x) => (x.user_id || x.username || x.id) === uid);
                  const raw = w?.full_name || w?.name || w?.username || uid;
                  const base = raw?.includes("@") ? raw.split("@")[0] : raw;
                  return base ? base.charAt(0).toUpperCase() + base.slice(1) : uid;
                };
                if (isAdmin) {
                  return (
                    <div
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 border text-xs font-bold whitespace-nowrap transition-colors ${
                        assignedTo
                          ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      } ${assignLoading ? "opacity-60" : ""}`}
                    >
                      <Users size={10} />
                      <select
                        disabled={assignLoading}
                        value={assignedTo || ""}
                        onChange={async (e) => {
                          const newUserId = e.target.value;
                          if (newUserId === assignedTo) return;
                          if (!contactId) return;
                          setAssignLoading(true);
                          const prev = assignedTo;
                          setAssignedTo(newUserId);
                          try {
                            await axiosClient.post(`/ventas/access-requests/assign`, {
                              contact_id: contactId,
                              user_id: newUserId || null,
                            });
                          } catch (err) {
                            console.error("Error reasignando lead:", err);
                            setAssignedTo(prev);
                            alert("No se pudo reasignar el lead: " + (err?.response?.data?.error || err?.message || "error desconocido"));
                          } finally {
                            setAssignLoading(false);
                          }
                        }}
                        className="bg-transparent outline-none border-0 text-xs font-bold cursor-pointer pr-1"
                      >
                        <option value="">— Sin asignar —</option>
                        {workers
                          .filter((w) => Number(w.level ?? 99) !== 0 || (w.user_id || w.username || w.id) === myJwt?.user_id)
                          .map((w) => {
                            const uid = w.user_id || w.username || w.id;
                            const isMe = uid === myJwt?.user_id;
                            return (
                              <option key={uid} value={uid}>
                                {formatWorker(uid)}{isMe ? " (yo)" : ""}
                              </option>
                            );
                          })}
                      </select>
                    </div>
                  );
                }
                if (assignedTo) {
                  return (
                    <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap flex items-center gap-1">
                      <Users size={10} />
                      {formatWorker(assignedTo)}
                    </span>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>

        {/* Form Content */}
        {/* overflow-x-hidden: red de seguridad. Si algo se pasa de ancho en el móvil,
            el formulario entero se desplazaba de lado y todo parecía salirse de los
            márgenes. Lo que se sale se arregla en su sitio (flex-wrap, min-w-0). */}
        <form onSubmit={handleSave} className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 lg:p-10">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 lg:items-stretch">
            {/* Sección 1: Información del Contacto */}
            {!isColumnExpanded("info") && (
              <CollapsedColumnRail
                icon={Globe}
                label="Información"
                iconClass="bg-red-50 text-red-600"
                onClick={() => expandColumn("info")}
              />
            )}
            <div className={`space-y-6 min-w-0 flex-1 ${!isColumnExpanded("info") ? "lg:hidden" : ""}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-red-50 text-red-600 rounded-2xl">
                  <Globe size={18} />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.15em]">
                  Información
                </h3>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  Nombre Completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300"
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <label className={`block text-xs font-black uppercase tracking-wider ${phoneError ? "text-red-500" : "text-slate-400"}`}>
                    Teléfono Principal <span className="text-red-500">*</span>
                    <span className="ml-1 normal-case font-semibold text-slate-400 tracking-normal">(preferiblemente con WhatsApp)</span>
                    {phoneError && <span className="ml-1 normal-case font-semibold text-red-500">— requerido</span>}
                  </label>
                  {contactId && !location.pathname.startsWith("/chat") && (
                    <button
                      type="button"
                      onClick={() => goToChat(contactId)}
                      className="text-[10px] font-bold text-emerald-500 hover:text-emerald-700 flex items-center gap-1 transition-colors"
                    >
                      <FaWhatsapp size={12} />
                      {t("common.ir_al_chat", "IR AL CHAT")}
                    </button>
                  )}
                </div>
                <div className="flex gap-2.5">
                  <select
                    className="w-[30%] min-w-0 px-3 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 text-sm font-semibold focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all"
                    value={formData.prefix}
                    onChange={(e) =>
                      setFormData({ ...formData, prefix: e.target.value })
                    }
                  >
                    {countries.map((c) => (
                      <option key={c.code} value={c.dial_code}>
                        {c.code} {c.dial_code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className={`flex-1 min-w-0 px-4 py-3.5 rounded-2xl border bg-slate-50/50 focus:bg-white focus:ring-4 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300 ${phoneError ? "border-red-400 focus:ring-red-500/10 focus:border-red-500" : "border-slate-100 focus:ring-red-500/10 focus:border-red-500"}`}
                    placeholder="612345678"
                    value={formData.phone_number}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Validación en vivo de longitud (solo si hay algo y no es E.164).
                      if (v.trim() && !v.trim().startsWith("+")) {
                        const c = validateNationalPhone(formData.prefix, v);
                        setPhoneError(!c.valid);
                        setPhoneErrorMsg(c.valid ? "" : c.message);
                      } else {
                        if (v.trim()) setPhoneError(false);
                        setPhoneErrorMsg("");
                      }
                      setFormData({ ...formData, phone_number: v });
                    }}
                  />
                </div>
                {phoneErrorMsg && (
                  <p className="mt-1.5 text-[11px] font-semibold text-red-500 flex items-center gap-1">
                    <FaExclamationCircle size={11} className="flex-shrink-0" />
                    {phoneErrorMsg}
                  </p>
                )}
              </div>

              {formData.wa_undeliverable_at && (
                <div className="flex items-start gap-2 -mt-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700">
                  <Ban size={14} className="flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] font-semibold leading-relaxed">
                    {t("chats.deliveryError.noWhatsapp")}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  Teléfono Fijo
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300"
                  placeholder="+34900000000"
                  value={formData.telefono_fijo}
                  onChange={(e) =>
                    setFormData({ ...formData, telefono_fijo: e.target.value })
                  }
                />
              </div>

              {/* Bloque Persona de Contacto */}
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                  Persona de Contacto
                </p>
                <div>
                  <div className="flex items-center justify-between mb-2.5">
                    <label className="block text-xs font-semibold text-slate-500">
                      Nombre
                    </label>
                    {formData.persona_contacto_id && !location.pathname.startsWith("/chat") && (
                      <button
                        type="button"
                        onClick={() => goToChat(formData.persona_contacto_id)}
                        className="text-[10px] font-bold text-emerald-500 hover:text-emerald-700 flex items-center gap-1 transition-colors"
                      >
                        <FaWhatsapp size={12} />
                        Chat
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300 text-sm"
                    placeholder="Nombre de la persona"
                    value={formData.persona_contacto}
                    onChange={(e) =>
                      setFormData({ ...formData, persona_contacto: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-2.5">
                    Teléfono Móvil
                  </label>
                  <div className="flex gap-2.5">
                    <select
                      className="w-[30%] min-w-[6.5rem] shrink-0 px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all"
                      value={formData.prefix_movil}
                      onChange={(e) =>
                        setFormData({ ...formData, prefix_movil: e.target.value })
                      }
                    >
                      {countries.map((c) => (
                        <option key={c.code} value={c.dial_code}>
                          {c.code} {c.dial_code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className={`flex-1 min-w-0 px-4 py-3 rounded-xl border bg-white focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300 text-sm ${movilError ? "border-red-400" : "border-slate-200"}`}
                      placeholder="612345678"
                      value={formData.telefono_movil}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.trim() && !v.trim().startsWith("+")) {
                          const c = validateNationalPhone(formData.prefix_movil, v);
                          setMovilError(c.valid ? "" : c.message);
                        } else {
                          setMovilError("");
                        }
                        setFormData({ ...formData, telefono_movil: v });
                      }}
                    />
                  </div>
                  {movilError && (
                    <p className="mt-1.5 text-[11px] font-semibold text-red-500 flex items-center gap-1">
                      <FaExclamationCircle size={11} className="flex-shrink-0" />
                      {movilError}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300"
                  placeholder="ejemplo@negocio.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>

              {/* Logo del negocio */}
              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  Logo
                </label>
                <div className="flex items-center gap-3">
                  {formData.logo_url ? (
                    <>
                      <img
                        src={formData.logo_url}
                        alt="Logo"
                        className="h-16 w-16 object-contain rounded-xl border border-slate-100 bg-slate-50"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            logo_url: "",
                            logo_s3_key: "",
                          }))
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 border border-red-100 transition-colors"
                      >
                        <Trash2 size={13} />
                        Eliminar
                      </button>
                    </>
                  ) : (
                    <label
                      className={`flex flex-col items-center justify-center w-full px-4 py-5 rounded-2xl border-2 border-dashed transition-colors cursor-pointer ${uploadingLogo ? "opacity-60 cursor-not-allowed border-slate-200 bg-slate-50/50" : dragOverLogo ? "border-red-400 bg-red-50/50" : "border-slate-200 bg-slate-50/50 hover:border-red-300 hover:bg-red-50/30"}`}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverLogo(true); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverLogo(false); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverLogo(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) handleLogoUpload({ target: { files: [file] } });
                      }}
                    >
                      {uploadingLogo ? (
                        <Loader2 size={20} className="animate-spin text-red-400 mb-1" />
                      ) : (
                        <ImagePlus size={20} className={`mb-1 ${dragOverLogo ? "text-red-500" : "text-slate-400"}`} />
                      )}
                      <span className="text-sm font-medium text-slate-500">
                        {uploadingLogo ? "Subiendo..." : dragOverLogo ? "Soltar aquí" : "Arrastra o haz clic"}
                      </span>
                      <span className="text-[10px] text-slate-400 mt-0.5">JPG, PNG, WEBP (máx. 2MB)</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        disabled={uploadingLogo}
                        onChange={handleLogoUpload}
                      />
                    </label>
                  )}
                </div>
                {logoError && (
                  <p className="mt-1.5 text-xs text-red-500 font-medium">
                    {logoError}
                  </p>
                )}
                {formData.logo_url && !formData.sector && (
                  <p className="mt-2 text-xs text-red-500 font-semibold flex items-center gap-1.5">
                    <AlertCircle size={13} />
                    Selecciona un sector para activar el simulador
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  Sector
                </label>
                <select
                  className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all"
                  value={valorSelectorSector(formData)}
                  onChange={(e) => {
                    // «Profesor de autoescuela» se guarda como sector autoescuela + autoescuela_plan (sectorProfesor.js).
                    const campos = camposLeadDeSelector(e.target.value);
                    const newSector = campos.sector;
                    setFormData({ ...formData, ...campos });
                    if (/^\d{5}$/.test(formData.codigo_postal) && newSector) {
                      handleCPLookup(
                        formData.codigo_postal,
                        newSector,
                        formData.grupos
                      );
                    }
                  }}
                >
                  <option value="">Seleccionar...</option>
                  <option value="barberia">Barbería</option>
                  <option value="salud">Salud</option>
                  <option value="peluquerias">Peluquería</option>
                  <option value="spa">Spa</option>
                  <option value="consultoria">Consultoría</option>
                  <option value="estetica">Estética</option>
                  <option value="despacho_abogados">Despacho de Abogados</option>
                  <option value="lavadero">Lavadero</option>
                  <option value="taller">Taller mecánico</option>
                  <option value="canina">Peluquería canina</option>
                  <option value="tatuajes">Tatuajes</option>
                  <option value="fisioterapia">Fisioterapia</option>
                  <option value="osteopatia">Osteopatía</option>
                  <option value="psicologia">Psicología</option>
                  {(!isHiddenVertical("autoescuela", hiddenVerticals) || formData.sector === "autoescuela") && (
                    <option value="autoescuela">Autoescuela</option>
                  )}
                  {(!isHiddenVertical("autoescuela", hiddenVerticals) || esLeadProfesor(formData)) && (
                    <option value={SECTOR_PROFESOR}>{ETIQUETA_PROFESOR}</option>
                  )}
                  <option value="bares_deportes">Bar Deportes</option>
                  <option value="bares_tematicos">Bar Temático</option>
                  <option value="marketing">Marketing genérico</option>
                  <option value="comida_para_llevar">Comida para Llevar</option>
                  <option value="restaurante">Restaurante</option>
                  <option value="distribucion">Distribución</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              {formData.sector && (
                <div>
                  <button
                    type="button"
                    onClick={handleEnterSimulator}
                    disabled={enteringSimulator}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gradient-to-r from-red-500 to-purple-600 text-white text-sm font-bold shadow-md hover:shadow-lg hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {enteringSimulator ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <MonitorPlay size={16} />
                    )}
                    {enteringSimulator ? "Cargando simulador..." : "Entrar al simulador de ConextCRM"}
                  </button>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-wider">
                    Dirección
                  </label>
                  {formData.direccion && (
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            formData.direccion,
                          )}`,
                          "_blank",
                        )
                      }
                      className="text-[10px] font-bold text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors"
                    >
                      <MapPin size={12} />
                      {t("common.view_map", "VER MAPA")}
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300"
                  placeholder="Calle y número"
                  value={formData.direccion}
                  onChange={(e) =>
                    setFormData({ ...formData, direccion: e.target.value })
                  }
                />
              </div>

            <div>
              <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                Código Postal
              </label>
              <input
                type="text"
                maxLength={5}
                className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300"
                placeholder="46006"
                value={formData.codigo_postal}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 5);
                  setFormData({ ...formData, codigo_postal: val });
                  if (val.length === 5) {
                    handleCPLookup(val, formData.sector, formData.grupos);
                  } else {
                    setCpSuggestion({ status: "idle", listName: null, city: null });
                  }
                }}
              />

              {/* Badge de sugerencia de lista */}
              {cpSuggestion.status === "loading" && (
                <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 font-medium">
                  <Loader2 size={13} className="animate-spin text-emerald-500" />
                  Detectando ciudad...
                </div>
              )}
              {cpSuggestion.status === "detected" && (
                <div className="mt-2 p-3 rounded-xl border border-emerald-200 bg-emerald-50/50 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 min-w-0">
                    <MapPin size={13} className="shrink-0" />
                    <span className="break-all">
                      Lista detectada:{" "}
                      <span className="font-black">{cpSuggestion.listName}</span>
                    </span>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={handleAcceptSuggestion}
                      className="px-2.5 py-1 rounded-lg bg-emerald-500 text-white text-[11px] font-bold hover:bg-emerald-600 transition-colors"
                    >
                      ✓ Añadir
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCpSuggestion({
                          status: "dismissed",
                          listName: null,
                          city: null,
                        })
                      }
                      className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 text-[11px] font-bold hover:bg-slate-200 transition-colors"
                    >
                      Ignorar
                    </button>
                  </div>
                </div>
              )}
              {cpSuggestion.status === "error" && (
                <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center gap-1.5">
                  <AlertCircle size={13} />
                  No se pudo detectar la ciudad para este código postal
                </p>
              )}
            </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  {t("leads.closing_days", "Días de cierre")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {weekdays.map((day) => {
                    // Resuelve el estado del día: "open" | "morning" | "afternoon" | "closed"
                    const entry = formData.closing_days.find(
                      (d) => d === day.key || d.startsWith(day.key + ":")
                    );
                    const dayState = !entry
                      ? "open"
                      : entry.endsWith(":morning")
                      ? "morning"
                      : entry.endsWith(":afternoon")
                      ? "afternoon"
                      : "closed";

                    const CYCLE = { open: "morning", morning: "afternoon", afternoon: "closed", closed: "open" };
                    const STYLES = {
                      open:      "bg-slate-50 border-slate-100 text-slate-400 hover:border-red-200 hover:text-red-600",
                      morning:   "bg-amber-50 border-amber-300 text-amber-700 shadow-sm shadow-amber-100",
                      afternoon: "bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm shadow-indigo-100",
                      closed:    "bg-red-600 border-red-600 text-white shadow-lg shadow-red-200",
                    };
                    const ICON_EL = {
                      open: null,
                      morning: <Sun size={11} className="inline-block mr-1 align-middle" />,
                      afternoon: <Moon size={11} className="inline-block mr-1 align-middle" />,
                      closed: <X size={11} className="inline-block mr-1 align-middle" />,
                    };

                    return (
                      <button
                        key={day.key}
                        type="button"
                        title={
                          dayState === "open" ? "Abierto todo el día" :
                          dayState === "morning" ? "Solo mañana (tarde cerrado)" :
                          dayState === "afternoon" ? "Solo tarde (mañana cerrado)" :
                          "Cerrado todo el día"
                        }
                        onClick={() => {
                          const nextState = CYCLE[dayState];
                          const withoutDay = formData.closing_days.filter(
                            (d) => d !== day.key && !d.startsWith(day.key + ":")
                          );
                          const newDays =
                            nextState === "open"
                              ? withoutDay
                              : nextState === "closed"
                              ? [...withoutDay, day.key]
                              : [...withoutDay, `${day.key}:${nextState}`];
                          setFormData({ ...formData, closing_days: newDays });
                        }}
                        className={`px-3 py-2 rounded-xl text-xs font-bold transition-all border ${STYLES[dayState]}`}
                      >
                        {ICON_EL[dayState]}{day.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-slate-400 font-medium flex items-center flex-wrap gap-x-1">
                  Clic para cambiar: abierto →
                  <Sun size={10} className="text-amber-400" /> solo mañana →
                  <Moon size={10} className="text-indigo-400" /> solo tarde →
                  <X size={10} className="text-white bg-red-500 rounded-sm" /> cerrado
                </p>
              </div>
            </div>

            {/* Sección 2: Capa Comercial */}
            {!isColumnExpanded("comercial") && (
              <CollapsedColumnRail
                icon={Building2}
                label="Capa Comercial"
                iconClass="bg-red-50 text-red-600"
                onClick={() => expandColumn("comercial")}
              />
            )}
            <div className={`space-y-6 min-w-0 flex-1 ${!isColumnExpanded("comercial") ? "lg:hidden" : ""}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-red-50 text-red-600 rounded-2xl">
                  <Building2 size={18} />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.15em]">
                  Capa Comercial
                </h3>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  Sitio Web
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300"
                  placeholder="conextcrm.es"
                  value={formData.sitio_web}
                  onChange={(e) =>
                    setFormData({ ...formData, sitio_web: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                  Instagram
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all placeholder:text-slate-300"
                  placeholder="@usuario"
                  value={formData.instagram}
                  onChange={(e) =>
                    setFormData({ ...formData, instagram: e.target.value })
                  }
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="tiene_facebook"
                  type="checkbox"
                  checked={!!formData.tiene_facebook}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tiene_facebook: e.target.checked,
                    })
                  }
                  className="w-4 h-4"
                />
                <label
                  htmlFor="tiene_facebook"
                  className="text-sm font-semibold text-slate-700"
                >
                  Tiene Facebook
                </label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="tiene_whatsapp_business"
                  type="checkbox"
                  checked={!!formData.tiene_whatsapp_business}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tiene_whatsapp_business: e.target.checked,
                    })
                  }
                  className="w-4 h-4"
                />
                <label
                  htmlFor="tiene_whatsapp_business"
                  className="text-sm font-semibold text-slate-700"
                >
                  Tiene WhatsApp Business
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                    Rating
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all text-center placeholder:text-slate-300"
                    placeholder="4.9"
                    value={formData.calificacion}
                    onChange={(e) =>
                      setFormData({ ...formData, calificacion: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2.5">
                    Reviews
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3.5 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none font-semibold text-slate-700 transition-all text-center placeholder:text-slate-300"
                    placeholder="240"
                    value={formData.num_resenas}
                    onChange={(e) =>
                      setFormData({ ...formData, num_resenas: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Sección 3: Seguimiento Comercial */}
            {!isColumnExpanded("seguimiento") && (
              <CollapsedColumnRail
                icon={Calendar}
                label="Seguimiento"
                iconClass="bg-emerald-50 text-emerald-600"
                onClick={() => expandColumn("seguimiento")}
              />
            )}
            <div className={`space-y-6 min-w-0 flex-1 ${!isColumnExpanded("seguimiento") ? "lg:hidden" : ""}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-2xl">
                  <Calendar size={18} />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.15em]">
                  Seguimiento
                </h3>
              </div>

              {/* Card Premium con Seguimiento */}
              <div className="bg-gradient-to-br from-emerald-50/50 via-white to-teal-50/30 p-5 rounded-2xl border border-emerald-100/40 space-y-4">
                {/* Interés */}
                <div>
                  <label className="block text-xs font-black text-emerald-700 uppercase tracking-wider mb-2.5">
                    Nivel de Interés
                  </label>
                  <select
                    className="w-full px-4 py-3.5 rounded-2xl border border-emerald-200 bg-white/50 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none font-semibold text-slate-700 transition-all"
                    value={formData.interest}
                    onChange={(e) =>
                      setFormData({ ...formData, interest: e.target.value })
                    }
                  >
                    <option value="">Seleccionar estado...</option>
                    <option value="En integración">🚀 En integración</option>
                    <option value="Muy interesado">🔥 Muy interesado</option>
                    <option value="Interesado">👍 Interesado</option>
                    <option value="Poco interesado">😐 Poco interesado</option>
                    <option value="No interesado">👎 No interesado</option>
                    <option value="No disponible">📵 No disponible</option>
                    <option value="No puede atender">
                      ⏳ No puede atender
                    </option>
                    <option value="No existe">🚫 No existe</option>
                    <option value="No corresponde">🚫 No corresponde</option>
                    {formData.sector === "lavadero" && (
                      <option value="Autolavado">🚿 Autolavado</option>
                    )}
                  </select>
                </div>

                {/* Competencia */}
                <div>
                  <label className="block text-xs font-black text-emerald-700 uppercase tracking-wider mb-2.5">
                    Competencia Actual
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select
                      className="w-full px-4 py-3.5 rounded-2xl border border-emerald-200 bg-white/50 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none font-semibold text-slate-700 transition-all"
                      value={formData.competitor}
                      onChange={(e) =>
                        setFormData({ ...formData, competitor: e.target.value })
                      }
                    >
                      <option value="">Ninguna / Desconocida</option>
                      <option value="Bookly">Bookly</option>
                      <option value="Booksy">Booksy</option>
                      <option value="Treatwell">Treatwell</option>
                      <option value="Calendly">Calendly</option>
                      <option value="Fresha">Fresha</option>
                      <option value="Square Appointments">Square Appointments</option>
                      <option value="GoBarber">GoBarber</option>
                      <option value="Neverlate">Neverlate</option>
                      <option value="Peluvis">Peluvis</option>
                      <option value="AgendaPro">AgendaPro</option>
                      <option value="Otros">Otros (especificar...)</option>
                    </select>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-full px-4 pr-8 py-3.5 rounded-2xl border border-emerald-200 bg-white/50 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none font-semibold text-slate-700 transition-all"
                        placeholder="0"
                        value={formData.competitor_price}
                        onChange={(e) =>
                          setFormData({ ...formData, competitor_price: e.target.value })
                        }
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-bold pointer-events-none">€</span>
                    </div>
                  </div>
                  {formData.competitor === "Otros" && (
                    <input
                      type="text"
                      className="mt-2.5 w-full px-4 py-2.5 rounded-2xl border border-emerald-200 bg-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-semibold animate-in fade-in slide-in-from-top-1"
                      placeholder="Nombre de la competencia"
                      value={formData.customCompetitor}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customCompetitor: e.target.value,
                        })
                      }
                    />
                  )}
                </div>

                {/* Televenta: toggle + disposición de cold call + agendar demo */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isTeleventa}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, televenta: e.target.checked }))
                      }
                    />
                    <span className="text-sm font-black text-indigo-700 uppercase tracking-wider">
                      Lead de televenta
                    </span>
                  </label>
                </div>
                {isTeleventa && (
                  <div className="p-3 rounded-2xl border border-indigo-200 bg-indigo-50/40">
                    <div className="text-xs font-black uppercase tracking-wider text-indigo-700 mb-2">
                      Cold call
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {["No contesta", "Volver a llamar", "No interesado", "¡Agendada!"].map(
                        (d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() =>
                              setFormData((p) => ({ ...p, televenta_disposition: d }))
                            }
                            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                              formData.televenta_disposition === d
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                            }`}
                          >
                            {d}
                          </button>
                        ),
                      )}
                    </div>
                    {formData.televenta_disposition === "¡Agendada!" && (
                      <ScheduleDemoBlock
                        lead={{
                          contact_id: contactId,
                          nombre: formData.nombre,
                          telefono: formData.phone_number
                            ? formData.phone_number.startsWith("+")
                              ? formData.phone_number
                              : `${formData.prefix}${formData.phone_number}`
                            : "",
                          sector: formData.sector,
                        }}
                        onScheduled={() => {
                          setSaveSuccess(true);
                          onSaved?.();
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Tipo de Contacto */}
                <div>
                  <label className="block text-xs font-black text-emerald-700 uppercase tracking-wider mb-2.5">
                    ¿Contacto Realizado?
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, contact_type: "" })
                      }
                      className={`flex-1 px-3 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                        formData.contact_type === ""
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Ninguno
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, contact_type: "llamada" })
                      }
                      className={`flex-1 px-3 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                        formData.contact_type === "llamada"
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Llamada
                    </button>
                    {!isTeleventa && (
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, contact_type: "visita" })
                        }
                        className={`flex-1 px-3 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                          formData.contact_type === "visita"
                            ? "bg-emerald-500 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        Visita
                      </button>
                    )}
                  </div>
                </div>

                {/* Tablet vendida (estado): panel azul de solo lectura. Oculto en televenta. */}
                {!isTeleventa && (formData.tablet_vendida && !formData.tablet_entregada ? (
                  <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-blue-800 flex items-center gap-1.5">
                          <CreditCard size={15} className="text-blue-600 flex-shrink-0" /> Tablet vendida
                        </p>
                        <p className="mt-1 text-[11px] font-semibold text-blue-700">
                          {Number(formData.tablet_venta_precio || 0).toLocaleString("es-ES", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 0,
                          })}
                          {" · "}
                          {formData.tablet_venta_metodo === "cargo_cuenta"
                            ? "Cargo a su cuenta (próxima facturación)"
                            : "Efectivo"}
                        </p>
                        {formData.tablet_vendida_at && (
                          <p className="mt-1 text-[11px] font-semibold text-blue-700">
                            Vendida el{" "}
                            {new Date(formData.tablet_vendida_at).toLocaleDateString("es-ES", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                          </p>
                        )}
                      </div>
                      {/* Revertir venta: vuelve a "entregada" limpiando los datos de venta */}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={false}
                        title="Revertir la venta y volver a tablet entregada"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            tablet_entregada: true,
                            tablet_vendida: false,
                            tablet_vendida_at: null,
                            tablet_vendida_by: null,
                            tablet_venta_precio: 100,
                            tablet_venta_metodo: "efectivo",
                          })
                        }
                        className="relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full bg-slate-300 transition-colors duration-200 outline-none focus:ring-4 focus:ring-emerald-500/20"
                      >
                        <span className="inline-block h-5 w-5 transform translate-x-1 rounded-full bg-white shadow-md transition-transform duration-200" />
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] text-blue-700/80 leading-snug">
                      Revierte si la venta fue un error y quieres volver a contarla como tablet entregada.
                    </p>
                  </div>
                ) : /* Tablet devuelta (estado): panel ámbar de solo lectura */
                formData.tablet_devuelta && !formData.tablet_entregada ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5">
                          <Undo2 size={15} className="text-amber-600 flex-shrink-0" /> Tablet devuelta
                        </p>
                        {formData.tablet_devuelta_at && (
                          <p className="mt-1 text-[11px] font-semibold text-amber-700">
                            Devuelta el{" "}
                            {new Date(formData.tablet_devuelta_at).toLocaleDateString("es-ES", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                          </p>
                        )}
                        {formData.tablet_devuelta_motivo && (
                          <p className="mt-1 text-xs text-amber-800/90 leading-snug">
                            <span className="font-semibold">Motivo:</span>{" "}
                            {formData.tablet_devuelta_motivo}
                          </p>
                        )}
                      </div>
                      {/* Re-entrega: reactivar limpia el estado de devolución */}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={false}
                        title="Volver a entregar la tablet"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            tablet_entregada: true,
                            tablet_devuelta: false,
                            tablet_devuelta_at: null,
                            tablet_devuelta_motivo: "",
                            tablet_devuelta_by: null,
                          })
                        }
                        className="relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full bg-slate-300 transition-colors duration-200 outline-none focus:ring-4 focus:ring-emerald-500/20"
                      >
                        <span className="inline-block h-5 w-5 transform translate-x-1 rounded-full bg-white shadow-md transition-transform duration-200" />
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] text-amber-700/80 leading-snug">
                      Activa el interruptor si vuelves a entregarle la tablet.
                    </p>
                  </div>
                ) : (
                  /* Tablet entregada */
                  <div className="rounded-2xl border border-emerald-200 bg-white/60 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                          <Smartphone size={15} className="text-emerald-600 flex-shrink-0" /> Tablet entregada
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                          Al activar, se crearán automáticamente 2 tareas: una a los
                          10 días (uso) y otra a los 28 días (verificación de
                          seguridad).
                        </p>
                        {initialData?.tablet_entregada_at && (
                          <p className="mt-1 text-[11px] font-semibold text-emerald-700">
                            Registrada el{" "}
                            {new Date(initialData.tablet_entregada_at).toLocaleDateString("es-ES", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!formData.tablet_entregada}
                        onClick={() => {
                          // Al apagar el toggle a mano, cancelar también la devolución
                          // y la venta en curso.
                          if (formData.tablet_entregada) {
                            setReturningTablet(false);
                            setTabletReturnReason("");
                            setSellingTablet(false);
                          }
                          setFormData({
                            ...formData,
                            tablet_entregada: !formData.tablet_entregada,
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors duration-200 outline-none focus:ring-4 focus:ring-emerald-500/20 ${
                          formData.tablet_entregada
                            ? "bg-emerald-500"
                            : "bg-slate-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                            formData.tablet_entregada ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Acciones (solo si está entregada): devolución y venta */}
                    {formData.tablet_entregada && (
                      <div className="mt-3 pt-3 border-t border-emerald-100">
                        {returningTablet ? (
                          <div>
                            <label className="block text-xs font-bold text-amber-700 mb-1.5">
                              Motivo de la devolución
                            </label>
                            <textarea
                              value={tabletReturnReason}
                              onChange={(e) => setTabletReturnReason(e.target.value)}
                              rows={2}
                              autoFocus
                              placeholder="¿Por qué ha devuelto la tablet? (no la usa, cambió de opinión, avería…)"
                              className="w-full px-3 py-2 rounded-xl border border-amber-200 bg-white focus:bg-white focus:ring-4 focus:ring-amber-500/10 focus:border-amber-400 outline-none text-sm text-slate-700 resize-none"
                            />
                            <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                              Al «Guardar cambios» dejará de constar como entregada y se cancelarán las tareas de seguimiento de la tablet.
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setReturningTablet(false);
                                setTabletReturnReason("");
                              }}
                              className="mt-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
                            >
                              Cancelar devolución
                            </button>
                          </div>
                        ) : sellingTablet ? (
                          <div>
                            <label className="block text-xs font-bold text-blue-700 mb-1.5">
                              Vender la tablet al cliente
                            </label>
                            {/* Importe */}
                            <div className="relative">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={formData.tablet_venta_precio}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    tablet_venta_precio: e.target.value,
                                  })
                                }
                                autoFocus
                                className="w-full pl-3 pr-8 py-2 rounded-xl border border-blue-200 bg-white focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 outline-none text-sm font-semibold text-slate-700"
                              />
                              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">
                                €
                              </span>
                            </div>
                            {/* Forma de pago */}
                            <p className="mt-2.5 mb-1.5 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                              Forma de pago
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData({ ...formData, tablet_venta_metodo: "efectivo" })
                                }
                                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                                  formData.tablet_venta_metodo === "efectivo"
                                    ? "bg-blue-500 text-white"
                                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                }`}
                              >
                                Efectivo
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData({ ...formData, tablet_venta_metodo: "cargo_cuenta" })
                                }
                                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all leading-tight ${
                                  formData.tablet_venta_metodo === "cargo_cuenta"
                                    ? "bg-blue-500 text-white"
                                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                }`}
                              >
                                Cargar a su cuenta
                              </button>
                            </div>
                            <p className="mt-2 text-[11px] text-slate-500 leading-snug">
                              {formData.tablet_venta_metodo === "cargo_cuenta"
                                ? "Se cargará al cliente en su próxima factura mensual. Al «Guardar cambios» la tablet pasará a «vendida» y se cancelarán las tareas de seguimiento."
                                : "Cobro en efectivo. Al «Guardar cambios» la tablet pasará a «vendida» y se cancelarán las tareas de seguimiento."}
                            </p>
                            <button
                              type="button"
                              onClick={() => setSellingTablet(false)}
                              className="mt-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
                            >
                              Cancelar venta
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setReturningTablet(true)}
                              className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-2 rounded-xl transition-colors"
                            >
                              <Undo2 size={15} /> Ha devuelto la tablet
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                // Abrir el formulario con los valores por defecto si aún
                                // no se había configurado una venta.
                                setFormData((prev) => ({
                                  ...prev,
                                  tablet_venta_precio:
                                    prev.tablet_venta_precio != null && prev.tablet_venta_precio !== ""
                                      ? prev.tablet_venta_precio
                                      : 100,
                                  tablet_venta_metodo: prev.tablet_venta_metodo || "efectivo",
                                }));
                                setSellingTablet(true);
                              }}
                              className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-2 rounded-xl transition-colors"
                            >
                              <CreditCard size={15} /> Marcar como vendida
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Recordatorio de Llamada */}
                <div>
                  <label className="block text-xs font-black text-emerald-700 uppercase tracking-wider mb-2.5">
                    Recordatorio de Llamada
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Fecha */}
                    <div className="relative">
                      <DatePicker
                        selected={formData.callReminderDate}
                        onChange={(date) => {
                          const newDate = date ? new Date(date) : null;
                          if (newDate && formData.callReminderDate) {
                            newDate.setHours(
                              formData.callReminderDate.getHours(),
                            );
                            newDate.setMinutes(
                              formData.callReminderDate.getMinutes(),
                            );
                          }
                          setFormData({
                            ...formData,
                            callReminderDate: newDate,
                          });
                        }}
                        dateFormat="dd/MM/yyyy"
                        locale="es"
                        placeholderText="Fecha"
                        className="w-full px-11 py-3.5 rounded-2xl border border-emerald-200 bg-white/50 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none font-semibold text-slate-700 transition-all text-sm"
                        isClearable
                      />
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 pointer-events-none">
                        <Calendar size={18} />
                      </div>
                    </div>

                    {/* Hora */}
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-emerald-500 flex-shrink-0" />
                      <Time24Picker
                        minuteStep={15}
                        value={
                          formData.callReminderDate
                            ? `${String(formData.callReminderDate.getHours()).padStart(2, "0")}:${String(formData.callReminderDate.getMinutes()).padStart(2, "0")}`
                            : ""
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!v) {
                            setFormData({ ...formData, callReminderDate: null });
                            return;
                          }
                          const [h, m] = v.split(":").map(Number);
                          const base = formData.callReminderDate
                            ? new Date(formData.callReminderDate)
                            : (() => {
                                const d = new Date();
                                d.setDate(d.getDate() + 2);
                                return d;
                              })();
                          base.setHours(h, m, 0, 0);
                          setFormData({ ...formData, callReminderDate: base });
                        }}
                      />
                    </div>
                  </div>
                  {formData.callReminderDate && (
                    <p className="mt-3 text-xs text-emerald-600 flex items-center gap-2 font-semibold">
                      <AlertCircle size={14} /> Tarea para el{" "}
                      {formData.callReminderDate.toLocaleString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                </div>

                {/* Notas de Seguimiento */}
                <div>
                  <label className="block text-xs font-black text-emerald-700 uppercase tracking-wider mb-2.5">
                    Notas de Seguimiento
                  </label>
                  <textarea
                    className="w-full px-4 py-3.5 rounded-2xl border border-emerald-200 bg-white/50 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none font-semibold text-slate-700 transition-all min-h-[100px] resize-none"
                    placeholder="Escribe aquí detalles de la conversación, objeciones, etc..."
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                  />
                </div>

                {/* Grabaciones de visitas/llamadas */}
                <LeadRecordings
                  contactId={contactId}
                  recordings={formData.recordings}
                  onChange={(next) => {
                    setFormData((prev) => ({ ...prev, recordings: next }));
                    // Las grabaciones se persisten al instante (su propio endpoint).
                    // Refrescamos la lista para que el chip de "grabaciones" aparezca
                    // y la reapertura del lead muestre la grabación recién subida.
                    onSaved?.();
                  }}
                />
              </div>
            </div>

            {/* Sección 4: Listas Asociadas */}
            {!isColumnExpanded("listas") && (
              <CollapsedColumnRail
                icon={Users}
                label="Listas"
                iconClass="bg-red-50 text-red-600"
                onClick={() => expandColumn("listas")}
              />
            )}
            <div className={`space-y-6 min-w-0 flex-1 ${!isColumnExpanded("listas") ? "lg:hidden" : ""}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-red-50 text-red-600 rounded-2xl">
                  <Users size={18} />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.15em]">
                  Listas
                </h3>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-3">
                  Listas del Lead
                </label>
                {availableGroups.length > 8 && (
                  <div className="relative mb-3">
                    <input
                      type="text"
                      placeholder="Buscar lista..."
                      value={groupSearch}
                      onChange={(e) => setGroupSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-slate-100 bg-slate-50/50 outline-none focus:ring-2 focus:ring-red-500/10 focus:border-red-500 font-semibold transition-all"
                    />
                    {groupSearch && (
                      <button
                        type="button"
                        onClick={() => setGroupSearch("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-4">
                  {availableGroups.filter((g) =>
                    !groupSearch.trim() ||
                    g.toLowerCase().includes(groupSearch.trim().toLowerCase())
                  ).map((group) => (
                    <button
                      key={group}
                      type="button"
                      onClick={() => toggleGroup(group)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                        formData.grupos.includes(group)
                          ? "bg-gradient-to-r from-red-600 to-violet-600 text-white shadow-lg shadow-red-200/40"
                          : "bg-slate-50 text-slate-500 border border-slate-100 hover:bg-slate-100"
                      }`}
                    >
                      {formatSectorLabel(group)}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Nueva..."
                    className="flex-1 min-w-0 px-3 py-2.5 text-sm rounded-xl border border-slate-100 bg-slate-50/50 outline-none focus:ring-2 focus:ring-red-500/10 focus:border-red-500 font-semibold transition-all"
                    value={customGroup}
                    onChange={(e) => setCustomGroup(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const name = sanitizeListNameIfSector(customGroup);
                      if (!name) return;

                      setCreatingList(true);
                      setCreateListError("");

                      try {
                        const response = await axiosClient.post(
                          "/sales/create-list",
                          {
                            list_name: name,
                          },
                        );

                        if (response.success && response.grupo) {
                          addSelfToListAccess(name);
                          // Añadir a estado local formData
                          setFormData((prev) => ({
                            ...prev,
                            grupos: [...new Set([...prev.grupos.filter((g) => g !== "general"), name])],
                          }));

                          // Actualizar availableGroups para que aparezca inmediatamente en los chips
                          setAvailableGroups((prev) => {
                            const exist = prev.some(
                              (g) =>
                                g.toLowerCase().trim() ===
                                name.toLowerCase().trim(),
                            );
                            return exist ? prev : [...prev, name];
                          });

                          // Limpiar input
                          setCustomGroup("");
                        }
                      } catch (err) {
                        console.error(
                          "[LeadEditModal] Error creating list:",
                          err,
                        );
                        setCreateListError(
                          err?.response?.data?.error ||
                            err?.message ||
                            "Error al crear la lista",
                        );

                        // Revertir después de 3 segundos
                        setTimeout(() => {
                          setCreateListError("");
                        }, 3000);
                      } finally {
                        setCreatingList(false);
                      }
                    }}
                    disabled={creatingList || !customGroup.trim()}
                    className="shrink-0 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-red-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingList ? "..." : "+"}
                  </button>
                </div>
                {createListError && (
                  <div className="mt-2 text-red-600 text-xs font-semibold bg-red-50 px-3 py-2 rounded-lg">
                    {createListError}
                  </div>
                )}
              </div>
            </div>

            {/* Sección Demo */}
            {!isColumnExpanded("demo") && (
              <CollapsedColumnRail
                icon={MonitorPlay}
                label="Demo"
                iconClass="bg-indigo-50 text-indigo-600"
                onClick={() => expandColumn("demo")}
              />
            )}
            <div className={`space-y-6 min-w-0 flex-1 ${!isColumnExpanded("demo") ? "lg:hidden" : ""}`}>
              <LeadDemoSection
                lead={{
                  contact_id: contactId,
                  nombre: formData.nombre,
                  telefono: formData.phone_number
                    ? (formData.phone_number.startsWith("+") ? formData.phone_number : `${formData.prefix}${formData.phone_number}`)
                    : initialData?.phone_number,
                  sector: formData.sector,
                  // Para prerrellenar el destinatario al mandar el enlace de la
                  // videollamada por correo.
                  email: formData.email,
                }}
                onNoCorresponde={() =>
                  // El backend acaba de sellar interest="No corresponde" en c_contactos;
                  // sin esto, "marco No corresponde → toco cualquier campo → Guardar"
                  // reenviaría el interest viejo y revertiría el sello (ver GOTCHA).
                  setFormData((fd) => ({ ...fd, interest: "No corresponde" }))
                }
              />
            </div>

          </div>

          {/* Su cuenta de prueba: accesos y enlace a Supervisión de pruebas (13/10/2026) */}
          <CuentaPruebaPanel
            status={accesosStatus}
            canViewCredentials={canViewCredentials}
            onVerAccesos={() => setCredentialsModalOpen(true)}
          />

          {/* Sección 5: Estado del Chat (fuera del grid de columnas) */}
          {formData.phone_number && (
            <div className="space-y-6 mt-10 max-w-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-cyan-50 text-cyan-600 rounded-2xl">
                  <FaWhatsapp size={18} />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.15em]">
                  {t("common.chat_status", "Estado del Chat")}
                </h3>
                {/* Se lee el último mensaje del lead y el siguiente gesto es responderle:
                    hasta ahora había que salir de la ficha y buscarlo en Chats (David, 10/9/2026). */}
                <button
                  type="button"
                  onClick={() => goToChat(contactId)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors"
                >
                  <FaWhatsapp size={13} /> {t("common.go_to_chat", "Ir al chat")}
                </button>
              </div>

                {formData.chat_status_label ? (
                  <div className="bg-gradient-to-br from-cyan-50/50 via-white to-red-50/30 p-5 rounded-2xl border border-cyan-100/40 space-y-4">
                    {/* Métricas de Chat */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Enviado */}
                      <div className="p-3.5 bg-white/60 border border-cyan-100/40 rounded-xl flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-600 uppercase">
                          {t("common.chat_status_sent", "Enviado")}
                        </span>
                        <span
                          className={`text-lg font-black ${
                            formData.chat_status &&
                            ["sent", "delivered", "read", "received"].includes(
                              formData.chat_status,
                            )
                              ? "text-emerald-500"
                              : "text-slate-300"
                          }`}
                        >
                          ✓
                        </span>
                      </div>

                      {/* Entregado */}
                      <div className="p-3.5 bg-white/60 border border-cyan-100/40 rounded-xl flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-600 uppercase">
                          {t("common.chat_status_delivered", "Entregado")}
                        </span>
                        <span
                          className={`text-lg font-black ${
                            formData.chat_status &&
                            ["delivered", "read", "received"].includes(
                              formData.chat_status,
                            )
                              ? "text-emerald-500"
                              : "text-slate-300"
                          }`}
                        >
                          ✓
                        </span>
                      </div>

                      {/* Leído */}
                      <div className="p-3.5 bg-white/60 border border-cyan-100/40 rounded-xl flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-600 uppercase">
                          {t("common.chat_status_read", "Leído")}
                        </span>
                        <span
                          className={`text-lg font-black ${
                            formData.chat_status &&
                            ["read", "received"].includes(formData.chat_status)
                              ? "text-emerald-500"
                              : "text-slate-300"
                          }`}
                        >
                          ✓
                        </span>
                      </div>

                      {/* Respondió */}
                      <div className="p-3.5 bg-white/60 border border-cyan-100/40 rounded-xl flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-600 uppercase">
                          {t("common.chat_status_received", "Respondió")}
                        </span>
                        <span
                          className={`text-lg font-black ${
                            formData.chat_status === "received"
                              ? "text-emerald-500"
                              : "text-slate-300"
                          }`}
                        >
                          ✓
                        </span>
                      </div>

                      {/* Error */}
                      <div className="p-3.5 bg-white/60 border border-cyan-100/40 rounded-xl flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-600 uppercase">
                          {t("common.chat_status_failed", "Error")}
                        </span>
                        <span
                          className={`text-lg font-black ${
                            formData.chat_status === "failed"
                              ? "text-red-500"
                              : "text-slate-300"
                          }`}
                        >
                          ✗
                        </span>
                      </div>

                      {/* Estado Actual */}
                      <div className="p-3.5 bg-white/60 border border-cyan-100/40 rounded-xl flex flex-col gap-1">
                        <span className="text-xs font-bold text-slate-600 uppercase">
                          Estado
                        </span>
                        <span className="text-xs font-bold text-cyan-600 capitalize">
                          {formData.chat_status_label ||
                            t("common.no_chat", "Sin chat")}
                        </span>
                      </div>
                    </div>

                    {/* Último Mensaje */}
                    {formData.chat_snippet && (
                      <div className="mt-4 p-3.5 bg-slate-50/50 border border-slate-200/30 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">
                          Último Mensaje
                        </p>
                        <p className="text-sm font-semibold text-slate-600 line-clamp-2 italic">
                          "{formData.chat_snippet}"
                        </p>
                        {formData.last_message_ts && (
                          <p className="text-xs text-slate-400 mt-2">
                            {new Date(formData.last_message_ts).toLocaleString(
                              "es-ES",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-slate-50/50 border border-slate-200 px-4 py-8 rounded-2xl text-center">
                    <p className="text-sm font-semibold text-slate-500">
                      {t("common.no_chat", "Sin chat")}
                    </p>
                  </div>
                )}
            </div>
          )}
        </form>

        {/* Footer Premium */}
        <div className="px-4 md:px-8 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-red-100/30 bg-gradient-to-r from-slate-50/50 to-white flex gap-2 flex-wrap">
          {/* Mostrar errores si existen */}
          {tarjetaError && (
            <div className="w-full bg-red-50 border border-red-200 p-3 rounded-lg text-red-700 text-sm mb-2 flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" /> {tarjetaError}
            </div>
          )}
          {flowError && (
            <div className="w-full bg-red-50 border border-red-200 p-3 rounded-lg text-red-700 text-sm mb-2 flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" /> {flowError}
            </div>
          )}
          {comparisonError && (
            <div className="w-full bg-red-50 border border-red-200 p-3 rounded-lg text-red-700 text-sm mb-2 flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" /> {comparisonError}
            </div>
          )}

          {/* Botón Enviar Tarjeta */}
          {contactId && (
            <div className="flex items-stretch gap-1 relative group">
              {tarjetaSent && (
                <span
                  className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap shadow-md pointer-events-none"
                  title={tarjetaSentBy ? `Tarjeta enviada por ${tarjetaSentBy}` : "Tarjeta enviada"}
                >
                  <CheckCircle className="w-2.5 h-2.5" />
                  {tarjetaInfo.chipLabel || "Enviada"}
                </span>
              )}
                <button
                  type="button"
                  onClick={() => {
                    // Ya enviada → permitir reenviar, pero confirmando para evitar
                    // duplicados accidentales.
                    if (
                      tarjetaSent &&
                      !window.confirm(
                        `A este lead ya se le envió la tarjeta${tarjetaSentBy ? ` de ${tarjetaSentBy}` : ""}. ¿Quieres enviársela otra vez?`,
                      )
                    ) {
                      return;
                    }
                    openSendAction("tarjeta");
                  }}
                  disabled={sendingTarjeta || sendingFlow || loading}
                  title={
                    tarjetaSent
                      ? `Ya se envió la tarjeta${tarjetaSentBy ? ` de ${tarjetaSentBy}` : ""}. Pulsa para reenviarla.`
                      : undefined
                  }
                  className={`px-3 sm:px-6 py-2.5 rounded-2xl rounded-r-lg font-bold transition-all active:scale-95 flex items-center justify-center gap-1.5 text-xs sm:text-sm disabled:opacity-50 ${
                    tarjetaSent
                      ? "bg-white border-2 border-red-300 text-red-600 hover:bg-red-50 shadow-sm"
                      : "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-xl shadow-red-200/50"
                  }`}
                >
                  {sendingTarjeta ? (<><Loader2 size={16} className="animate-spin" />Enviando...</>) : tarjetaSent ? (<><CreditCard size={16} /> Reenviar Tarjeta</>) : (<><CreditCard size={16} /> Enviar Tarjeta</>)}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewModal({ open: true, type: "tarjeta" })}
                  disabled={sendingTarjeta || sendingFlow || loading}
                  className="px-3 py-2 rounded-2xl rounded-l-lg bg-red-50 border border-red-200 text-red-400 hover:text-red-600 hover:bg-red-100 transition-colors disabled:opacity-40"
                  title="Vista previa del mensaje"
                >
                  <Eye size={16} />
                </button>
              {tarjetaSent && renderSendHistoryTooltip(tarjetaInfo, "la tarjeta", "la")}
            </div>
          )}

          {/* Botón Enviar Flow + vista previa */}
          {contactId && (
            <div className="flex items-stretch gap-1 relative group">
              {flowSent && (
                <span
                  className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap shadow-md pointer-events-none"
                  title={flowInfo.lastName ? `Flow enviado por ${flowInfo.lastName}` : "Flow enviado"}
                >
                  <CheckCircle className="w-2.5 h-2.5" />
                  {flowInfo.chipLabel || "Enviado"}
                </span>
              )}
              <button
                type="button"
                // Abre el modal único: variantes + historial + vista previa. No confirmamos
                // el reenvío porque el historial (quién y cuándo) se ve nada más abrirlo.
                onClick={() => setSendFlowModalOpen(true)}
                disabled={sendingFlow || sendingTarjeta || loading}
                title={`${
                  flowSent
                    ? `Ya se envió el Flow${flowInfo.lastName ? ` (${flowInfo.lastName})` : ""}. Pulsa para reenviarlo.`
                    : "Enviar el Flow al lead."
                }${
                  televentaEmailTemplateIdForSector(formData.sector)
                    ? " Sale por WhatsApp y, si el lead tiene email, también por correo."
                    : ""
                }`}
                className={`px-3 sm:px-6 py-2.5 rounded-2xl font-bold transition-all active:scale-95 flex items-center justify-center gap-1.5 text-xs sm:text-sm disabled:opacity-50 ${
                  flowSent
                    ? "bg-white border-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50 shadow-sm"
                    : "bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-xl shadow-emerald-200/50"
                }`}
              >
                {sendingFlow ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    {/* Los dos canales por los que sale el Flow, en vez de la flecha
                        genérica: WhatsApp siempre, y el sobre solo si el sector tiene
                        email (si no, el Flow solo puede ir por WhatsApp). */}
                    <span className="inline-flex items-center gap-1">
                      <WhatsAppIcon size={15} />
                      {televentaEmailTemplateIdForSector(formData.sector) && (
                        <>
                          <span className="opacity-50 text-[10px] leading-none">+</span>
                          <Mail className="w-3.5 h-3.5" />
                        </>
                      )}
                    </span>
                    {flowSent ? "Reenviar Flow" : "Enviar Flow"}
                  </>
                )}
              </button>
              {televentaEmailTemplateIdForSector(formData.sector) && (
                <button
                  type="button"
                  onClick={() => setEmailPreviewOpen(true)}
                  className="self-center ml-1 p-2 px-2.5 bg-indigo-50 border border-indigo-200 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors"
                  title="Vista previa del email de televenta (móvil/ordenador)"
                >
                  <Eye size={14} />
                </button>
              )}
              {marketingEmailTemplateIdForSector(formData.sector) && (
                <button
                  type="button"
                  onClick={() => setMarketingEmailPreviewOpen(true)}
                  className="self-center ml-1 p-2 px-2.5 bg-sky-50 border border-sky-200 text-sky-400 hover:text-sky-600 hover:bg-sky-100 rounded-full transition-colors"
                  title="Vista previa del email de marketing (móvil/ordenador)"
                >
                  <Eye size={14} />
                </button>
              )}
              {/* Estado del email del lead. Tres estados, incluido "Sin email": antes el
                  chip solo existía si el lead TENÍA correo, así que en el caso que
                  importa (falta el dato) no se veía nada y el email se saltaba callado. */}
              {televentaEmailTemplateIdForSector(formData.sector) && (
                formData.email ? (
                  <span
                    className={`self-center ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-wide whitespace-nowrap ${
                      emailSent
                        ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                        : "bg-orange-50 text-orange-600 border border-orange-100"
                    }`}
                    title={
                      emailSent
                        ? `Email de televenta enviado${emailInfo.lastName ? ` (${emailInfo.lastName})` : ""}${emailInfo.lastDate ? ` · ${emailInfo.lastDate}` : ""}`
                        : "Email de televenta pendiente de enviar (se manda solo/junto con el Flow)"
                    }
                  >
                    <Mail className="w-2.5 h-2.5" />
                    {/* La fecha va VISIBLE, como en el badge del Flow ("Trini · 5 ago").
                        Antes decía solo "Email enviado" y escondía el cuándo en el title. */}
                    {emailSent
                      ? `Televenta${emailInfo.lastDate ? ` · ${emailInfo.lastDate}` : ""}`
                      : "Televenta pend."}
                  </span>
                ) : (
                  <span
                    className="self-center ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-wide whitespace-nowrap bg-amber-50 text-amber-700 border border-amber-200"
                    title="Este lead no tiene email: al enviar el Flow solo le llegará el WhatsApp. Rellena el campo Email de la ficha para que también reciba el correo."
                  >
                    <Mail className="w-2.5 h-2.5" />
                    Sin email
                  </span>
                )
              )}
              {/* Email de MARKETING (el del vídeo de campañas, distinto del de televenta).
                  Solo se pinta cuando se ha enviado: a diferencia del de televenta, este no
                  sale junto al Flow normal — se manda a propósito con el Flow marketing, así
                  que un "pendiente" leería como si se le debiera algo al lead. */}
              {marketingEmailTemplateIdForSector(formData.sector) && marketingEmailInfo.sent && (
                <span
                  className="self-center ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-wide whitespace-nowrap bg-sky-50 text-sky-600 border border-sky-100"
                  title={`Email de marketing enviado${marketingEmailInfo.lastName ? ` por ${marketingEmailInfo.lastName}` : ""}${marketingEmailInfo.lastDate ? ` · ${marketingEmailInfo.lastDate}` : ""}${marketingEmailInfo.count > 1 ? ` (${marketingEmailInfo.count} envíos)` : ""}`}
                >
                  <Megaphone className="w-2.5 h-2.5" />
                  {`Marketing${marketingEmailInfo.lastDate ? ` · ${marketingEmailInfo.lastDate}` : ""}`}
                </span>
              )}
            </div>
          )}

          {/* Botón Enviar competencia (imagen comparativa ConextCRM vs competidor) */}
          {contactId && (
            <button
              type="button"
              onClick={() => handleSendComparison()}
              disabled={sendingFlow || sendingTarjeta || sendingComparison || loading}
              className="px-3 sm:px-6 py-2.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold hover:from-amber-600 hover:to-orange-700 disabled:opacity-50 shadow-xl shadow-amber-200/50 transition-all active:scale-95 flex items-center justify-center gap-1.5 text-xs sm:text-sm"
            >
              {sendingComparison ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generando...
                </>
              ) : (
                <><Swords size={16} /> Enviar competencia</>
              )}
            </button>
          )}

          {/* Botón Enviar Accesos */}
          {contactId && (
            <div className="flex items-stretch gap-1 relative group">
              {accesosSent && (
                <span
                  className={`absolute -top-2 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap shadow-md ${
                    accesosWasSentByWA ? "bg-emerald-500" : "bg-indigo-500"
                  }`}
                  title={
                    accesosWasSentByWA
                      ? "Accesos enviados por WhatsApp"
                      : "Accesos creados (no enviados por WhatsApp)"
                  }
                >
                  <CheckCircle className="w-2.5 h-2.5" />
                  {accesosChipLabel}
                </span>
              )}
                <button
                  type="button"
                  onClick={() => openSendAction("accesos")}
                  disabled={loading || sendingFlow || sendingTarjeta || accesosSent}
                  title={
                    accesosSent
                      ? accesosWasSentByWA
                        ? "Este lead ya tiene cuenta en el CRM y se le enviaron los accesos por WhatsApp."
                        : "Este lead ya tiene cuenta en el CRM (accesos creados sin envío)."
                      : undefined
                  }
                  className={`px-3 sm:px-6 py-2.5 rounded-2xl rounded-r-lg font-bold transition-all active:scale-95 flex items-center justify-center gap-1.5 text-xs sm:text-sm ${
                    accesosSent
                      ? "bg-slate-200 text-slate-500 cursor-not-allowed shadow-none"
                      : "bg-gradient-to-r from-purple-600 to-violet-700 text-white hover:from-purple-700 hover:to-violet-800 disabled:opacity-50 shadow-xl shadow-purple-200/50"
                  }`}
                >
                  <KeyRound size={16} /> Crear Accesos
                </button>
                {/* Un ojo por canal: los accesos salen por WhatsApp y por email, así
                    que la ficha ofrece las dos vistas previas (igual que el modal de
                    crear accesos, donde cada checkbox tiene la suya). */}
                <button
                  type="button"
                  onClick={() => setPreviewModal({ open: true, type: "accesos" })}
                  disabled={loading || sendingFlow || sendingTarjeta}
                  className="px-3 py-2 rounded-2xl rounded-l-lg bg-purple-50 border border-purple-200 text-purple-400 hover:text-purple-600 hover:bg-purple-100 transition-colors disabled:opacity-40"
                  title="Vista previa del mensaje de WhatsApp"
                >
                  <FaWhatsapp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setAccesosEmailPreviewOpen(true)}
                  disabled={loading || sendingFlow || sendingTarjeta}
                  className="px-3 py-2 rounded-2xl rounded-l-lg bg-blue-50 border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-40"
                  title="Vista previa del email"
                >
                  <Mail size={16} />
                </button>
                {accesosSent && (canViewCredentials || accesosStatusLoading) && (
                  <button
                    type="button"
                    onClick={() => setCredentialsModalOpen(true)}
                    disabled={!canViewCredentials || accesosStatusLoading}
                    className="px-3 py-2 rounded-2xl rounded-l-lg bg-amber-50 border border-amber-200 text-amber-600 hover:text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-40 inline-flex items-center justify-center"
                    title={
                      accesosStatusLoading
                        ? "Cargando credenciales..."
                        : "Ver usuario y contraseña del cliente"
                    }
                  >
                    {accesosStatusLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <KeyRound size={16} />
                    )}
                  </button>
                )}
              {accesosSent && isAdmin && !cuentaPorTelefono && (
                <button
                  type="button"
                  onClick={handleClearAccesos}
                  disabled={loading || sendingFlow || sendingTarjeta || accesosClearing}
                  className="px-3 py-2 rounded-2xl rounded-l-lg bg-rose-50 border border-rose-200 text-rose-500 hover:text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-40 inline-flex items-center justify-center"
                  title="Limpiar accesos: borra los flags del lead y elimina la empresa trial del CRM para poder volver a crear (solo admin)"
                >
                  {accesosClearing ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>
              )}
              {accesosSent && (
                <div
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity z-50"
                >
                  <div className="bg-slate-900 text-white text-xs font-medium rounded-xl px-3 py-2 shadow-2xl leading-snug">
                    {accesosWasSentByWA
                      ? "Este lead ya tiene cuenta en el CRM y se le enviaron los accesos por WhatsApp."
                      : "Este lead ya tiene cuenta en el CRM (accesos creados, no enviados por WhatsApp)."}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-slate-900" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Fila inferior fija: Cancelar + Guardar siempre juntos y a ancho
              completo (el w-full fuerza un salto de línea en el flex-wrap, así
              el primario nunca se pierde en una fila intermedia ni queda pegado
              al borde de la tablet). */}
          <div className="w-full flex gap-2">
            {/* Botón Cancelar */}
            <button
              type="button"
              onClick={handleCloseClick}
              className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-bold hover:bg-white hover:border-red-200 transition-all active:scale-95 text-xs sm:text-sm"
            >
              Cancelar
            </button>

            {/* Botón Guardar */}
            <button
              onClick={handleSave}
              disabled={loading || sendingFlow || sendingTarjeta}
              className={`flex-1 px-4 py-2.5 rounded-2xl text-white font-bold disabled:opacity-50 shadow-xl transition-all active:scale-95 flex items-center justify-center gap-2 text-xs sm:text-sm ${
                saveSuccess && !loading
                  ? "bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-emerald-200/50"
                  : "bg-gradient-to-r from-red-600 to-violet-600 hover:from-red-700 hover:to-violet-700 shadow-red-200/50"
              }`}
            >
              {loading ? (
                "Guardando..."
              ) : saveSuccess ? (
                <>
                  <CheckCircle size={18} /> Guardado
                </>
              ) : contactId ? (
                "Guardar Cambios"
              ) : (
                "Crear Lead"
              )}
            </button>
          </div>
        </div>
      </div>

      <CredentialsViewerModal
        open={credentialsModalOpen}
        onClose={() => setCredentialsModalOpen(false)}
        leadName={formData.nombre}
        username={credentialsUsername}
        password={credentialsPassword}
        sentByWhatsApp={accesosWasSentByWA}
        lastLoginAt={accesosStatus?.last_login_at || null}
        passwordChanged={accesosStatus?.must_change_password === false}
        loginCode={credentialsLoginCode}
        loginCodeActive={accesosStatus?.login_code_active !== false}
        onSendWhatsApp={handleSendAccesosWhatsApp}
        sendingWhatsApp={sendingAccesosWA}
        canSendWhatsApp={!!contactId && !!credentialsUsername && !!credentialsPassword}
        onSendEmail={handleSendAccesosEmail}
        sendingEmail={sendingAccesosEmail}
        leadEmail={formData.email || ""}
        emailSentAt={accesosEmailSentAt || initialData?.accesos_email_sent_at || null}
        companyId={sessionCredentials?.company_id || initialData?.trial_company_id || null}
        leadNombre={formData.nombre}
      />

      <SectorPickerModal
        open={accesosModalOpen}
        onClose={() => { setAccesosModalOpen(false); setAccesosPhone(null); }}
        leadName={formData.nombre}
        presetSector={formData.sector}
        presetPlan={esLeadProfesor(formData) ? "profesor" : null}
        contactId={contactId}
        leadEmail={formData.email || ""}
        leadPhone={
          accesosPhone || (
            formData.phone_number.startsWith("+")
              ? formData.phone_number
              : `${formData.prefix}${formData.phone_number}`
          )
        }
        leadPhoneLocal={(() => {
          // Número nacional sin prefijo de país para usar como contraseña
          // temporal en v_create_client.
          const raw = formData.phone_number || "";
          if (!raw) return "";
          if (raw.startsWith("+")) {
            // Si conocemos el prefijo del lead, lo quitamos del principio.
            if (formData.prefix && raw.startsWith(formData.prefix)) {
              return raw.substring(formData.prefix.length).replace(/\D+/g, "");
            }
            // Fallback: dejamos que el backend lo strippee con KNOWN_DIAL_CODES.
            return "";
          }
          // Ya viene sin prefijo.
          return raw.replace(/\D+/g, "");
        })()}
        onSuccess={({ username, password, login_code, company_id: trialCompanyId, sentByWhatsApp, sentByEmail, email } = {}) => {
          const nowIso = new Date().toISOString();
          setAccesosClearedInSession(false);
          setAccesosCreatedInSession(true);
          if (sentByWhatsApp) setAccesosSentInSession(true);
          if (sentByEmail) setAccesosEmailSentAt(nowIso);
          if (email) setFormData((prev) => ({ ...prev, email }));
          // Guardar credenciales en sesión para mostrarlas inmediatamente al
          // pulsar el ojo, sin esperar al fetch de /ventas/accesos-status.
          if (username && password) {
            setSessionCredentials({
              username,
              password,
              login_code: login_code || null,
              company_id: trialCompanyId,
            });
          }
          if (contactId) {
            // user_id del vendedor que está creando los accesos (para la
            // supervisión: cada vendedor solo ve los leads cuyos accesos creó él).
            let accesosCreatedBy = null;
            try {
              const tok = localStorage.getItem("token");
              if (tok) accesosCreatedBy = jwtDecode(tok).user_id || null;
            } catch {}
            axiosClient.post("/sales/mark-contacted", {
              contact_id: contactId,
              type: "accesos",
              notes: sentByWhatsApp ? "Accesos enviados" : "Accesos creados",
            }).catch((err) => console.warn("mark-contacted accesos failed:", err));
            // Persistir flags en el lead. `accesos_created_at` siempre;
            // `accesos_sent_at` solo si se envió por WhatsApp.
            axiosClient.post("/ventas/bulk-contacts", {
              contact_id: contactId,
              accesos_created_at: nowIso,
              ...(accesosCreatedBy && { accesos_created_by: accesosCreatedBy }),
              ...(sentByWhatsApp && { accesos_sent_at: nowIso }),
              ...(username && { trial_admin_username: username }),
              ...(trialCompanyId && { trial_company_id: trialCompanyId }),
            }).catch((err) => console.warn("bulk-contacts persist accesos failed:", err));
          }
        }}
      />
      <RecipientPickerModal
        open={recipientPicker.open}
        onClose={() => setRecipientPicker({ open: false, pendingAction: null })}
        mainPhone={mainPhoneDisplay || ""}
        personaName={formData.persona_contacto}
        personaPhone={fullPhoneMovilDisplay || ""}
        onSelect={(phone) => {
          const action = recipientPicker.pendingAction;
          setRecipientPicker({ open: false, pendingAction: null });
          executeSendAction(action, phone);
        }}
      />
      {showAccessDenied && accessDeniedData && (
        <AccessRequestModal
          open={showAccessDenied}
          onClose={() => setShowAccessDenied(false)}
          contactId={accessDeniedData.contactId}
          contactName={accessDeniedData.contactName}
          contactPhone={accessDeniedData.contactPhone}
          contactLists={accessDeniedData.contactLists}
          onRequested={onAccessRequested}
        />
      )}
      <WhatsAppPreviewModal
        open={previewModal.open}
        onClose={() => setPreviewModal({ open: false, type: null })}
        type={previewModal.type}
        recipientName={formData.nombre || formData.persona_contacto || ""}
        senderName={senderPreviewName}
        tarjetaUrl={tarjetaPreviewUrl}
        telefono={getTelefonoComercial(_previewUser?.user_id)}
      />
      {/* Preview del email desde la ficha. Con accesos ya creados enseña las
          credenciales REALES; sin ellos, el ejemplo con su banda de aviso. */}
      <AccesosEmailPreviewModal
        open={accesosEmailPreviewOpen}
        onClose={() => setAccesosEmailPreviewOpen(false)}
        companyId={sessionCredentials?.company_id || initialData?.trial_company_id || null}
        email={formData.email || ""}
        nombre={formData.nombre}
      />
      <SendFlowModal
        open={sendFlowModalOpen}
        onClose={() => setSendFlowModalOpen(false)}
        leadName={formData.nombre || formData.persona_contacto || ""}
        phone={mainPhoneDisplay}
        email={formData.email || ""}
        leadSector={formData.sector || null}
        isTeleventa={isTeleventa}
        sends={flowSendsList}
        // Cada variante con SU historial. Las tres primeras sellan `flow_*` (comparten
        // historial de verdad); "Flow marketing" sella `marketing_flow_*`, y el email que
        // acompaña a cada una es distinto: `email_*` en televenta, `marketing_email_*` en
        // marketing. Antes todas leían el de televenta.
        sendsByAction={{
          flow: flowSendsList,
          flow_presentacion: flowSendsList,
          flow_televenta: flowSendsList,
          flow_marketing: marketingFlowSendsList,
          flow_email: emailSendsList,
        }}
        emailSendsByAction={{
          flow: emailSendsList,
          flow_presentacion: emailSendsList,
          flow_televenta: emailSendsList,
          flow_marketing: marketingEmailSendsList,
        }}
        formatDate={formatSendDate}
        sending={sendingFlow}
        onSend={(action, sector) => {
          // El sector viaja por ref: entre esto y handleSendFlow puede meterse el
          // selector de destinatario. Cerramos el modal para no apilarlo encima.
          flowSectorOverrideRef.current = sector || null;
          setSendFlowModalOpen(false);
          // "Solo email": no hay WhatsApp, así que ni selector de destinatario ni
          // garantías sobre el número. Va directo al camino de email.
          if (action === "flow_email") {
            handleSendFlow(null, sector, false, isTeleventa, false, false, true);
            return;
          }
          openSendAction(action);
        }}
        onPreview={(action, sector) => {
          // El ojo de "Solo email" abre la vista previa del email, no la del WhatsApp.
          if (action === "flow_email") {
            setSendFlowModalOpen(false);
            setEmailPreviewSector(sector || formData.sector || null);
            setEmailPreviewOpen(true);
            return;
          }
          setFlowPreview({
            open: true,
            sector,
            // La vista previa abre YA en la variante del ojo pulsado.
            withPresentation: action !== "flow",
            online: action === "flow_televenta" ? true : isTeleventa,
            single: action === "flow_televenta",
          });
        }}
      />
      <FlowPreviewModal
        open={flowPreview.open}
        onClose={() => setFlowPreview((p) => ({ ...p, open: false }))}
        leadName={formData.nombre || formData.persona_contacto || ""}
        initialSector={flowPreview.sector}
        initialOnline={flowPreview.online}
        initialWithPresentation={flowPreview.withPresentation}
        initialSingle={flowPreview.single}
        // Se abre desde el ojo de SendFlowModal (z-70): tiene que quedar por encima o no
        // se ve. La réplica del flow, que sale de la vista previa, sube a 90 sola.
        overlayZIndex={80}
      />
      <EmailPreviewModal
        open={emailPreviewOpen}
        onClose={() => {
          setEmailPreviewOpen(false);
          setEmailPreviewSector(null);
        }}
        sector={emailPreviewSector || formData.sector}
        nombre={formData.nombre || formData.persona_contacto || ""}
        overlayZIndex={70}
      />
      <EmailPreviewModal
        open={marketingEmailPreviewOpen}
        onClose={() => setMarketingEmailPreviewOpen(false)}
        sector={formData.sector}
        nombre={formData.nombre || formData.persona_contacto || ""}
        product="marketing"
        overlayZIndex={70}
      />

      {/* Selector de sector para la comparativa de competencia */}
      {comparisonSectorPicker.open && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setComparisonSectorPicker({ open: false, phone: null })}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              ¿De qué sector quieres la comparativa?
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Este lead no tiene un sector con tabla de competencia. Elige uno.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(COMPETITION_CONFIG).map(([sectorKey, config]) => (
                <button
                  key={sectorKey}
                  type="button"
                  className="rounded-xl border border-slate-200 hover:border-amber-400 hover:bg-amber-50 p-4 font-semibold text-slate-700 hover:text-amber-700 transition flex items-center justify-center text-center"
                  onClick={() => {
                    const phone = comparisonSectorPicker.phone;
                    setComparisonSectorPicker({ open: false, phone: null });
                    handleSendComparison(phone, sectorKey);
                  }}
                >
                  {config.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700"
              onClick={() => setComparisonSectorPicker({ open: false, phone: null })}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Selector de competidor para la comparativa de competencia */}
      {comparisonCompetitorPicker.open && comparisonCompetitorPicker.sectorKey && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setComparisonCompetitorPicker({ open: false, phone: null, sectorKey: null })}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              ¿Contra qué competidor?
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Comparativa de {COMPETITION_CONFIG[comparisonCompetitorPicker.sectorKey].label} frente a ConextCRM.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COMPETITION_CONFIG[comparisonCompetitorPicker.sectorKey].competitors.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="rounded-xl border border-slate-200 hover:border-amber-400 hover:bg-amber-50 p-4 font-semibold text-slate-700 hover:text-amber-700 transition flex items-center justify-center text-center"
                  onClick={() => {
                    const { phone, sectorKey } = comparisonCompetitorPicker;
                    setComparisonCompetitorPicker({ open: false, phone: null, sectorKey: null });
                    handleSendComparison(phone, sectorKey, c.id);
                  }}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700"
              onClick={() => setComparisonCompetitorPicker({ open: false, phone: null, sectorKey: null })}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal cambios sin guardar */}
      {showUnsavedModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-7 flex flex-col gap-5">
            <div>
              <h3 className="text-lg font-black text-slate-900 mb-1">
                Cambios sin guardar
              </h3>
              <p className="text-sm text-slate-500">
                Tienes cambios que no se han guardado todavía. ¿Qué quieres hacer?
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                disabled={loading}
                onClick={async () => {
                  setShowUnsavedModal(false);
                  // Solo cerrar la ficha si el guardado fue correcto; si falla
                  // (p.ej. error de red) la dejamos abierta para reintentar.
                  const ok = await handleSave({ preventDefault: () => {} });
                  if (ok) onClose();
                }}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-bold hover:opacity-90 transition-all disabled:opacity-60 shadow-md shadow-red-100"
              >
                Guardar y salir
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUnsavedModal(false);
                  onClose();
                }}
                className="w-full py-3 rounded-xl bg-slate-100 text-slate-600 text-sm font-bold hover:bg-slate-200 transition-all"
              >
                Salir sin guardar
              </button>
              <button
                type="button"
                onClick={() => setShowUnsavedModal(false)}
                className="w-full py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Cancelar (seguir editando)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Modal de solo lectura para volver a consultar las credenciales del admin
// trial cuando el cliente aún no ha cambiado la contraseña por primera vez.
function CredentialsViewerModal({
  open,
  onClose,
  leadName,
  username,
  password,
  sentByWhatsApp,
  lastLoginAt,
  passwordChanged,
  loginCode,
  loginCodeActive = true,
  onSendWhatsApp,
  sendingWhatsApp,
  canSendWhatsApp,
  onSendEmail,
  sendingEmail,
  leadEmail,
  emailSentAt,
  companyId,
  leadNombre,
}) {
  const [showPassword, setShowPassword] = useState(true);
  useEffect(() => {
    if (!open) setShowPassword(true);
  }, [open]);

  // Input inline del email: solo se abre si la ficha no tiene email.
  const [emailInputOpen, setEmailInputOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  useEffect(() => {
    if (!open) {
      setEmailInputOpen(false);
      setEmailDraft("");
      setEmailPreviewOpen(false);
    }
  }, [open]);

  if (!open) return null;

  const copy = (val) => {
    try {
      navigator.clipboard?.writeText(val);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 z-10">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Accesos del cliente</h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[280px]">
              {leadName || "Lead"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 ml-2 flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-xl p-3 mb-4 leading-relaxed flex items-start gap-2">
          <KeyRound size={13} className="flex-shrink-0 mt-0.5" />
          {passwordChanged ? (
            <span>Esta es la <b>contraseña actual</b> del cliente (ya la cambió en su acceso).</span>
          ) : (
            <span>Contraseña <b>temporal</b>: el cliente deberá cambiarla en su primer acceso.</span>
          )}
        </div>

        <div className="bg-gray-50 rounded-2xl p-5 space-y-4 border border-gray-100">
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
              Web de acceso
            </label>
            <div className="flex items-center justify-between gap-2 p-3 bg-white border border-gray-200 rounded-xl">
              <code className="text-sm font-bold text-violet-600 break-all">app.conextcrm.es</code>
              <button
                type="button"
                onClick={() => copy("app.conextcrm.es")}
                className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                title="Copiar"
              >
                <Copy size={15} />
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
              Usuario
            </label>
            <div className="flex items-center justify-between gap-2 p-3 bg-white border border-gray-200 rounded-xl">
              <code className="text-sm font-bold text-blue-600 break-all">{username || "—"}</code>
              <button
                type="button"
                onClick={() => copy(username || "")}
                className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                title="Copiar"
                disabled={!username}
              >
                <Copy size={15} />
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Contraseña
              </label>
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="text-[10px] font-semibold text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
              >
                {showPassword ? <EyeOff size={12} /> : <Eye size={12} />}
                {showPassword ? "Ocultar" : "Mostrar"}
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 p-3 bg-white border border-gray-200 rounded-xl">
              <code className="text-sm font-bold text-rose-600 break-all">
                {showPassword ? password || "—" : "••••••••••"}
              </code>
              <button
                type="button"
                onClick={() => copy(password || "")}
                className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                title="Copiar"
                disabled={!password}
              >
                <Copy size={15} />
              </button>
            </div>
          </div>

          {loginCode && loginCodeActive && (
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                Código rápido (usuario y contraseña)
              </label>
              <div className="flex items-center justify-between gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <code className="text-sm font-bold text-amber-700 break-all">{loginCode}</code>
                <button
                  type="button"
                  onClick={() => copy(loginCode)}
                  className="text-amber-500 hover:text-amber-700 p-1 flex-shrink-0"
                  title="Copiar"
                >
                  <Copy size={15} />
                </button>
              </div>
              <p className="text-[11px] text-amber-700 mt-1.5 leading-snug flex items-start gap-1.5">
                <Zap size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  Para entrar al instante: pon <strong>{loginCode}</strong> en usuario y{" "}
                  <strong>{loginCode}</strong> en contraseña. Solo válido durante la prueba
                  gratuita.
                </span>
              </p>
            </div>
          )}

          {loginCode && !loginCodeActive && (
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                Código rápido (ya no válido)
              </label>
              <div className="flex items-center justify-between gap-2 p-3 bg-gray-100 border border-gray-200 rounded-xl">
                <code className="text-sm font-bold text-gray-400 line-through break-all">{loginCode}</code>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5 leading-snug flex items-start gap-1.5">
                <AlertCircle size={13} className="text-gray-400 flex-shrink-0 mt-0.5" />
                <span>
                  El cliente ya es de pago, así que el código rápido{" "}
                  <strong>{loginCode}</strong> dejó de funcionar. Debe entrar con el{" "}
                  <strong>Usuario</strong> y la <strong>Contraseña</strong> de arriba.
                </span>
              </p>
            </div>
          )}
        </div>

        {sentByWhatsApp && (
          <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg p-2 mt-3 border border-emerald-100 flex items-start gap-1.5">
            <CheckCircle size={13} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>Estas credenciales se enviaron por WhatsApp al cliente.</span>
          </p>
        )}
        {lastLoginAt && (
          <p className="text-[11px] text-gray-500 mt-3">
            Último inicio de sesión: {new Date(lastLoginAt).toLocaleString("es-ES")}
          </p>
        )}

        {emailSentAt && (
          <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg p-2 mt-3 border border-emerald-100 flex items-start gap-1.5">
            <CheckCircle size={13} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>
              Accesos enviados por email el {new Date(emailSentAt).toLocaleString("es-ES")}.
            </span>
          </p>
        )}

        {onSendEmail && (
          <>
            {emailInputOpen && (
              <input
                type="email"
                autoFocus
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                placeholder="cliente@sudominio.es"
                className="mt-4 w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            <div className="mt-3 flex items-stretch gap-2">
              <button
                type="button"
                onClick={async () => {
                  // Sin email en la ficha: primero se abre el input; el segundo clic envía.
                  if (!leadEmail && !emailInputOpen) {
                    setEmailInputOpen(true);
                    return;
                  }
                  const ok = await onSendEmail(emailInputOpen ? emailDraft : null);
                  if (ok) setEmailInputOpen(false);
                }}
                disabled={sendingEmail || !username || !password}
                className="flex-1 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Mail size={18} />
                    {emailSentAt
                      ? "Reenviar accesos por email"
                      : leadEmail || emailInputOpen
                        ? "Enviar accesos por email"
                        : "Enviar accesos por email…"}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setEmailPreviewOpen(true)}
                className="px-3 rounded-2xl bg-blue-50 border border-blue-200 text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition-colors"
                title="Vista previa del email"
              >
                <Eye size={16} />
              </button>
            </div>
          </>
        )}

        {onSendWhatsApp && (
          <button
            type="button"
            onClick={onSendWhatsApp}
            disabled={sendingWhatsApp || !canSendWhatsApp}
            className="mt-5 w-full px-6 py-3 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 text-white font-bold hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sendingWhatsApp ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <FaWhatsapp size={18} />
                {sentByWhatsApp
                  ? "Reenviar accesos por WhatsApp"
                  : "Enviar accesos por WhatsApp"}
              </>
            )}
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full px-6 py-3 rounded-2xl bg-gradient-to-r from-red-600 to-violet-600 text-white font-bold hover:from-red-700 hover:to-violet-700 transition-all"
        >
          Cerrar
        </button>

        <AccesosEmailPreviewModal
          open={emailPreviewOpen}
          onClose={() => setEmailPreviewOpen(false)}
          companyId={companyId}
          email={leadEmail || emailDraft || ""}
          nombre={leadNombre}
        />
      </div>
    </div>
  );
}

